import { useState, useEffect, useContext, createContext, useRef, Component, ErrorInfo, ReactNode, FormEvent, useMemo } from "react";
// User interface - now managed by session (matches buildNav User type)
interface User {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email?: string | null;
  job_title?: string;
  role: string;
  organization_id?: number | null;
  home_id?: number | null;
  is_superadmin?: boolean;
  status?: string;
}
import { Router, Route, Switch, Link, useLocation } from "wouter";
import { QueryClientProvider, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryClient, apiRequest } from "./lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import HighlightCard from '@/components/ui/highlight-card';
import Tile from '@/components/ui/tile';
import { Button } from "@/components/ui/button";
import { ObjectUploader } from "@/components/ObjectUploader";
import type { UploadResult } from '@uppy/core';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/usePermissions";
import { Toaster } from "@/components/ui/toaster";
import { RuntimeBanner } from "@/components/RuntimeBanner";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Shield, Building2, FileText, ClipboardCheck, ClipboardList, Users, LogOut, Plus, Eye, Edit, BarChart3, Home, ArrowUpDown, ArrowUp, ArrowDown, Save, Building, Trash2, AlertTriangle, UserX, Clock, Settings, Pencil, Upload, FileUp, Mail, AlertCircle, ChevronRight, ChevronLeft, CheckCircle, CheckCircle2, Bell, XCircle, Minus, TrendingUp, Activity, PieChart as PieChartIcon, Calendar } from "lucide-react";
import AuditProgressAndScore from "@/components/AuditProgressAndScore";
import ScoreSummaryCard from "@/components/ScoreSummaryCard";
import { getScoreStatus } from "@shared/scoring";
import { buildNav, getEffectiveUser } from "@root/nav/buildNav";
import { canEditInScope, canManageUserRole, ROLE_PERMISSIONS, type Role } from "@root/auth/roles";
import { ScrollNavigation } from "@/components/scroll-navigation";
import { ResponsiveBreadcrumb, useBreadcrumbs } from "@/components/breadcrumb";
import { PageHeader } from "@/components/back-navigation";
import { StickyActionBar } from "@/components/sticky-action-bar";
import { ThemeProvider } from "@/components/theme-provider";
import { ThemeToggle } from "@/components/theme-toggle";
import { ConfirmModal } from "@/components/ui/confirm-modal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { csvHomeImportRowSchema, csvUserImportRowSchema } from "@shared/schema";
import { BuilderPage } from "@/components/template-builder";
import { TemplatePermissionManager } from "@/pages/TemplatePermissionManager";
import { SettingsPage } from "@/pages/SettingsPage";
import { RegionsPage } from "@/pages/RegionsPage";
import ReportsPage from "@/pages/ReportsPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import { Landing as LandingPage } from "@/pages/Landing";
import { DemoPage } from "@/pages/Demo";
import { PrivacyPage } from "@/pages/Privacy";
import { TermsPage } from "@/pages/Terms";
import { EnquiriesPage } from "@/pages/EnquiriesPage";
import { ViewCompletedAuditPage } from "@/pages/ViewCompletedAuditPage";
import { useCanCreateTemplates } from "@/hooks/useTemplatePermissions";
import { SessionProvider, useSession, useWriteGuard, ImpersonationBanner, SubscriptionBlockedModal } from '@/contexts/session';

// Re-export session hooks for backward compatibility with existing imports from '@/App'
export { useSession, useWriteGuard, SessionProvider, ImpersonationBanner, SubscriptionBlockedModal };
import { toArray } from "@/lib/utils";
import { formatNotificationTitle, formatNotificationDetails } from "@/lib/notificationFormat";
// Removed dashboard summary config — always render Reminders in the dashboard slot
import { parseArrayParam, serializeArrayParam } from "@/lib/urlFilters";
import { PieChart, Pie, Cell, BarChart, Bar, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { MultiSelectFilter, type MultiSelectOption } from "@/components/ui/multi-select-filter";
import RemindersCard from '@/components/RemindersCard';
// Types from API responses

interface Organization {
  id: number;
  name: string;
  billing_email: string;
  address?: string;
  contact_number?: string;
  status: string;
  features_json: any;
  created_at?: string;
}

interface Home {
  id: number;
  name: string;
  address?: string;
  email?: string;
  contact_number?: string;
  bed_count?: number;
  is_active: boolean;
  organization_id?: number;
}

interface Template {
  id: number;
  name: string;
  description: string;
  category_name: string;
  type_name?: string;
  type_id: number;
  created_by: number;
}

interface Audit {
  id: number;
  name: string;
  description?: string;
  status: string;
  priority: string;
  type_id?: number;
  category_id?: number;
  type_name?: string;
  home_id?: number;
  due_date?: string;
  audit_data?: any;
  score?: number | null;
  created_at?: string;
  // Extended fields used in filters and permissions
  template_id?: number;
  assigned_to?: number;
  created_by?: number;
}

// Session context moved to client/src/contexts/session.tsx to avoid circular imports

// Sidebar Navigation Component
function AppSidebar() {
  const { session, isLoading, logout } = useSession();
  const [location] = useLocation();

  // Single source of truth: session data only
  const effectiveUser = session?.user || null;
  const isImpersonating = !!session?.isImpersonating;
  
  
  // Don't render sidebar content during loading
  if (isLoading) {
    return (
      <Sidebar variant="inset" data-testid="sidebar">
        <SidebarContent className="flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </SidebarContent>
      </Sidebar>
    );
  }
  
  // If no user in session (logged out), render empty sidebar
  if (!effectiveUser) {
    return (
      <Sidebar variant="inset" data-testid="sidebar">
        <SidebarContent>
          {/* Completely empty - no navigation when logged out */}
        </SidebarContent>
      </Sidebar>
    );
  }
  
  // Build navigation based on effective user role
  const navigation = buildNav(effectiveUser, null, isImpersonating);

  return (
    <Sidebar variant="inset" data-testid="sidebar">
      <SidebarHeader>
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-blue-500 rounded-lg flex items-center justify-center shadow-md">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="text-base font-semibold text-foreground whitespace-nowrap">Smart Care</span>
          </div>
          <ThemeToggle />
        </div>
      </SidebarHeader>
      
      <SidebarContent>
        {/* Main Navigation */}
        {navigation.main.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>Main</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {navigation.main.map((item: any) => {
                  const Icon = item.icon;
                  return (
                    <SidebarMenuItem key={item.href}>
                      <SidebarMenuButton asChild isActive={location === item.href}>
                        <Link href={item.href} data-testid={item.testId}>
                          <Icon className="w-4 h-4" />
                          <span>{item.label}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
        
        {/* Admin Navigation - only show if there are admin items */}
        {navigation.admin.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>Administration</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {navigation.admin.map((item: any) => {
                  const Icon = item.icon;
                  return (
                    <SidebarMenuItem key={item.href}>
                      <SidebarMenuButton asChild isActive={location === item.href}>
                        <Link href={item.href} data-testid={item.testId}>
                          <Icon className="w-4 h-4" />
                          <span>{item.label}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>
      
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={location === '/settings'}>
              <Link href="/settings" data-testid="nav-settings">
                <Settings className="w-4 h-4" />
                <span>Settings</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={logout} data-testid="nav-logout">
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}

// Enhanced Layout Component with Impersonation Support
function Layout({ children }: { children: ReactNode }) {
  const { session, isLoading } = useSession();
  const [location, setLocation] = useLocation();
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  // Use session user directly
  const effectiveUser = session?.user;
  const isImpersonating = !!session?.isImpersonating;

  // Fetch notifications for bell icon
  const { data: notifications = [] } = useQuery({ 
    queryKey: ["/api/notifications"], 
    queryFn: () => apiRequest("GET", "/api/notifications").then(res => res.json()),
    refetchInterval: 10000,
    enabled: !!effectiveUser
  });

  const unreadNotifications = Array.isArray(notifications) ? notifications.filter((n: any) => !n.is_read) : [];
  const unreadCount = unreadNotifications.length;

  // Show loading while session is being fetched
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // If no effective user, render children (login page)
  if (!effectiveUser) return <>{children}</>;

  // locator injection removed

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <ImpersonationBanner />
        <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <div className="flex items-center space-x-3 ml-4">
            <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-blue-500 rounded-lg flex items-center justify-center shadow-md">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="page-title text-lg font-bold text-foreground">Smart Care Audits</h1>
              <p className="text-xs text-muted-foreground">UK Care Compliance Platform</p>
            </div>
          </div>
          <div className="ml-auto flex items-center space-x-3">
            {/* Notification Bell Dropdown */}
            <Popover open={notificationsOpen} onOpenChange={setNotificationsOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative"
                  data-testid="button-notifications"
                >
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="end">
                <div className="bg-slate-50 dark:bg-slate-900 p-3 border-b">
                  <h3 className="font-semibold">Notifications</h3>
                  <p className="text-xs text-slate-500">{unreadCount} unread</p>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {unreadNotifications.length > 0 ? (
                    unreadNotifications.map((notification: any) => {
                      const handleClick = async () => {
                        // Mark read via API (best-effort)
                        try {
                          await apiRequest('PATCH', `/api/notifications/${notification.id}`, { is_read: true });
                        } catch (err) {
                          // ignore - non-blocking
                          console.debug('mark read failed', err);
                        }

                        // Resolve navigation target: prefer link, then audit_url, then audit_id
                        let target: string | null = null;
                        if (notification.link) target = notification.link;
                        else if (notification.audit_url) target = notification.audit_url;
                        else if (notification.audit_id) target = `/audits/take?auditId=${notification.audit_id}`;

                        if (target) {
                          // Strip origin if an absolute URL was stored (e.g. http://localhost:3000/audits/123)
                          target = target.replace(/^https?:\/\/[^/]+/i, '');
                          // Normalize legacy `/audits/:id` to the new form `/audits/take?auditId=` so the edit form opens
                          const m = target.match(/\/audits\/(\d+)(?:\/.*)?$/);
                          if (m) {
                            target = `/audits/take?auditId=${m[1]}`;
                          }
                          if (process.env.NODE_ENV === 'development') console.debug('Header notification navigation target:', target, notification);
                          setLocation(target);
                          setNotificationsOpen(false);
                        }
                      };

                      // Compute a user-friendly title and details (sanitise legacy raw-event titles)
                      const title = formatNotificationTitle(notification);
                      const detailsLine = (() => {
                        const details = formatNotificationDetails(notification);
                        if (details) return details;
                        const msg = notification.message || '';
                        const event = (notification.event || '').toLowerCase();
                        // Hide the message when it's just the raw event token or duplicates the title
                        if (!msg) return '';
                        if (title && msg === title) return '';
                        if (event && msg.toLowerCase().includes(event)) return '';
                        // Hide messages that are just dates (they duplicate the created_at line)
                        const looksLikeDate = (text: string | undefined | null) => {
                          if (!text) return false;
                          const t = text.trim().replace(/^"|"$/g, '');
                          const dateLike = /^(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}|\d{4}[\/\-]\d{1,2}[\/\-]\d{1,2}|[A-Za-z]{3,9}\s+\d{1,2},?\s+\d{4})$/;
                          return dateLike.test(t);
                        };
                        if (looksLikeDate(msg)) return '';
                        return msg;
                      })();
                      return (
                        <div
                          key={notification.id}
                          className="p-3 border-b hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer transition-colors"
                          onClick={handleClick}
                          data-testid={`notification-item-${notification.id}`}
                        >
                          <div className="flex items-start gap-2">
                                  <div className={`mt-1 ${notification.severity === 'critical' ? 'text-red-500' : notification.severity === 'warning' ? 'text-amber-500' : 'text-blue-500'}`}>
                                    <Bell className="h-4 w-4" />
                                  </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm">{title}</p>
                              {detailsLine ? (
                                <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">{detailsLine}</p>
                              ) : null}
                              <p className="text-xs text-meta mt-1">{new Date(notification.created_at).toLocaleString()}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="p-6 text-center text-slate-500">
                      <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No new notifications</p>
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>

            <ThemeToggle />
            
            {isImpersonating && (
              <Badge variant="destructive" className="animate-pulse" data-testid="read-only-badge">
                READ ONLY
              </Badge>
            )}
            <Badge variant="outline" data-testid="user-role-badge">
              {effectiveUser?.role}
            </Badge>
            {(effectiveUser?.is_superadmin ?? false) && (
              <Badge variant="default">Superadmin</Badge>
            )}
            {isImpersonating && (
              <Badge variant="secondary" className="text-xs" data-testid="effective-user-badge">
                as {effectiveUser?.first_name}
              </Badge>
            )}
          </div>
        </header>
        <main className="flex-1 space-y-4 p-4 pt-6 md:p-8">
          {children}
        </main>
      </SidebarInset>
      <ScrollNavigation />
    </SidebarProvider>
  );
}

// Root Route Component that properly handles hooks
function RootRoute() {
  const { session } = useSession();
  const [, setLocation] = useLocation();
  
  // Use session user as source of truth for routing decisions
  const effectiveUser = session?.user;
  
  useEffect(() => {
    if (effectiveUser) {
      // Route "/" → redirect to "/dashboard" immediately
      setLocation('/dashboard');
    }
  }, [effectiveUser, setLocation]);
  
  // Show landing page for unauthenticated users, or redirect authenticated users
  return effectiveUser ? null : <LandingPage />;
}


// Dashboard Page - renders appropriate dashboard based on effective role
function DashboardPage() {
  const { session } = useSession();
  const user = session?.user;
  const [, setLocation] = useLocation();
  // If the current session is impersonating, don't override the dashboard route -
  // use the impersonated user's role. Only redirect platform users when they are
  // the active (non-impersonating) user.
  const isImpersonating = session?.isImpersonating ?? false;
  const queryClient = useQueryClient();
  const [dashboardPeriod, setDashboardPeriod] = useState<'this_month' | 'last_month'>('this_month');
  const { dateFrom: periodFrom, dateTo: periodTo } = useMemo(() => {
    const now = new Date();
    if (dashboardPeriod === 'this_month') {
      return {
        dateFrom: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0],
        dateTo: new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0]
      };
    }
    return {
      dateFrom: new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0],
      dateTo: new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0]
    };
  }, [dashboardPeriod]);

  // Manager Dashboard Component
  // If a platform-level user (Developer or PlatformManager) visits /dashboard and
  // they are not impersonating, redirect them to the platform-specific dashboard
  // route so they see the Developer/PlatformManager dashboard instead of the
  // manager view.
  useEffect(() => {
    if (!isImpersonating && user) {
      // Platform-level users -> their own dashboards
      if (user.role === 'Developer' || user.role === 'PlatformManager') {
        const route = user.role === 'Developer' ? '/dashboard/developer' : '/dashboard/platform-manager';
        setLocation(route);
        return;
      }
      // Home staff should land on the staff dashboard rather than the manager view
      if (user.role === 'HomeStaff') {
        setLocation('/dashboard/staff');
        return;
      }
    }
  }, [isImpersonating, user, setLocation]);
  const { data: homesResp, isLoading } = useQuery({ 
    queryKey: ["/api/homes"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/homes");
      const data = await res.json();
      return Array.isArray(data) ? data : (data?.items ?? []);
    }
  });
  
  const { data: templatesResp, isLoading: templatesLoading } = useQuery({ 
    queryKey: ["/api/templates/available"], 
    queryFn: () => apiRequest("GET", "/api/templates/available?limit=10").then(res => res.json())
  });
  
  // Normalize API responses: handle both array format and {total, items} format
  const templates = toArray(templatesResp);
  
  // Audits Awaiting Review: manager-focused workflow
  // Include 'reopened' (an audit that was submitted previously and reopened should be considered here)
  // The audits API accepts comma-separated status values
  const { data: auditsResp, isLoading: auditsLoading } = useQuery({ 
    queryKey: ["/api/audits", "AwaitingReview"], 
    // Include Submitted so audits that were submitted and later reopened are picked up
    queryFn: () => apiRequest("GET", "/api/audits?status=Submitted,awaiting_review,reopened&orgScope=true&limit=10").then(res => res.json())
  });
  const audits = toArray(auditsResp);
  
  // Fetch users visible to the requester (scoped by server). Use /api/users without role filter
  // so OrgManager/OrgAdmin will receive all users in their organization.
  const { data: staff = [], isLoading: staffLoading } = useQuery({ 
    queryKey: ["/api/users"], 
    queryFn: () => apiRequest("GET", "/api/users").then(res => res.json())
  });

  // Fetch notifications (activity notifications)
  const { data: notifications = [], isLoading: notificationsLoading } = useQuery({ 
    queryKey: ["/api/notifications"], 
    queryFn: () => apiRequest("GET", "/api/notifications?kind=notification").then(res => res.json()),
    refetchInterval: 10000
  });

  // Fetch reminders
  const { data: reminders = [], isLoading: remindersLoading } = useQuery({ 
    queryKey: ["/api/notifications", "reminders"], 
    queryFn: () => apiRequest("GET", "/api/notifications?kind=reminder").then(res => res.json()),
    refetchInterval: 10000
  });

  // Fetch dashboard summary data based on period (with trends and top types)
  const { data: dashboardSummary, isLoading: summaryLoading } = useQuery({
    queryKey: ["/api/reports/summary", periodFrom, periodTo],
    queryFn: () => apiRequest("GET", `/api/reports/summary?dateFrom=${periodFrom}&dateTo=${periodTo}&includeTrend=true&includeTopTypes=true`).then(res => res.json())
  });

  const unreadNotifications = Array.isArray(notifications) ? notifications.filter((n: any) => !n.is_read) : [];
  const unreadReminders = Array.isArray(reminders) ? reminders.filter((n: any) => !n.is_read) : [];
  
  // Mutation to dismiss notification
  const dismissNotificationMutation = useMutation({
    mutationFn: async (notificationId: number) => {
      return await apiRequest("POST", `/api/notifications/${notificationId}/dismiss`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
    }
  });
  
  // Fetch latest good practices list and pick the first valid item (same filtering as GoodPracticesPage)
  const { data: latestGoodPracticeListResp, isLoading: gpListLoading } = useQuery({
    queryKey: ["/api/good-practices", "latest-list"],
    queryFn: () => apiRequest("GET", "/api/good-practices?limit=10").then(res => res.json()),
    refetchInterval: 60000 // Cache for 60 seconds
  });

  // Derive items & valid list from the list response
  const _gp_list_items = (latestGoodPracticeListResp as any)?.items ?? (Array.isArray(latestGoodPracticeListResp) ? latestGoodPracticeListResp : []);
  const _gp_valid_list = (_gp_list_items as any[]).filter((gp: any) => {
    if (!gp) return false;
    // Allow items marked `is_none` if they have a meaningful description.
    const t = (gp.title || '').toString().trim();
    const d = (gp.description || '').toString().trim();
    // Reject only when both title and description are empty
    if (!t && !d) return false;
    // Reject when both are effectively 'n/a'
    if ((d && d.toLowerCase() === 'n/a') && (t && t.toLowerCase() === 'n/a')) return false;
    // Accept item even if gp.is_none === true as long as description contains useful text
    if (gp.is_none && (!d || d.toLowerCase() === 'n/a')) return false;
    return true;
  });

  // If list returns no valid items, fall back to single latest endpoint
  const { data: latestGoodPracticeSingleResp, isLoading: gpSingleLoading } = useQuery({
    queryKey: ["/api/good-practices", "latest-single"],
    queryFn: () => apiRequest("GET", "/api/good-practices/latest").then(res => res.json()),
    enabled: !gpListLoading && _gp_valid_list.length === 0,
    refetchInterval: 60000
  });

  const latestGoodPracticeClean = (() => {
    if (_gp_valid_list.length > 0) return _gp_valid_list[0];
    const gp = latestGoodPracticeSingleResp as any;
  if (!gp) return null;
  const title = (gp.title || '').toString().trim();
  const desc = (gp.description || '').toString().trim();
  // Reject only when both title and description empty
  if (!title && !desc) return null;
  // Reject when both are effectively 'n/a'
  if ((desc && desc.toLowerCase() === 'n/a') && (title && title.toLowerCase() === 'n/a')) return null;
  // Allow is_none if description appears meaningful
  if (gp.is_none && (!desc || desc.toLowerCase() === 'n/a')) return null;
    // Dev debug: log why we used the single fallback
    if (process.env.NODE_ENV === 'development') {
      console.debug('GoodPractice: using single fallback', { gp, listResp: latestGoodPracticeListResp, validList: _gp_valid_list });
    }
    return gp;
  })();

  const gpLoading = gpListLoading || gpSingleLoading;
  
  // Role-based chart visibility: Only show to Org/Home Managers (NOT Admins)
  const showDashboardCharts = user?.role === 'OrgManager' || user?.role === 'HomeManager';
  const isOrgLevel = user?.role === 'OrgManager' || user?.role === 'OrgAdmin';

  // Fetch audits and actions to compute Urgent Tasks (cross-system prioritized feed)
  const { data: auditsAllResp, isLoading: auditsAllLoading } = useQuery({
    queryKey: ["/api/audits", "for-urgent-all"],
    queryFn: () => apiRequest("GET", "/api/audits?orgScope=true&limit=100").then(res => res.json()),
    refetchInterval: 30000,
  });
  const auditsAll = toArray(auditsAllResp);

  // Fetch open audits count from analytics endpoint (single source of truth)
  const { data: openAuditsResp, isLoading: openAuditsLoading } = useQuery({
    queryKey: ["/api/analytics/open-audits"],
    queryFn: () => apiRequest("GET", "/api/analytics/open-audits").then(res => res.json()),
    refetchInterval: 30000,
  });
  const openAuditsCount = openAuditsResp ? (openAuditsResp.count ?? 0) : 0;

  // Fetch actions (to include overdue actions in urgent tasks)
  const { data: actionsResp, isLoading: actionsLoading } = useQuery({
    queryKey: ["/api/actions", "for-urgent-all"],
    queryFn: () => apiRequest("GET", "/api/actions?orgScope=true&limit=100").then(res => res.json()),
    refetchInterval: 30000,
  });
  const actionsAll = toArray(actionsResp);

  // Build a prioritized list for Urgent Tasks combining audits and actions
  const getUrgentTasks = (auditsList: any[], actionsList: any[]) => {
    const now = new Date();
    const soonMs = 7 * 24 * 60 * 60 * 1000;
    const items: any[] = [];

    // 1) Low-score audits (score < 60)
    for (const a of auditsList) {
      if (typeof a.score === 'number' && a.score < 60) {
        items.push({
          kind: 'Audit',
          id: `audit-${a.id}`,
          auditId: a.id,
          name: a.name,
          home_name: a.home_name || a.homeName || a.home || '',
          due_date: a.due_date || a.submitted_at || a.created_at,
          priority: a.priority || (a.score < 40 ? 'urgent' : 'high'),
          severity: a.priority === 'urgent' || a.priority === 'critical' ? 'critical' : (a.priority || 'medium'),
          status: a.status,
        });
      }
    }

    // 2) Due-soon audits (within 7 days) and overdue
    for (const a of auditsList) {
      if (!a.due_date) continue;
      const d = new Date(a.due_date);
      const ms = d.getTime();
      if (ms <= now.getTime() + soonMs) {
        // Avoid duplicating if already added by low-score
        if (!items.find(i => i.auditId === a.id)) {
          items.push({
            kind: 'Audit',
            id: `audit-${a.id}`,
            auditId: a.id,
            name: a.name,
            home_name: a.home_name || a.homeName || a.home || '',
            due_date: a.due_date,
            priority: a.priority || 'medium',
            severity: a.priority === 'urgent' || a.priority === 'critical' ? 'critical' : (a.priority || 'medium'),
            status: a.status,
          });
        }
      }
    }

    // 3) Follow-up / flagged audits (requires_follow_up)
    for (const a of auditsList) {
      if (a.requires_follow_up) {
        if (!items.find(i => i.auditId === a.id)) {
          items.push({
            kind: 'Audit',
            id: `audit-${a.id}`,
            auditId: a.id,
            name: a.name,
            home_name: a.home_name || a.homeName || a.home || '',
            due_date: a.due_date || a.submitted_at || a.created_at,
            priority: 'high',
            severity: 'high',
            status: a.status,
          });
        }
      }
    }

    // 4) Overdue actions (status != completed and dueDate < now)
    for (const act of actionsList) {
      const due = act.due_date || act.dueDate || act.due;
      if (due) {
        const d = new Date(due);
        if (d.getTime() < now.getTime() && (act.status || act.status === undefined ? (String(act.status).toLowerCase() !== 'completed') : true)) {
          items.push({
            kind: 'Action',
            id: `action-${act.id}`,
            actionId: act.id,
            name: act.action_description || act.title || act.name || 'Action',
            home_name: act.home_name || act.homeName || act.home || '',
            due_date: due,
            priority: act.priority || 'high',
            severity: 'high',
            status: act.status,
            actionStatus: act.status
          });
        }
      }
    }

    // Exclude plain awaiting_review audits unless marked critical/severity
    const filtered = items.filter(i => {
      if (i.kind === 'Audit') {
        // map back to source audit to inspect status and severity/priority
        const src = auditsList.find(a => a.id === i.auditId);
        if (!src) return true;
        // Exclude audits that are awaiting review or were reopened (they belong in the awaiting-review card)
        if ((src.status === 'awaiting_review' || src.status === 'reopened') && !(src.priority === 'critical' || src.severity === 'critical')) {
          return false;
        }
      }
      return true;
    });

    // Sort: critical > urgent/high > medium > low, then by dueDate ascending
    const rank = (it: any) => {
      const p = (it.priority || '').toString().toLowerCase();
      if (it.severity === 'critical' || p === 'critical') return 0;
      if (p === 'urgent') return 1;
      if (p === 'high') return 2;
      if (p === 'medium') return 3;
      return 4; // low/unknown
    };

    filtered.sort((a, b) => {
      const r = rank(a) - rank(b);
      if (r !== 0) return r;
      const da = a.due_date ? new Date(a.due_date).getTime() : Number.MAX_SAFE_INTEGER;
      const db = b.due_date ? new Date(b.due_date).getTime() : Number.MAX_SAFE_INTEGER;
      return da - db;
    });

    return filtered.slice(0, 5);
  };

  const urgentTasks = getUrgentTasks(auditsAll, actionsAll);
  // Limit how many urgent items are shown in the tile to avoid stretching neighbouring tiles
  const URGENT_VISIBLE = 4;
  const displayedUrgent = (urgentTasks || []).slice(0, URGENT_VISIBLE);
  // openAuditsCount provided by server; openAuditsLoading indicates loading state

  // Notification formatting helper imported from '@/lib/notificationFormat'

  return (
    <div className="space-y-6">
  <ResponsiveBreadcrumb items={[{ label: (user?.role === 'HomeAdmin' || user?.role === 'OrgAdmin' ? 'Dashboard' : 'Manager Dashboard') }]} className="mb-4" />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold flex items-center gap-3 page-title text-foreground" data-testid="page-title">
            <CheckCircle2 className="w-6 h-6 text-purple-600" />
            {(user?.role === 'HomeAdmin' || user?.role === 'OrgAdmin') ? 'Dashboard' : 'Manager Dashboard'}
          </h1>
          <p className="text-sm text-muted-foreground">Manage your care home operations</p>
        </div>
        <div className="text-sm text-muted-foreground">
          Welcome back, {user?.first_name}!
        </div>
      </div>

      {/* Reminders: placed below header, above Quick Insights */}
      <div className="mt-4">
        <RemindersCard className="w-full" reminders={unreadReminders} dismiss={(id: number) => dismissNotificationMutation.mutate(id)} />
      </div>

      {/* Compact Dashboard Charts - Only for Org/Home Managers */}
      {showDashboardCharts && (
  <Card className="bg-white dark:bg-slate-900 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
           <BarChart3 className="h-4 w-4 text-primary" />
           <CardTitle className="text-base font-extrabold">Quick Insights</CardTitle>
           {/* NOTE: Quick Insights is intentionally scaled down (smaller title, buttons, and inner cards)
             to act as a compact manager view. Adjust sizes here (CardTitle/text-base, button classes,
             and inner card paddings) to globally change the compact variant. */}
              </div>
              <div className="flex gap-2">
                <Button
                  variant={dashboardPeriod === 'this_month' ? 'default' : 'outline'}
                  size="sm"
                  className="text-xs px-2 py-1"
                  onClick={() => setDashboardPeriod('this_month')}
                  data-testid="button-this-month"
                >
                  This Month
                </Button>
                <Button
                  variant={dashboardPeriod === 'last_month' ? 'default' : 'outline'}
                  size="sm"
                  className="text-xs px-2 py-1"
                  onClick={() => setDashboardPeriod('last_month')}
                  data-testid="button-last-month"
                >
                  Last Month
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {summaryLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-32 bg-slate-200 dark:bg-slate-700 rounded animate-pulse"></div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Mini Donut Chart - Completion Rate (scaled down) */}
                <div className="bg-white dark:bg-slate-800 p-3 rounded-lg shadow-sm">
                  <h4 className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">Completion Rate</h4>
                  <div className="flex items-center justify-center">
                    <div>
                      <PieChart width={88} height={88}>
                        <Pie
                          data={[
                            { name: 'Completed', value: dashboardSummary?.completedAudits || 0, color: 'hsl(var(--chart-2))' },
                            { name: 'Pending', value: dashboardSummary?.pendingAudits || 0, color: 'hsl(var(--chart-4))' }
                          ]}
                          cx="50%"
                          cy="50%"
                          innerRadius={24}
                          outerRadius={40}
                          dataKey="value"
                        >
                          {[{ color: 'hsl(var(--chart-2))' }, { color: 'hsl(var(--chart-4))' }].map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </div>
                    <div className="ml-3">
                      <div className="text-xl font-bold text-primary">{dashboardSummary?.completionRate || 0}%</div>
                      <div className="text-xs text-slate-500">{dashboardSummary?.totalAudits || 0} total</div>
                    </div>
                  </div>
                </div>

                {/* Score Summary (replaces Top Audit Types in compact Quick Insights) */}
                <div>
                  {/* eslint-disable-next-line react/jsx-pascal-case */}
                  <ScoreSummaryCard dateFrom={periodFrom} dateTo={periodTo} />
                </div>

                {/* Avg Score with Trend */}
                <div className="bg-white dark:bg-slate-800 p-3 rounded-lg shadow-sm">
                  <h4 className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">Average Score</h4>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-3xl font-bold text-primary">{dashboardSummary?.averageScore || 0}%</div>
                      <div className="text-xs text-slate-500">Based on {(dashboardSummary as any)?.scoredCount ?? dashboardSummary?.totalAudits ?? 0} audits</div>
                        {dashboardSummary?.scoreChange !== undefined && dashboardSummary.scoreChange !== 0 && (
                        <div className={`flex items-center gap-1 text-sm mt-1 ${dashboardSummary.scoreChange > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {dashboardSummary.scoreChange > 0 ? (
                            <TrendingUp className="h-4 w-4" />
                          ) : (
                            <Activity className="h-4 w-4" />
                          )}
                          <span>{dashboardSummary.scoreChange > 0 ? '+' : ''}{dashboardSummary.scoreChange}%</span>
                        </div>
                      )}
                    </div>
                    <div className="text-4xl">
                      {/* Colored circle matching score ranges: 0-25 red, 26-40 yellow, 41-65 blue, 66-100 green */}
                      {
                        (() => {
                          const mean = dashboardSummary?.averageScore ?? 0;
                          let color = '#ef4444';
                          if (mean > 25 && mean <= 40) color = '#f59e0b';
                          else if (mean > 40 && mean <= 65) color = '#3b82f6';
                          else if (mean > 65) color = '#16a34a';
                          return (
                            <div style={{ width: 28, height: 28, borderRadius: 9999, background: color, boxShadow: 'inset -6px -8px 0 rgba(255,255,255,0.15)' }} />
                          );
                        })()
                      }
                    </div>
                  </div>
                </div>
                {/* (Top Audit Types removed - Score Summary is shown as the middle tile) */}
              </div>
            )}
          </CardContent>
        </Card>
      )}

  {/* Manager Dashboard Cards */}
  <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-3 items-stretch">
        {/* Templates Available */}
  <HighlightCard className="h-full flex flex-col" accent="green" title={"Templates"}>
    <CardContent className="flex flex-col h-full">
      {templatesLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="flex flex-col h-full">
          <div>
            <div className="text-3xl font-bold text-green-600">{templates.length}</div>
          </div>
          <div className="mt-auto pt-3">
            <Button variant="plain" className="w-full tile-btn-success" onClick={() => setLocation('/templates')} data-testid="view-templates">
              <Eye className="w-4 h-4 mr-2" /> View All Templates
            </Button>
          </div>
        </div>
      )}
    </CardContent>
  </HighlightCard>

        {/* Audits Awaiting Review */}
  <HighlightCard className="h-full flex flex-col" accent="orange" title={"Open Audits"}>
    <CardContent className="flex flex-col h-full">
      {openAuditsLoading || auditsLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="flex flex-col h-full">
          <div>
            <div data-testid="open-audits-value" className="text-3xl font-bold text-amber-600">{openAuditsCount > 0 ? openAuditsCount : '0'}</div>
          </div>
          <div className="mt-auto pt-3">
            <Button variant="plain" className="w-full tile-btn-warning" onClick={() => setLocation('/audits?status=&types=&categories=&orgs=&homes=')} data-testid="view-all-audits">
              <Eye className="w-4 h-4 mr-2" /> View All My Audits
            </Button>
          </div>
        </div>
      )}
    </CardContent>
  </HighlightCard>

        {/* (RemindersCard moved into Quick Insights above) */}

        {/* Manage Staff */}
  <HighlightCard className="h-full flex flex-col" accent="blue" title={"Staff"}>
          <CardContent className="flex flex-col h-full">
            {staffLoading ? (
              <div className="space-y-2">
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              </div>
            ) : (
              <div className="flex flex-col h-full">
                <div>
                  <div className="text-3xl font-bold text-blue-600">{staff.length}</div>
                </div>
                <div className="mt-auto pt-3">
                  <Button variant="plain" className="w-full tile-btn-info" onClick={() => setLocation('/admin/users')} data-testid="view-all-users">
                    <Eye className="w-4 h-4 mr-2" />
                    View All Staff
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </HighlightCard>
      </div>
      {/* Reminders are rendered by RemindersCard above; original inline block removed. */}
      
      {/* Urgent Tasks + Good Practice Side-by-side */}
      <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
  {/* Urgent Tasks Tile */}
  <section className="rounded-2xl shadow-sm border border-red-200 bg-red-50 p-4 sm:p-5 min-w-0 h-full flex flex-col" data-testid="urgent-tasks">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-600" />
            <div>
              <div className="text-2xl font-extrabold text-red-800">Urgent Tasks</div>
              <p className="text-sm text-red-800/80">Top issues that need attention</p>
            </div>
          </div>

          <div className="mt-4 flex flex-col h-full min-h-[220px]">
            {auditsAllLoading ? (
              <div className="space-y-2">
                <div className="h-6 bg-red-100 rounded animate-pulse"></div>
                <div className="h-16 bg-red-100 rounded animate-pulse"></div>
              </div>
            ) : urgentTasks && urgentTasks.length > 0 ? (
              <div className="flex flex-col h-full">
                <div className="space-y-3 overflow-y-auto" style={{ maxHeight: '320px' }}>
                  {displayedUrgent.map((t: any) => {
                    const link = t.kind === 'Audit' ? `/audits/view?auditId=${t.auditId}` : `/actions/${t.actionId}`;
                    const reasonLabel = t.priority ? `Priority: ${t.priority}` : (t.due_date ? new Date(t.due_date).toLocaleDateString() : '');
                    return (
                      <Link key={t.id} href={link} className="block rounded-xl border border-red-200 bg-white hover:bg-red-50 transition-colors px-3 py-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />
                          <span className="truncate font-medium text-red-900">{t.name}</span>
                        </div>
                        {reasonLabel ? <p className="mt-1 text-sm text-red-800/80 line-clamp-2">{reasonLabel}</p> : null}
                      </Link>
                    );
                  })}
                </div>

                {urgentTasks.length > displayedUrgent.length && (
                  <div className="text-xs text-muted-foreground mt-2">Showing {displayedUrgent.length} of {urgentTasks.length}</div>
                )}

                {/* View All removed per UX decision - keep CTA out to avoid creating a separate page */}
              </div>
            ) : (
              <div className="flex flex-col h-full">
                <div className="rounded-xl border border-red-200 bg-red-50/60 px-4 py-8 text-center my-auto w-full">
                  <AlertCircle className="mx-auto h-8 w-8 text-red-400" />
                  <p className="mt-2 text-red-800/80">No urgent issues. You’re all caught up.</p>
                </div>

                {/* View All removed per UX decision - keep CTA out to avoid creating a separate page */}
              </div>
            )}
          </div>
        </section>

        <Tile variant="success" className="hover:shadow-md transition-shadow h-full flex flex-col" data-testid="good-practices">

          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-success" />
            <div>
              <div className="text-2xl font-extrabold text-success">Latest Good Practice</div>
              <div className="text-base text-gray-600 dark:text-gray-300 mt-1">{isOrgLevel ? 'Inspirational practices from your organization' : 'Inspirational practices from your organization'}</div>
            </div>
          </div>

          <div className="mt-4 flex flex-col h-full">
            {gpLoading ? (
              <div className="space-y-2">
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              </div>
            ) : latestGoodPracticeClean ? (
              <div className="space-y-4 flex flex-col h-full">
                <div className="rounded-lg p-5" data-testid={`card-gp-latest`}>
                  {latestGoodPracticeClean.title && (
                    <h3 className="font-semibold text-xl text-success dark:text-green-100 mb-2">{latestGoodPracticeClean.title}</h3>
                  )}
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                    {latestGoodPracticeClean.description?.length > 200 ? `${latestGoodPracticeClean.description.substring(0, 200)}...` : latestGoodPracticeClean.description}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-gray-700 dark:text-gray-300 flex-wrap">
                    <div className="flex items-center gap-2 text-meta">
                      <Calendar className="w-4 h-4 flex-shrink-0" />
                      <span className="text-sm leading-none">{latestGoodPracticeClean.created_at ? new Date(latestGoodPracticeClean.created_at).toLocaleDateString() : 'Unknown date'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-meta">
                      <FileText className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate max-w-[260px] text-sm leading-none">{latestGoodPracticeClean.audit_name || 'General Medication Audit'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-meta">
                      <Home className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate max-w-[220px] text-sm leading-none">{latestGoodPracticeClean.home_name || 'Unknown home'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-meta">
                      <Users className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate max-w-[160px] text-sm leading-none">{latestGoodPracticeClean.author_name || 'Home Manager'}</span>
                    </div>
                  </div>
                </div>
                  {session?.user && ['OrgManager','OrgAdmin','HomeManager','HomeAdmin'].includes(session.user.role) ? (
                  <div className="mt-auto pt-4">
                    <Button className="w-full tile-btn-success" onClick={() => setLocation('/good-practices')} data-testid="view-good-practices">
                      <Eye className="w-4 h-4 mr-2" />
                      View all Good Practices
                    </Button>
                  </div>
                ) : (
                  <div className="text-xs text-slate-500 mt-2">View all good practices (organization)</div>
                )}
              </div>
                ) : (
                  <div className="text-center py-4 my-auto">
                <CheckCircle2 className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                <p className="text-gray-500 text-sm">No good practices recorded yet</p>
              </div>
            )}
          </div>
        </Tile>
      </div>
    </div>
  );
}

// Staff Dashboard Component
function StaffDashboard() {
  const { session } = useSession();
  const user = session?.user;
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();

  // Mutation to dismiss notification (used by compact strip)
  const dismissNotificationMutation = useMutation({
    mutationFn: async (notificationId: number) => {
      return await apiRequest('POST', `/api/notifications/${notificationId}/dismiss`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/unread-count'] });
    }
  });
  const { data: templatesResp, isLoading: templatesLoading } = useQuery({ 
    queryKey: ["/api/templates"], 
    queryFn: () => apiRequest("GET", "/api/templates").then(res => res.json())
  });
  
  // Normalize API responses: handle both array format and {total, items} format
  const templates = toArray(templatesResp);
  
  // Fetch audits created by the current user (recent results)
  const { data: myAuditsResp, isLoading: auditsLoading } = useQuery({ 
    queryKey: ["/api/audits", "mine"], 
    queryFn: () => apiRequest("GET", "/api/audits?createdBy=me&limit=10").then(res => res.json()),
    refetchInterval: 60000,
  });

  const myAudits = toArray(myAuditsResp);

  // Fetch latest good practices list and pick the first valid item (same filtering as GoodPracticesPage)
  const { data: latestGoodPracticeListResp, isLoading: gpLoading } = useQuery({
    queryKey: ["/api/good-practices", "latest-list-staff"],
    queryFn: () => apiRequest("GET", "/api/good-practices?limit=10").then(res => res.json()),
    refetchInterval: 60000 // Cache for 60 seconds
  });

  const latestGoodPracticeClean = (() => {
    const resp = latestGoodPracticeListResp as any;
    const items = resp?.items ?? (Array.isArray(resp) ? resp : []);
    const valid = (items as any[]).filter((gp: any) => {
      if (!gp) return false;
      if (gp.is_none) return false;
      const t = (gp.title || '').toString().trim();
      const d = (gp.description || '').toString().trim();
      if (!t && !d) return false;
      if (d.toLowerCase() === 'n/a' || t.toLowerCase() === 'n/a') return false;
      return true;
    });
    const chosen = valid.length > 0 ? valid[0] : null;
    if (process.env.NODE_ENV === 'development') {
      console.debug('Staff GoodPractice: listResp, validList, chosen', { listResp: resp, validList: valid, chosen });
    }
    return chosen;
  })();

  // Compact notifications for Staff (show top 2 unread)
  const { data: notifications = [], isLoading: notificationsLoading } = useQuery({
    queryKey: ["/api/notifications", "staff-compact"],
    queryFn: () => apiRequest("GET", "/api/notifications?kind=notification&limit=5").then(res => res.json()),
    refetchInterval: 15000,
  });
  const unreadCompact = Array.isArray(notifications) ? notifications.filter((n: any) => !n.is_read).slice(0, 2) : [];

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      <ResponsiveBreadcrumb items={[{ label: "Staff Dashboard" }]} className="mb-4" />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title text-[26px] font-bold text-foreground" data-testid="page-title">Staff Dashboard</h1>
          <p className="text-sm text-muted-foreground">Your audit workspace</p>
        </div>
        <div className="text-sm text-muted-foreground">
          Welcome back, {user?.first_name}!
        </div>
      </div>
      
      {/* Compact Notifications strip for Staff (above cards) */}
      {unreadCompact.length > 0 && (
        <Card className="border-l-4 border-l-blue-500" data-testid="staff-compact-notifications">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Bell className="h-5 w-5 text-blue-600" />
                Notifications
                <Badge variant="default" className="ml-2">{unreadCompact.length}</Badge>
              </CardTitle>
              <Link href="/notifications">
                <Button variant="ghost" size="sm">View all</Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {unreadCompact.map((n: any) => (
                <button
                  key={n.id}
                  className="w-full flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg text-left"
                  onClick={() => {
                    // Dismiss (mark as read) and navigate to link/audit_url
                    dismissNotificationMutation.mutate(n.id as number);
                    if (n.link) setLocation(n.link);
                    else if (n.audit_url) setLocation(n.audit_url);
                  }}
                  data-testid={`staff-compact-notification-${n.id}`}
                >
                  <Bell className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-slate-900 dark:text-slate-100">{n.title || n.message}</p>
                    <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">{n.actor_name ? `by ${n.actor_name}` : ''}</p>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Staff Dashboard Cards */}
      <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
        {/* Start an Audit */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <Plus className="w-5 h-5 mr-2 text-purple-600" />
              Start an Audit
            </CardTitle>
            <p className="text-gray-600 dark:text-gray-300">Begin a new compliance audit</p>
          </CardHeader>
          <CardContent>
            {templatesLoading ? (
              <div className="space-y-2">
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              </div>
            ) : templates.length > 0 ? (
              <div className="space-y-3">
                <div className="text-2xl font-bold text-purple-600">{templates.length}</div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Templates available
                </p>
                <div className="space-y-2">
                  <Link href="/audits/new">
                    <Button className="w-full" data-testid="start-new-audit">
                      Start New Audit
                    </Button>
                  </Link>
                  <Link href="/templates">
                    <Button variant="outline" size="sm" className="w-full" data-testid="browse-templates">
                      Browse Templates
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <Plus className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                <p className="text-gray-500 text-sm mb-3">No templates available</p>
                <p className="text-xs text-gray-400">Contact your manager for access</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* My Audits - Assigned incomplete audits */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <ClipboardCheck className="w-5 h-5 mr-2 text-blue-600" />
              My Audits
            </CardTitle>
            <p className="text-gray-600 dark:text-gray-300">Assigned audits (Draft & Submitted)</p>
          </CardHeader>
          <CardContent>
            {auditsLoading ? (
              <div className="space-y-2">
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              </div>
            ) : myAudits.length > 0 ? (
              <div className="flex flex-col">
                <div className="text-2xl font-bold text-blue-600">{myAudits.length}</div>
                <div className="space-y-3 max-h-80 overflow-y-auto mt-3">
                  {myAudits.slice(0, 5).map((audit: Audit) => (
                    <div key={audit.id} className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-gray-700">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 min-w-0 mr-2">
                          <div className="font-medium text-sm truncate">{audit.name}</div>
                          <div className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                            <Clock className="w-3 h-3" />
                            {audit.due_date ? formatRelativeTime(audit.due_date) : 'No due date'}
                          </div>
                        </div>
                        <Badge variant={audit.status === 'Submitted' ? 'default' : 'secondary'} className="text-xs">{audit.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 text-center">
                  <Link href="/audits">
                    <Button variant="outline" size="sm" className="w-full" data-testid="view-all-audits">
                      View All My Audits
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <ClipboardCheck className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                <p className="text-gray-500 text-sm mb-3">You have no assigned audits</p>
                <p className="text-xs text-gray-400">No incomplete audits assigned to you</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Good Practice Spotlight Widget */}
      <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-green-200 dark:border-green-800">
        <CardHeader>
          <CardTitle className="text-lg flex items-center">
            <CheckCircle2 className="w-5 h-5 mr-2 text-green-600" />
            Latest Good Practice
          </CardTitle>
          <p className="text-gray-600 dark:text-gray-300">Inspirational practice from your home</p>
        </CardHeader>
        <CardContent>
          {gpLoading ? (
            <div className="space-y-2">
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
            </div>
          ) : latestGoodPracticeClean ? (
            <div className="space-y-3">
              {latestGoodPracticeClean.title && (
                <h3 className="font-semibold text-lg text-green-900 dark:text-green-100" data-testid="gp-title">
                  {latestGoodPracticeClean.title}
                </h3>
              )}
              <p className="text-sm text-gray-700 dark:text-gray-300" data-testid="gp-description">
                {latestGoodPracticeClean.description?.length > 200
                  ? `${latestGoodPracticeClean.description.substring(0, 200)}...`
                  : latestGoodPracticeClean.description}
              </p>
                <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 mt-2">
                <div className="flex items-center gap-1 italic" data-testid="gp-created-at">
                  <Calendar className="w-3 h-3" />
                  {latestGoodPracticeClean.created_at ? new Date(latestGoodPracticeClean.created_at).toLocaleDateString() : 'Unknown date'}
                </div>
                <div className="italic" data-testid="gp-author">
                  {latestGoodPracticeClean.author_name ? `by ${latestGoodPracticeClean.author_name}` : (latestGoodPracticeClean.created_by ? `by user ${latestGoodPracticeClean.created_by}` : '')}
                </div>
                {latestGoodPracticeClean.audit_id && (
                  <div className="ml-auto text-xs italic">
                    <Link href={`/audits/take?auditId=${latestGoodPracticeClean.audit_id}`}>
                      <Button variant="ghost" size="sm" data-testid="gp-source-audit">{latestGoodPracticeClean.audit_name || 'Source audit'}</Button>
                    </Link>
                  </div>
                )}
              </div>
              <Link href="/good-practices">
                <Button variant="outline" size="sm" className="w-full mt-2" data-testid="view-good-practices">
                  <Eye className="w-3 h-3 mr-1" />
                  View All Good Practices
                </Button>
              </Link>
            </div>
          ) : (
            <div className="text-center py-4">
              <CheckCircle2 className="w-12 h-12 mx-auto text-gray-400 mb-2" />
              <p className="text-gray-500 text-sm">No good practices recorded yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Developer Dashboard Component
function DeveloperDashboard() {
  const { session } = useSession();
  const user = session?.user;
  const { data: organizationsResp, isLoading: orgsLoading } = useQuery({ 
    queryKey: ["/api/organizations"], 
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/organizations");
      const data = await res.json();
      return Array.isArray(data) ? data : data?.items ?? [];
    }
  });
  
  const { data: homesResp, isLoading: homesLoading } = useQuery({ 
    queryKey: ["/api/homes"], 
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/homes");
      const data = await res.json();
      return Array.isArray(data) ? data : data?.items ?? [];
    }
  });
  
  const { data: usersResp, isLoading: usersLoading } = useQuery({ 
    queryKey: ["/api/users"], 
    queryFn: () => apiRequest("GET", "/api/users").then(res => res.json())
  });
  
  const { data: templatesResp, isLoading: templatesLoading } = useQuery({ 
    queryKey: ["/api/templates"], 
    queryFn: () => apiRequest("GET", "/api/templates").then(res => res.json())
  });
  
  const { data: enquiriesResp, isLoading: enquiriesLoading } = useQuery({ 
    queryKey: ["/api/demo-enquiries"], 
    queryFn: () => apiRequest("GET", "/api/demo-enquiries").then(res => res.json())
  });

  // Normalize API responses: handle both array format and {total, items} format
  const organizations = toArray(organizationsResp);
  const homes = toArray(homesResp);
  const users = toArray(usersResp);
  const templates = toArray(templatesResp);
  const enquiries = toArray(enquiriesResp);

  const { data: healthData, isLoading: healthLoading } = useQuery({ 
    queryKey: ["/api/health"], 
    queryFn: () => apiRequest("GET", "/api/health").then(res => res.json()),
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  return (
    <div className="space-y-6">
      <ResponsiveBreadcrumb items={[{ label: "Developer Dashboard" }]} className="mb-4" />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title text-[26px] font-bold text-foreground" data-testid="page-title">Developer Dashboard</h1>
          <p className="text-sm text-muted-foreground">Platform overview and administration</p>
        </div>
        <div className="flex items-center space-x-3">
          {healthData && (
            <Badge 
              variant={healthData.runtime === 'pg' ? 'default' : 'secondary'} 
              className="text-xs" 
              data-testid="runtime-badge"
            >
              Runtime: {healthData.runtime?.toUpperCase() || 'Unknown'}
            </Badge>
          )}
          <div className="text-sm text-muted-foreground">
            Welcome back, {user?.first_name}!
          </div>
        </div>
      </div>
      
      {/* Developer Dashboard Cards - 2x2 Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2">
        {/* Organizations */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <Building className="w-5 h-5 mr-2 text-blue-600" />
              Organizations
            </CardTitle>
            <p className="text-gray-600 dark:text-gray-300">Total organizations in platform</p>
          </CardHeader>
          <CardContent>
            {orgsLoading ? (
              <div className="space-y-2">
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-[26px] font-bold text-blue-600" data-testid="organizations-count">
                  {organizations.length}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {organizations.length === 0 ? 'No organizations yet' : 
                   organizations.length === 1 ? 'organization registered' : 'organizations registered'}
                </p>
                <Link href="/admin/organizations">
                  <Button variant="outline" size="sm" className="w-full" data-testid="manage-organizations">
                    Manage Organizations
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Care Homes */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <Home className="w-5 h-5 mr-2 text-green-600" />
              Care Homes
            </CardTitle>
            <p className="text-gray-600 dark:text-gray-300">Total care homes in platform</p>
          </CardHeader>
          <CardContent>
            {homesLoading ? (
              <div className="space-y-2">
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-[26px] font-bold text-green-600" data-testid="homes-count">
                  {homes.length}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {homes.length === 0 ? 'No care homes yet' : 
                   homes.length === 1 ? 'care home registered' : 'care homes registered'}
                </p>
                <Link href="/homes">
                  <Button variant="outline" size="sm" className="w-full" data-testid="manage-homes">
                    Manage Care Homes
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Users/Clients */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <Users className="w-5 h-5 mr-2 text-purple-600" />
              Clients/Users
            </CardTitle>
            <p className="text-gray-600 dark:text-gray-300">Total users across platform</p>
          </CardHeader>
          <CardContent>
            {usersLoading ? (
              <div className="space-y-2">
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-[26px] font-bold text-purple-600" data-testid="users-count">
                  {users.length}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {users.length === 0 ? 'No users yet' : 
                   users.length === 1 ? 'user registered' : 'users registered'}
                </p>
                <Link href="/admin/users">
                  <Button variant="outline" size="sm" className="w-full" data-testid="manage-users">
                    Manage Users
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Templates */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <FileText className="w-5 h-5 mr-2 text-orange-600" />
              Templates
            </CardTitle>
            <p className="text-gray-600 dark:text-gray-300">Available audit templates</p>
          </CardHeader>
          <CardContent>
            {templatesLoading ? (
              <div className="space-y-2">
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-[26px] font-bold text-orange-600" data-testid="templates-count">
                  {templates.length}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {templates.length === 0 ? 'No templates yet' : 
                   templates.length === 1 ? 'template available' : 'templates available'}
                </p>
                <div className="space-y-2">
                  <Link href="/templates/builder">
                    <Button className="w-full" data-testid="create-template">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Template
                    </Button>
                  </Link>
                  {templates.length > 0 && (
                    <Link href="/templates">
                      <Button variant="outline" size="sm" className="w-full" data-testid="view-templates">
                        View All Templates
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Demo Enquiries */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <Mail className="w-5 h-5 mr-2 text-pink-600" />
              Demo Enquiries
            </CardTitle>
            <p className="text-gray-600 dark:text-gray-300">Incoming demo requests</p>
          </CardHeader>
          <CardContent>
            {enquiriesLoading ? (
              <div className="space-y-2">
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-[26px] font-bold text-pink-600" data-testid="enquiries-count">
                  {enquiries.length}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {enquiries.filter((e: any) => !e.contacted).length > 0 ? (
                    <span className="text-orange-600 dark:text-orange-400 font-medium">
                      {enquiries.filter((e: any) => !e.contacted).length} new enquir{enquiries.filter((e: any) => !e.contacted).length === 1 ? 'y' : 'ies'}
                    </span>
                  ) : (
                    'All enquiries contacted'
                  )}
                </p>
                <Link href="/enquiries">
                  <Button variant="outline" size="sm" className="w-full" data-testid="manage-enquiries">
                    Manage Enquiries
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Platform Manager Dashboard Component
function PlatformManagerDashboard() {
  const { session } = useSession();
  const user = session?.user;
  const { data: organizationsResp, isLoading: orgsLoading } = useQuery({ 
    queryKey: ["/api/organizations"], 
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/organizations");
      const data = await res.json();
      return Array.isArray(data) ? data : data?.items ?? [];
    }
  });
  
  const { data: homesResp, isLoading: homesLoading } = useQuery({ 
    queryKey: ["/api/homes"], 
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/homes");
      const data = await res.json();
      return Array.isArray(data) ? data : data?.items ?? [];
    }
  });
  
  const { data: usersResp, isLoading: usersLoading } = useQuery({ 
    queryKey: ["/api/users"], 
    queryFn: () => apiRequest("GET", "/api/users").then(res => res.json())
  });
  
  const { data: enquiriesResp, isLoading: enquiriesLoading } = useQuery({ 
    queryKey: ["/api/demo-enquiries"], 
    queryFn: () => apiRequest("GET", "/api/demo-enquiries").then(res => res.json())
  });
  
  // Normalize API responses: handle both array format and {total, items} format
  const organizations = toArray(organizationsResp);
  const homes = toArray(homesResp);
  const users = toArray(usersResp);
  const enquiries = toArray(enquiriesResp);

  return (
    <div className="space-y-6">
      <ResponsiveBreadcrumb items={[{ label: "Platform Manager Dashboard" }]} className="mb-4" />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title text-[26px] font-bold text-foreground" data-testid="page-title">Platform Manager Dashboard</h1>
          <p className="text-sm text-muted-foreground">Platform overview and management</p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="text-sm text-muted-foreground">
            Welcome back, {user?.first_name}!
          </div>
        </div>
      </div>
      
      {/* Platform Manager Dashboard Cards - 3 Cards Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Organizations */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <Building className="w-5 h-5 mr-2 text-blue-600" />
              Organizations
            </CardTitle>
            <p className="text-gray-600 dark:text-gray-300">Total organizations</p>
          </CardHeader>
          <CardContent>
            {orgsLoading ? (
              <div className="space-y-2">
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-[26px] font-bold text-blue-600" data-testid="organizations-count">
                  {organizations.length}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {organizations.length === 0 ? 'No organizations yet' : 
                   organizations.length === 1 ? 'organization registered' : 'organizations registered'}
                </p>
                <Link href="/admin/organizations">
                  <Button variant="outline" size="sm" className="w-full" data-testid="manage-organizations">
                    Manage Organizations
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Care Homes */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <Home className="w-5 h-5 mr-2 text-green-600" />
              Care Homes
            </CardTitle>
            <p className="text-gray-600 dark:text-gray-300">Total care homes</p>
          </CardHeader>
          <CardContent>
            {homesLoading ? (
              <div className="space-y-2">
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-[26px] font-bold text-green-600" data-testid="homes-count">
                  {homes.length}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {homes.length === 0 ? 'No care homes yet' : 
                   homes.length === 1 ? 'care home registered' : 'care homes registered'}
                </p>
                <Link href="/homes">
                  <Button variant="outline" size="sm" className="w-full" data-testid="manage-homes">
                    Manage Care Homes
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Users */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <Users className="w-5 h-5 mr-2 text-purple-600" />
              Platform Users
            </CardTitle>
            <p className="text-gray-600 dark:text-gray-300">Total platform users</p>
          </CardHeader>
          <CardContent>
            {usersLoading ? (
              <div className="space-y-2">
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-[26px] font-bold text-purple-600" data-testid="users-count">
                  {users.length}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {users.length === 0 ? 'No users yet' : 
                   users.length === 1 ? 'user registered' : 'users registered'}
                </p>
                <Link href="/admin/users">
                  <Button variant="outline" size="sm" className="w-full" data-testid="manage-users">
                    Manage Users
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Demo Enquiries */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <Mail className="w-5 h-5 mr-2 text-pink-600" />
              Demo Enquiries
            </CardTitle>
            <p className="text-gray-600 dark:text-gray-300">Incoming demo requests</p>
          </CardHeader>
          <CardContent>
            {enquiriesLoading ? (
              <div className="space-y-2">
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-[26px] font-bold text-pink-600" data-testid="enquiries-count">
                  {enquiries.length}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {enquiries.filter((e: any) => !e.contacted).length > 0 ? (
                    <span className="text-orange-600 dark:text-orange-400 font-medium">
                      {enquiries.filter((e: any) => !e.contacted).length} new enquir{enquiries.filter((e: any) => !e.contacted).length === 1 ? 'y' : 'ies'}
                    </span>
                  ) : (
                    'All enquiries contacted'
                  )}
                </p>
                <Link href="/enquiries">
                  <Button variant="outline" size="sm" className="w-full" data-testid="manage-enquiries">
                    Manage Enquiries
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Manager Staff Page Component
function ManagerStaffPage() {
  const { session } = useSession();
  const user = session?.user;
  const { canWrite, showWriteBlockedMessage } = useWriteGuard();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Permission check - Only Managers, Admins, and Superadmins can manage staff
  const canManageStaff = user?.is_superadmin || user?.role === 'Admin' || user?.role === 'Manager';

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<User | null>(null);
  const [passwordStaff, setPasswordStaff] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Queries
  const { data: staff = [], isLoading } = useQuery({ 
    queryKey: ["/api/users", "staff"], 
    queryFn: () => apiRequest("GET", "/api/users?role=Staff").then(res => res.json())
  });
  
  const { data: homesResp } = useQuery({ 
    queryKey: ["/api/homes"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/homes");
      const data = await res.json();
      return Array.isArray(data) ? data : data?.items ?? [];
    }
  });
  
  // Normalize API responses: handle both array format and {total, items} format
  const homes = toArray(homesResp);

  // Staff form schema
  const staffCreateSchema = z.object({
    username: z.string().min(3, "Username must be at least 3 characters"),
    first_name: z.string().min(1, "First name is required"),
    last_name: z.string().min(1, "Last name is required"),
    job_title: z.string().optional(),
    home_id: z.number().nullable(),
    password: z.string().min(6, "Password must be at least 6 characters"),
  });

  const staffEditSchema = z.object({
    first_name: z.string().min(1, "First name is required"),
    last_name: z.string().min(1, "Last name is required"),
    job_title: z.string().optional(),
    home_id: z.number().nullable(),
  });

  const passwordSchema = z.object({
    password: z.string().min(6, "Password must be at least 6 characters"),
    confirmPassword: z.string(),
  }).refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

  type StaffCreateData = z.infer<typeof staffCreateSchema>;
  type StaffEditData = z.infer<typeof staffEditSchema>;
  type PasswordData = z.infer<typeof passwordSchema>;

  // Form handling
  const createForm = useForm<StaffCreateData>({
    resolver: zodResolver(staffCreateSchema),
    defaultValues: {
      username: "",
      first_name: "",
      last_name: "",
      job_title: "",
      home_id: null,
      password: "",
    },
  });

  const editForm = useForm<StaffEditData>({
    resolver: zodResolver(staffEditSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      job_title: "",
      home_id: null,
    },
  });

  const passwordForm = useForm<PasswordData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: StaffCreateData) => {
      if (!canWrite) {
        const message = showWriteBlockedMessage();
        toast(message);
        throw new Error("Write operations disabled");
      }
      return apiRequest("POST", "/api/users", { ...data, role: "HomeStaff" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/users"] });
      setIsCreateModalOpen(false);
      createForm.reset();
      toast({
        title: "Success",
        description: "Staff member created successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create staff member",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: StaffEditData }) => {
      if (!canWrite) {
        const message = showWriteBlockedMessage();
        toast(message);
        throw new Error("Write operations disabled");
      }
      return apiRequest("PUT", `/api/users/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/users"] });
      setEditingStaff(null);
      editForm.reset();
      toast({
        title: "Success",
        description: "Staff member updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update staff member",
        variant: "destructive",
      });
    },
  });

  const setPasswordMutation = useMutation({
    mutationFn: ({ id, password }: { id: number; password: string }) => {
      if (!canWrite) {
        const message = showWriteBlockedMessage();
        toast(message);
        throw new Error("Write operations disabled");
      }
      return apiRequest("PUT", `/api/users/${id}/password`, { password });
    },
    onSuccess: () => {
      setPasswordStaff(null);
      passwordForm.reset();
      toast({
        title: "Success",
        description: "Password updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update password",
        variant: "destructive",
      });
    },
  });

  // Handler functions
  const handleCreateSubmit = (data: StaffCreateData) => {
    createMutation.mutate(data);
  };

  const handleEditSubmit = (data: StaffEditData) => {
    if (editingStaff) {
      updateMutation.mutate({ id: editingStaff.id, data });
    }
  };

  const handlePasswordSubmit = (data: PasswordData) => {
    if (passwordStaff) {
      setPasswordMutation.mutate({ id: passwordStaff.id, password: data.password });
    }
  };

  const handleEdit = (staff: User) => {
    setEditingStaff(staff);
    editForm.reset({
      first_name: staff.first_name,
      last_name: staff.last_name,
      job_title: staff.job_title || "",
      home_id: staff.home_id || null,
    });
  };

  const handleSetPassword = (staff: User) => {
    setPasswordStaff(staff);
    passwordForm.reset();
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    createForm.reset();
  };

  const handleCloseEditModal = () => {
    setEditingStaff(null);
    editForm.reset();
  };

  const handleClosePasswordModal = () => {
    setPasswordStaff(null);
    passwordForm.reset();
  };

  // Filter staff based on search
  const filteredStaff = (staff as User[]).filter(member =>
    `${member.first_name} ${member.last_name} ${member.username} ${member.job_title || ''}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  // Permission check - redirect if not authorized
  if (!canManageStaff) {
    return (
      <div className="text-center py-8">
  <h1 className="page-title text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
        <p className="text-gray-600">You don't have permission to manage staff.</p>
        <Link href="/dashboard">
          <Button className="mt-4">Return to Dashboard</Button>
        </Link>
      </div>
    );
  }

  const getHomeName = (homeId: number | null | undefined) => {
    if (!homeId) return "No home assigned";
    const home = (homes as Home[]).find(h => h.id === homeId);
    return home ? home.name : "Unknown home";
  };

  return (
    <div className="space-y-6">
      <ResponsiveBreadcrumb 
        items={[
          { label: "Manager Dashboard", href: "/dashboard/manager" },
          { label: "Staff Management" }
        ]} 
        className="mb-4" 
      />
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title text-[26px] font-bold text-foreground" data-testid="page-title">Staff Management</h1>
          <p className="text-sm text-muted-foreground">Manage staff members and their access</p>
        </div>
        <Button 
          onClick={() => setIsCreateModalOpen(true)}
          disabled={!canWrite}
          data-testid="button-create-staff"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Staff Member
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="flex gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search staff by name, username, or job title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
            data-testid="input-search-staff"
          />
        </div>
      </div>

      {/* Staff Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading staff...</p>
            </div>
          ) : filteredStaff.length === 0 ? (
            <div className="p-6 text-center">
              <Users className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">No staff found</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {searchTerm ? "No staff match your search criteria." : "No staff members have been added yet."}
              </p>
              {!searchTerm && (
                <Button onClick={() => setIsCreateModalOpen(true)} disabled={!canWrite}>
                  Add First Staff Member
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Username</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Job Title</TableHead>
                  <TableHead>Home</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredStaff.map((member) => (
                  <TableRow key={member.id} data-testid={`staff-row-${member.id}`}>
                    <TableCell className="font-medium">{member.username}</TableCell>
                    <TableCell>{member.first_name} {member.last_name}</TableCell>
                    <TableCell>{member.job_title || "No title"}</TableCell>
                    <TableCell>{getHomeName(member.home_id)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(member)}
                          disabled={!canWrite}
                          data-testid={`button-edit-${member.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSetPassword(member)}
                          disabled={!canWrite}
                          data-testid={`button-password-${member.id}`}
                        >
                          <Shield className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Staff Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Staff Member</DialogTitle>
            <DialogDescription>
              Create a new staff member account with access to the system.
            </DialogDescription>
          </DialogHeader>
          <Form {...createForm}>
            <form onSubmit={createForm.handleSubmit(handleCreateSubmit)} className="space-y-4">
              <FormField
                control={createForm.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Username</FormLabel>
                    <FormControl>
                      <Input placeholder="username" {...field} data-testid="input-create-username" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={createForm.control}
                  name="first_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input placeholder="John" {...field} data-testid="input-create-first-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={createForm.control}
                  name="last_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Doe" {...field} data-testid="input-create-last-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={createForm.control}
                name="job_title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Job Title (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="Care Assistant" {...field} data-testid="input-create-job-title" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                />

                <FormField
                control={createForm.control}
                name="home_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Assigned Home (Optional)</FormLabel>
                    <FormControl>
                      <select 
                        {...field} 
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                        data-testid="select-create-home"
                      >
                        <option value="">No home assigned</option>
                        {(homes as Home[]).map((home) => (
                          <option key={home.id} value={home.id}>
                            {home.name}
                          </option>
                        ))}
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                />

                <FormField
                control={createForm.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="••••••••" {...field} data-testid="input-create-password" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseCreateModal}
                  data-testid="button-cancel-create"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending}
                  data-testid="button-save-create"
                >
                  {createMutation.isPending ? "Creating..." : "Create Staff Member"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Edit Staff Modal */}
      <Dialog open={editingStaff !== null} onOpenChange={() => setEditingStaff(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Staff Member</DialogTitle>
            <DialogDescription>
              Update staff member information.
            </DialogDescription>
          </DialogHeader>
          <Form {...editForm}>
            <form onSubmit={editForm.handleSubmit(handleEditSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={editForm.control}
                  name="first_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input placeholder="John" {...field} data-testid="input-edit-first-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={editForm.control}
                  name="last_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Doe" {...field} data-testid="input-edit-last-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={editForm.control}
                name="job_title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Job Title (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="Care Assistant" {...field} data-testid="input-edit-job-title" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                />

                <FormField
                control={editForm.control}
                name="home_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Assigned Home (Optional)</FormLabel>
                    <FormControl>
                      <select 
                        {...field} 
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                        data-testid="select-edit-home"
                      >
                        <option value="">No home assigned</option>
                        {(homes as Home[]).map((home) => (
                          <option key={home.id} value={home.id}>
                            {home.name}
                          </option>
                        ))}
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseEditModal}
                  data-testid="button-cancel-edit"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={updateMutation.isPending}
                  data-testid="button-save-edit"
                >
                  {updateMutation.isPending ? "Updating..." : "Update Staff Member"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Set Password Modal */}
      <Dialog open={passwordStaff !== null} onOpenChange={() => setPasswordStaff(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Set Password</DialogTitle>
            <DialogDescription>
              Set a new password for {passwordStaff?.first_name} {passwordStaff?.last_name}.
            </DialogDescription>
          </DialogHeader>
          <Form {...passwordForm}>
            <form onSubmit={passwordForm.handleSubmit(handlePasswordSubmit)} className="space-y-4">
              <FormField
                control={passwordForm.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New Password</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="••••••••" {...field} data-testid="input-new-password" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                />

                <FormField
                control={passwordForm.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm Password</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="••••••••" {...field} data-testid="input-confirm-password" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClosePasswordModal}
                  data-testid="button-cancel-password"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={setPasswordMutation.isPending}
                  data-testid="button-save-password"
                >
                  {setPasswordMutation.isPending ? "Setting..." : "Set Password"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Login Page
function LoginPage() {
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [isLoading, setIsLoading] = useState(false);
  const { refreshSession } = useSession();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const res = await apiRequest("POST", "/api/auth/login", credentials);
      const data = await res.json();
      // Trigger session refresh and navigate client-side (no page reload)
      refreshSession();
      setLocation('/dashboard');
    } catch (error) {
      toast({ 
        title: "Login Failed", 
        description: "Please check your credentials",
        variant: "destructive" 
      });
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 grid place-items-center p-4">
      <Card className="w-full max-w-md mx-auto bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
        <CardHeader>
          <div className="w-full flex flex-col items-center text-center p-4">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-blue-500 rounded-full flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="page-title text-2xl font-extrabold text-gray-900 dark:text-white">Smart Care Audits</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-1">UK Care Home Compliance Platform</p>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="username" className="text-gray-700 dark:text-gray-300">Username</Label>
              <Input
                id="username"
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
                placeholder="Enter your username"
                required
                data-testid="input-username"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="password" className="text-gray-700 dark:text-gray-300">Password</Label>
                <Link href="/forgot-password">
                  <button type="button" className="text-sm text-blue-600 dark:text-blue-400 hover:underline" data-testid="link-forgot-password">
                    Forgot password?
                  </button>
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Enter your password"
                required
                data-testid="input-password"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full" 
              disabled={isLoading}
              data-testid="button-login"
            >
              {isLoading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// Home form schema
const homeSchema = z.object({
  name: z.string().min(1, "Home name is required"),
  address: z.string().optional(),
  email: z.string().email("Invalid email format").optional().or(z.literal("")),
  contact_number: z.string().optional(),
  bed_count: z.number().optional(),
  organization_id: z.number().nullable(),
  region_id: z.number().nullable(),
  is_active: z.boolean().default(true),
});

type HomeFormData = z.infer<typeof homeSchema>;

// My Home Info Page (HomeManager view - editable info display)
function MyHomeInfoPage() {
  const { session } = useSession();
  const user = session?.user;
  const breadcrumbs = useBreadcrumbs();
  // Popover/search open state and search terms used by filters (local to AuditsPage)
  const [statusSearchOpen, setStatusSearchOpen] = useState(false);
  const [statusSearchTerm, setStatusSearchTerm] = useState("");
  const [typeSearchOpen, setTypeSearchOpen] = useState(false);
  const [typeSearchTerm, setTypeSearchTerm] = useState("");
  const [categorySearchOpen, setCategorySearchOpen] = useState(false);
  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const [orgSearchOpen, setOrgSearchOpen] = useState(false);
  const [orgSearchTerm, setOrgSearchTerm] = useState("");
  const [homeSearchOpen, setHomeSearchOpen] = useState(false);
  const [homeSearchTerm, setHomeSearchTerm] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);

  const { data: homesResp, isLoading } = useQuery({ 
    queryKey: ["/api/homes"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/homes");
      const data = await res.json();
      return Array.isArray(data) ? data : data?.items ?? [];
    }
  });

  const homes = toArray(homesResp);
  const myHome = homes.find((h: Home) => h.id === user?.home_id);

  const form = useForm<HomeFormData>({
    resolver: zodResolver(homeSchema),
    defaultValues: {
      name: myHome?.name || "",
      address: myHome?.address || "",
      email: myHome?.email || "",
      contact_number: myHome?.contact_number || "",
      bed_count: myHome?.bed_count || undefined,
      organization_id: myHome?.organization_id || null,
      is_active: myHome?.is_active ?? true,
    },
  });

  // Reset form when myHome changes
  useEffect(() => {
    if (myHome) {
      form.reset({
        name: myHome.name,
        address: myHome.address || "",
        email: myHome.email || "",
        contact_number: myHome.contact_number || "",
        bed_count: myHome.bed_count || undefined,
        organization_id: myHome.organization_id || null,
        is_active: myHome.is_active ?? true,
      });
    }
  }, [myHome, form]);

  const updateMutation = useMutation({
    mutationFn: async (data: HomeFormData) => {
      const response = await apiRequest("PUT", `/api/homes/${myHome?.id}`, data);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to update home");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/homes"] });
      toast({ title: "Success", description: "Home information updated successfully" });
      setIsEditing(false);
    },
    onError: (error: Error) => {
      toast({ 
        title: "Error", 
        description: error.message,
        variant: "destructive"
      });
    },
  });

  const onSubmit = (data: HomeFormData) => {
    updateMutation.mutate(data);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="page-title text-[26px] font-bold">My Home</h1>
        </div>
        <div className="h-64 bg-gray-200 rounded animate-pulse" />
      </div>
    );
  }

  if (!myHome) {
    return (
      <div className="space-y-6">
        <ResponsiveBreadcrumb items={breadcrumbs.myHome()} className="mb-4" />
        <Card className="p-8">
          <div className="text-center space-y-4">
            <Building2 className="w-16 h-16 text-gray-400 mx-auto" />
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Home Assigned</h3>
              <p className="text-gray-600">You are not assigned to a care home. Please contact your administrator.</p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ResponsiveBreadcrumb items={breadcrumbs.myHome()} className="mb-4" />
      
      <div className="flex items-center justify-between">
  <h1 className="page-title text-[26px] font-bold text-foreground" data-testid="page-title">My Home</h1>
        {!isEditing && (
          <Button 
            onClick={() => setIsEditing(true)}
            data-testid="button-edit-home"
          >
            <Pencil className="w-4 h-4 mr-2" />
            Edit
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{isEditing ? "Edit Home Information" : myHome.name}</CardTitle>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Home Name *</FormLabel>
                        <FormControl>
                          <Input {...field} data-testid="input-home-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input {...field} data-testid="input-home-email" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Address</FormLabel>
                        <FormControl>
                          <Input {...field} data-testid="input-home-address" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="contact_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Contact Number</FormLabel>
                        <FormControl>
                          <Input {...field} data-testid="input-home-contact" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="bed_count"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Bed Count</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            {...field}
                            value={field.value || ""}
                            onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                            data-testid="input-home-beds"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    type="submit" 
                    disabled={updateMutation.isPending}
                    data-testid="button-save-home"
                  >
                    {updateMutation.isPending ? "Saving..." : "Save Changes"}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      form.reset();
                      setIsEditing(false);
                    }}
                    disabled={updateMutation.isPending}
                    data-testid="button-cancel-edit"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </Form>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Address</label>
                  <p className="text-base" data-testid="home-address">{myHome.address || 'Not specified'}</p>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Email</label>
                  <p className="text-base" data-testid="home-email">{myHome.email || 'Not specified'}</p>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Contact Number</label>
                  <p className="text-base" data-testid="home-contact">{myHome.contact_number || 'Not specified'}</p>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Bed Count</label>
                  <p className="text-base" data-testid="home-beds">{myHome.bed_count || 'Not specified'}</p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Status</label>
                  <p className="text-base" data-testid="home-status">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      myHome.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {myHome.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Organization form schema (for OrgManager to edit their organization)
const orgSchema = z.object({
  name: z.string().min(1, "Organization name is required"),
  address: z.string().optional(),
  contact_number: z.string().optional(),
});

type OrgFormData = z.infer<typeof orgSchema>;

// My Organization Info Page (OrgManager view - editable info display)
function MyOrganizationPage() {
  const { session } = useSession();
  const user = session?.user;
  const breadcrumbs = useBreadcrumbs();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);

  const { data: orgsResp, isLoading } = useQuery({ 
    queryKey: ["/api/organizations"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/organizations");
      const data = await res.json();
      return Array.isArray(data) ? data : data?.items ?? [];
    }
  });

  const orgs = toArray(orgsResp);
  const myOrg = orgs.find((o: Organization) => o.id === user?.organization_id);

  const form = useForm<OrgFormData>({
    resolver: zodResolver(orgSchema),
    defaultValues: {
      name: myOrg?.name || "",
      address: myOrg?.address || "",
      contact_number: myOrg?.contact_number || "",
    },
  });

  useEffect(() => {
    if (myOrg) {
      form.reset({
        name: myOrg.name,
        address: myOrg.address || "",
        contact_number: myOrg.contact_number || "",
      });
    }
  }, [myOrg, form]);

  const updateMutation = useMutation({
    mutationFn: async (data: OrgFormData) => {
      const response = await apiRequest("PUT", `/api/organizations/${myOrg?.id}`, data);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to update organization");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organizations"] });
      toast({ title: "Success", description: "Organization information updated successfully" });
      setIsEditing(false);
    },
    onError: (error: Error) => {
      toast({ 
        title: "Error", 
        description: error.message,
        variant: "destructive"
      });
    },
  });

  const onSubmit = (data: OrgFormData) => {
    updateMutation.mutate(data);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="page-title text-[26px] font-bold">My Organization</h1>
        </div>
        <div className="h-64 bg-gray-200 rounded animate-pulse" />
      </div>
    );
  }

  if (!myOrg) {
    return (
      <div className="space-y-6">
        <ResponsiveBreadcrumb items={[
          { label: "Home", href: "/dashboard" },
          { label: "My Organization" }
        ]} className="mb-4" />
        <Card className="p-8">
          <div className="text-center space-y-4">
            <Building2 className="w-16 h-16 text-gray-400 mx-auto" />
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Organization Assigned</h3>
              <p className="text-gray-600">You are not assigned to an organization. Please contact your administrator.</p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ResponsiveBreadcrumb items={[
        { label: "Home", href: "/dashboard" },
        { label: "My Organization" }
      ]} className="mb-4" />
      
      <div className="flex items-center justify-between">
  <h1 className="page-title text-[26px] font-bold text-foreground" data-testid="page-title">My Organization</h1>
        {!isEditing && (
          <Button 
            onClick={() => setIsEditing(true)}
            data-testid="button-edit-org"
          >
            <Pencil className="w-4 h-4 mr-2" />
            Edit
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{isEditing ? "Edit Organization Information" : myOrg.name}</CardTitle>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Organization Name *</FormLabel>
                        <FormControl>
                          <Input {...field} data-testid="input-org-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="contact_number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Contact Number</FormLabel>
                        <FormControl>
                          <Input {...field} data-testid="input-org-contact" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel>Address</FormLabel>
                        <FormControl>
                          <Input {...field} data-testid="input-org-address" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    type="submit" 
                    disabled={updateMutation.isPending}
                    data-testid="button-save-org"
                  >
                    {updateMutation.isPending ? "Saving..." : "Save Changes"}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      form.reset();
                      setIsEditing(false);
                    }}
                    disabled={updateMutation.isPending}
                    data-testid="button-cancel-edit"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </Form>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Address</label>
                  <p className="text-base" data-testid="org-address">{myOrg.address || 'Not specified'}</p>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Contact Number</label>
                  <p className="text-base" data-testid="org-contact">{myOrg.contact_number || 'Not specified'}</p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Status</label>
                  <p className="text-base" data-testid="org-status">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      myOrg.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {myOrg.status}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Enhanced Admin Homes Page
function HomesPage() {
  const { session } = useSession();
  const user = session?.user;
  const { canWrite, showWriteBlockedMessage } = useWriteGuard();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const breadcrumbs = useBreadcrumbs();
  

  // Get effective user - session user takes precedence (for impersonation)
  const effectiveUser = session?.user ?? user;
  
  // Permission check - Only Developer and PlatformManager may create homes in the UI.
  // OrgManager and lower roles may edit homes (server enforces scope), but cannot create/delete.
  const canManageHomes = effectiveUser?.role === 'Developer' || effectiveUser?.role === 'PlatformManager';

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingHome, setEditingHome] = useState<Home | null>(null);
  const [deletingHome, setDeletingHome] = useState<Home | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Combobox states
  const [orgSearchOpen, setOrgSearchOpen] = useState(false);
  const [statusSearchOpen, setStatusSearchOpen] = useState(false);
  const [orgSearchTerm, setOrgSearchTerm] = useState("");
  const [statusSearchTerm, setStatusSearchTerm] = useState("");
  
  // For OrgManager/OrgAdmin, default to their organization
  const [orgFilter, setOrgFilter] = useState(() => {
    if ((effectiveUser?.role === 'OrgManager' || effectiveUser?.role === 'OrgAdmin') && effectiveUser?.organization_id) {
      return effectiveUser.organization_id.toString();
    }
    return "all";
  });

  // Queries
  const { data: homesResp, isLoading } = useQuery({ 
    queryKey: ["/api/homes"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/homes");
      const data = await res.json();
      return Array.isArray(data) ? data : data?.items ?? [];
    }
  });
  
  const { data: organizationsResp } = useQuery({ 
    queryKey: ["/api/organizations"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/organizations");
      const data = await res.json();
      return Array.isArray(data) ? data : data?.items ?? [];
    }
  });
  
  // Track selected organization for region fetching
  const [selectedOrgIdForRegions, setSelectedOrgIdForRegions] = useState<number | null>(null);
  
  const { data: regionsResp } = useQuery({
    queryKey: ["/api/regions", selectedOrgIdForRegions],
    queryFn: async () => {
      if (!selectedOrgIdForRegions) return [];
      const response = await fetch(`/api/regions?organization_id=${selectedOrgIdForRegions}`, {
        credentials: 'include'
      });
      if (!response.ok) return [];
      return response.json();
    },
    enabled: !!selectedOrgIdForRegions,
  });
  
  // Normalize API responses: handle both array format and {total, items} format
  const homes = toArray(homesResp);
  const organizations = toArray(organizationsResp);
  const regions = regionsResp || [];

  // Form handling
  const form = useForm<HomeFormData>({
    resolver: zodResolver(homeSchema),
    defaultValues: {
      name: "",
      address: "",
      email: "",
      bed_count: undefined,
      organization_id: null,
      region_id: null,
      is_active: true,
    },
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: HomeFormData) => 
      apiRequest("POST", "/api/homes", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/homes"] });
      setIsCreateModalOpen(false);
      form.reset();
      toast({
        title: "Success",
        description: "Home created successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create home",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: HomeFormData }) => 
      apiRequest("PUT", `/api/homes/${id}`, data),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ["/api/homes"] });
      setEditingHome(null);
      form.reset();
      toast({
        title: "Success",
        description: "Home updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update home",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/homes/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/homes"] });
      setDeletingHome(null);
      toast({
        title: "Success",
        description: "Home deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete home",
        variant: "destructive",
      });
    },
  });

  // Handler functions
  const handleSubmit = (data: HomeFormData) => {
    if (editingHome) {
      updateMutation.mutate({ id: editingHome.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (home: Home) => {
    setEditingHome(home);
    setSelectedOrgIdForRegions(home.organization_id || null);
    form.reset({
      name: home.name,
      address: home.address || "",
      email: home.email || "",
      contact_number: home.contact_number || "",
      bed_count: home.bed_count,
      organization_id: home.organization_id || null,
      region_id: (home as any).region_id || null,
      is_active: home.is_active,
    });
  };

  const handleDelete = () => {
    if (deletingHome) {
      deleteMutation.mutate(deletingHome.id);
    }
  };

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
    setEditingHome(null);
    setSelectedOrgIdForRegions(null);
    form.reset();
  };

  // Reset form when opening create modal
  useEffect(() => {
    if (isCreateModalOpen && !editingHome) {
      // For OrgManagers/OrgAdmins: auto-fill organization
      if ((effectiveUser?.role === 'OrgManager' || effectiveUser?.role === 'OrgAdmin') && effectiveUser?.organization_id) {
        setSelectedOrgIdForRegions(effectiveUser.organization_id);
        form.reset({
          name: "",
          address: "",
          email: "",
          contact_number: "",
          bed_count: undefined,
          organization_id: effectiveUser.organization_id,
          region_id: null,
          is_active: true,
        });
      } else {
        // For Developers/Platform Managers: blank form
        setSelectedOrgIdForRegions(null);
        form.reset({
          name: "",
          address: "",
          email: "",
          contact_number: "",
          bed_count: undefined,
          organization_id: null,
          region_id: null,
          is_active: true,
        });
      }
    }
  }, [isCreateModalOpen, editingHome, effectiveUser, form]);

  // Filtered data
  const filteredHomes = homes.filter((home: Home) => {
    const matchesSearch = home.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (home.address || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" ||
                         (statusFilter === "active" && home.is_active) ||
                         (statusFilter === "inactive" && !home.is_active);
    const matchesOrg = orgFilter === "all" ||
                       (orgFilter === "standalone" && !home.organization_id) ||
                       (orgFilter !== "standalone" && home.organization_id === parseInt(orgFilter));
    return matchesSearch && matchesStatus && matchesOrg;
  });

  // Helper functions
  const getOrganizationName = (orgId: number | null | undefined) => {
    if (!orgId) return "Standalone";
    const org = organizations.find((o: Organization) => o.id === orgId);
    return org?.name || "Unknown";
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="page-title text-[26px] font-bold">Care Homes</h1>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-gray-200 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ResponsiveBreadcrumb items={breadcrumbs.homes()} className="mb-4" />
      
      <div className="flex items-center justify-between">
  <h1 className="page-title text-[26px] font-bold text-foreground" data-testid="page-title">
          Care Homes
        </h1>
        {canManageHomes && (
          <Button 
            onClick={() => !canWrite ? toast(showWriteBlockedMessage()) : setIsCreateModalOpen(true)}
            disabled={!canWrite}
            data-testid="button-create-home"
            title={!canWrite ? "Create disabled during impersonation" : "Create new home"}
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Home
          </Button>
        )}
      </div>

      {/* Search and Filter */}
      <div className="flex gap-4 p-4 bg-card rounded-lg border">
        <div className="flex-1">
          <Input
            placeholder="Search homes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="search-homes"
          />
        </div>
        <div className="w-64">
          <Popover open={orgSearchOpen} onOpenChange={setOrgSearchOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={orgSearchOpen}
                className="w-full justify-between"
                data-testid="filter-organization"
              >
                {orgFilter === 'all' 
                  ? 'All Organizations' 
                  : orgFilter === 'standalone'
                  ? 'Standalone Homes'
                  : organizations.find((org: Organization) => org.id.toString() === orgFilter)?.name || 'Select...'}
                <ArrowUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0" align="start">
              <Command>
                <CommandInput 
                  placeholder="Search organizations..." 
                  value={orgSearchTerm}
                  onValueChange={setOrgSearchTerm}
                />
                <CommandList>
                  <CommandEmpty>No organizations found.</CommandEmpty>
                  <CommandGroup>
                    {effectiveUser?.role !== 'OrgManager' && effectiveUser?.role !== 'OrgAdmin' && (
                      <CommandItem
                        value="all"
                        onSelect={() => {
                          setOrgFilter('all');
                          setOrgSearchOpen(false);
                          setOrgSearchTerm("");
                        }}
                      >
                        <CheckCircle className={`mr-2 h-4 w-4 ${orgFilter === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                        All Organizations
                      </CommandItem>
                    )}
                    {(effectiveUser?.role === 'Developer' || effectiveUser?.role === 'PlatformManager') && (
                      <CommandItem
                        value="standalone"
                        onSelect={() => {
                          setOrgFilter('standalone');
                          setOrgSearchOpen(false);
                          setOrgSearchTerm("");
                        }}
                      >
                        <CheckCircle className={`mr-2 h-4 w-4 ${orgFilter === 'standalone' ? 'opacity-100' : 'opacity-0'}`} />
                        Standalone Homes
                      </CommandItem>
                    )}
                    {organizations
                      .filter((org: Organization) => 
                        org.name.toLowerCase().includes(orgSearchTerm.toLowerCase())
                      )
                      .map((org: Organization) => (
                        <CommandItem
                          key={org.id}
                          value={org.name}
                          onSelect={() => {
                            setOrgFilter(org.id.toString());
                            setOrgSearchOpen(false);
                            setOrgSearchTerm("");
                          }}
                        >
                          <CheckCircle className={`mr-2 h-4 w-4 ${orgFilter === org.id.toString() ? 'opacity-100' : 'opacity-0'}`} />
                          {org.name}
                        </CommandItem>
                      ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <div className="w-48">
          <Popover open={statusSearchOpen} onOpenChange={setStatusSearchOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={statusSearchOpen}
                className="w-full justify-between"
                data-testid="filter-status"
              >
                {statusFilter === 'all' ? 'All Status' : statusFilter === 'active' ? 'Active' : 'Inactive'}
                <ArrowUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-48 p-0" align="start">
              <Command>
                <CommandInput 
                  placeholder="Search status..." 
                  value={statusSearchTerm}
                  onValueChange={setStatusSearchTerm}
                />
                <CommandList>
                  <CommandEmpty>No status found.</CommandEmpty>
                  <CommandGroup>
                    {[
                      { value: 'all', label: 'All Status' },
                      { value: 'active', label: 'Active' },
                      { value: 'inactive', label: 'Inactive' }
                    ].filter((status) =>
                      status.label.toLowerCase().includes(statusSearchTerm.toLowerCase())
                    ).map((status) => (
                      <CommandItem
                        key={status.value}
                        value={status.value}
                        onSelect={() => {
                          setStatusFilter(status.value);
                          setStatusSearchOpen(false);
                          setStatusSearchTerm("");
                        }}
                      >
                        <CheckCircle className={`mr-2 h-4 w-4 ${statusFilter === status.value ? 'opacity-100' : 'opacity-0'}`} />
                        {status.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Homes Table */}
      {filteredHomes.length === 0 ? (
        <Card className="p-8">
          <div className="text-center space-y-4" data-testid="empty-state">
            <Building2 className="w-16 h-16 text-gray-400 mx-auto" />
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm || statusFilter !== "all" || orgFilter !== "all" ? "No homes found" : "No homes yet"}
              </h3>
              <p className="text-gray-600">
                {searchTerm || statusFilter !== "all" || orgFilter !== "all"
                  ? "Try adjusting your search or filter criteria."
                  : "Create your first care home to get started."}
              </p>
            </div>
            {!(searchTerm || statusFilter !== "all" || orgFilter !== "all") && canManageHomes && (
              <Button 
                onClick={() => !canWrite ? toast(showWriteBlockedMessage()) : setIsCreateModalOpen(true)}
                disabled={!canWrite}
                data-testid="create-first-home"
                title={!canWrite ? "Create disabled during impersonation" : "Create first home"}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create First Home
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Beds</TableHead>
                <TableHead>Organization</TableHead>
                <TableHead>Status</TableHead>
                {canManageHomes && <TableHead className="w-[120px]">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredHomes.map((home) => (
                <TableRow key={home.id} data-testid={`row-home-${home.id}`}>
                  <TableCell>
                    <div className="font-medium">{home.name}</div>
                  </TableCell>
                  <TableCell data-testid={`address-${home.id}`}>
                    {home.address || "-"}
                  </TableCell>
                  <TableCell className="text-sm">
                    {home.email || "-"}
                  </TableCell>
                  <TableCell>{home.bed_count || "-"}</TableCell>
                  <TableCell data-testid={`org-${home.id}`}>
                    {getOrganizationName(home.organization_id)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={home.is_active ? "default" : "secondary"}>
                      {home.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  {canManageHomes && (
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => !canWrite ? toast(showWriteBlockedMessage()) : handleEdit(home)}
                          disabled={!canWrite}
                          data-testid={`button-edit-${home.id}`}
                          title={!canWrite ? "Edit disabled during impersonation" : "Edit home"}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => !canWrite ? toast(showWriteBlockedMessage()) : setDeletingHome(home)}
                          disabled={!canWrite}
                          data-testid={`button-delete-${home.id}`}
                          title={!canWrite ? "Delete disabled during impersonation" : "Delete home"}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={isCreateModalOpen || editingHome !== null} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-2xl" data-testid="home-modal">
          <DialogHeader>
            <DialogTitle>
              {editingHome ? "Edit Home" : "Create Home"}
            </DialogTitle>
            <DialogDescription>
              {editingHome 
                ? "Update the home details below."
                : "Enter the details for the new care home."}
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Home Name</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="Sunny Meadows Care Home" 
                        {...field} 
                        data-testid="input-name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                />

                <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="123 High Street, Manchester, M1 1AA" 
                        {...field} 
                        data-testid="input-address"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />


              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input 
                          type="email"
                          placeholder="contact@carehome.com" 
                          {...field} 
                          data-testid="input-email"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="contact_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact Number</FormLabel>
                      <FormControl>
                        <Input 
                          type="tel"
                          placeholder="01234 567890" 
                          {...field} 
                          data-testid="input-contact-number"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="bed_count"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bed Count</FormLabel>
                    <FormControl>
                      <Input 
                        type="number"
                        min="0"
                        {...field} 
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                        data-testid="input-bed-count"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {canManageHomes && (
                <>
                  <FormField
                    control={form.control}
                    name="organization_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Organization (Optional)</FormLabel>
                        <FormControl>
                          <select
                            value={field.value?.toString() ?? "null"}
                            onChange={(e) => {
                              const newValue = e.target.value === "null" ? null : parseInt(e.target.value);
                              field.onChange(newValue);
                              setSelectedOrgIdForRegions(newValue);
                            }}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                            data-testid="select-organization"
                          >
                            <option value="null">Standalone (No Organization)</option>
                            {organizations.length > 0 ? (
                              organizations
                                .filter((org: Organization) => org.status === 'Active')
                                .map((org: Organization) => (
                                  <option key={org.id} value={org.id.toString()}>
                                    {org.name}
                                  </option>
                                ))
                            ) : (
                              <option disabled>Loading organizations...</option>
                            )}
                          </select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {form.watch("organization_id") && (
                    <FormField
                      control={form.control}
                      name="region_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Region (Optional)</FormLabel>
                          <FormControl>
                            <select
                              value={field.value?.toString() ?? "null"}
                              onChange={(e) => {
                                const newValue = e.target.value === "null" ? null : parseInt(e.target.value);
                                field.onChange(newValue);
                              }}
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                              data-testid="select-region"
                            >
                              <option value="null">No Region</option>
                              {regions.length > 0 ? (
                                regions.map((region: any) => (
                                  <option key={region.id} value={region.id.toString()}>
                                    {region.name}
                                  </option>
                                ))
                              ) : (
                                <option disabled>No regions available</option>
                              )}
                            </select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </>
              )}

              <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        className="w-4 h-4"
                        data-testid="checkbox-active"
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Active</FormLabel>
                      <p className="text-sm text-muted-foreground">
                        Whether this home is actively operating
                      </p>
                    </div>
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseModal}
                  data-testid="button-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  data-testid="button-save"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? "Saving..."
                    : editingHome 
                    ? "Update Home"
                    : "Create Home"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deletingHome !== null}
        onOpenChange={(open) => !open && setDeletingHome(null)}
        title="Delete Home"
        subtitle={`Are you sure you want to delete "${deletingHome?.name}"? This action cannot be undone and will remove all associated data.`}
        confirmText="Delete Home"
        onConfirm={handleDelete}
        requireInput={deletingHome?.name}
        variant="destructive"
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}

// Template Configuration Dialog Component
function TemplateConfigurationDialog({ 
  template, 
  open, 
  onOpenChange,
  existingConfig
}: { 
  template: Template | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingConfig?: any;
}) {
  const { session } = useSession();
  const user = session?.user;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Determine scope type based on user role
  // HomeManager can ONLY configure at Home scope
  // OrgManager/OrgAdmin can ONLY configure at Organization scope
  const isOrgManager = user?.role === 'OrgManager' || user?.role === 'OrgAdmin';
  const isHomeManager = user?.role === 'HomeManager';
  const defaultScopeType = isOrgManager ? 'Organization' : 'Home';
  
  const form = useForm({
    resolver: zodResolver(
      z.object({
        priority: z.enum(['urgent', 'high', 'medium', 'low']),
        frequency_type: z.enum(['once', 'daily', 'weekly', 'monthly', 'custom_interval']),
        frequency_value: z.number().optional(),
        start_date: z.string().optional(),
        end_date: z.string().optional(),
        next_due_at: z.string().optional(),
        notify_before_days: z.number().min(0).max(30),
        scope_type: z.enum(['Organization', 'Home']),
        is_active: z.boolean(),
        
        // Advanced recurrence fields
        repeat_interval: z.number().optional(),
        repeat_on_days: z.array(z.number()).optional(),
        monthly_pattern_type: z.enum(['day_of_month', 'day_of_week']).optional(),
        monthly_day_of_month: z.number().optional(),
        monthly_week_of_month: z.number().optional(),
        monthly_day_of_week: z.number().optional(),
        end_condition_type: z.enum(['never', 'after_occurrences', 'on_date']).optional(),
        end_after_occurrences: z.number().optional(),
        
        // Daily reminder time
        daily_reminder_time: z.string().optional(),
      })
    ),
    defaultValues: {
      priority: existingConfig?.priority || 'medium',
      frequency_type: existingConfig?.frequency_type || 'once',
      frequency_value: existingConfig?.frequency_value || 7,
      start_date: existingConfig?.start_date || '',
      end_date: existingConfig?.end_date || '',
      next_due_at: existingConfig?.next_due_at || '',
      notify_before_days: existingConfig?.notify_before_days || 3,
      scope_type: existingConfig?.scope_type || defaultScopeType,
      is_active: existingConfig?.is_active ?? true,
      
      // Advanced recurrence defaults
      repeat_interval: existingConfig?.repeat_interval || 1,
      repeat_on_days: existingConfig?.repeat_on_days || [],
      monthly_pattern_type: existingConfig?.monthly_pattern_type || 'day_of_month',
      monthly_day_of_month: existingConfig?.monthly_day_of_month || 1,
      monthly_week_of_month: existingConfig?.monthly_week_of_month || 1,
      monthly_day_of_week: existingConfig?.monthly_day_of_week || 1,
      end_condition_type: existingConfig?.end_condition_type || 'never',
      end_after_occurrences: existingConfig?.end_after_occurrences || 10,
      
      // Daily reminder time default (9:00 AM UK time)
      daily_reminder_time: existingConfig?.daily_reminder_time || '09:00',
    },
  });
  
  const frequencyType = form.watch('frequency_type');
  const monthlyPatternType = form.watch('monthly_pattern_type');
  const endConditionType = form.watch('end_condition_type');
  const repeatOnDays = form.watch('repeat_on_days');
  
  const saveMutation = useMutation({
    mutationFn: async (data: any) => {
      const payload = {
        template_id: template?.id,
        scope_type: data.scope_type,
        scope_id: data.scope_type === 'Organization' ? user?.organization_id : user?.home_id,
        priority: data.priority,
        frequency_type: data.frequency_type,
        frequency_value: data.frequency_type === 'custom_interval' ? data.frequency_value : undefined,
        start_date: data.start_date || undefined,
        end_date: data.end_date || undefined,
        next_due_at: data.next_due_at || undefined,
        notify_before_days: data.notify_before_days,
        is_active: data.is_active,
        
        // Advanced recurrence fields - only include if frequency is weekly or monthly
        repeat_interval: (data.frequency_type === 'weekly' || data.frequency_type === 'monthly') ? data.repeat_interval : undefined,
        repeat_on_days: data.frequency_type === 'weekly' ? data.repeat_on_days : undefined,
        monthly_pattern_type: data.frequency_type === 'monthly' ? data.monthly_pattern_type : undefined,
        monthly_day_of_month: (data.frequency_type === 'monthly' && data.monthly_pattern_type === 'day_of_month') ? data.monthly_day_of_month : undefined,
        monthly_week_of_month: (data.frequency_type === 'monthly' && data.monthly_pattern_type === 'day_of_week') ? data.monthly_week_of_month : undefined,
        monthly_day_of_week: (data.frequency_type === 'monthly' && data.monthly_pattern_type === 'day_of_week') ? data.monthly_day_of_week : undefined,
        end_condition_type: (data.frequency_type === 'weekly' || data.frequency_type === 'monthly') ? data.end_condition_type : undefined,
        end_after_occurrences: (data.end_condition_type === 'after_occurrences') ? data.end_after_occurrences : undefined,
        
        // Daily reminder time - only include if frequency is daily
        daily_reminder_time: data.frequency_type === 'daily' ? data.daily_reminder_time : undefined,
      };
      
      if (existingConfig) {
        const response = await apiRequest("PATCH", `/api/template-configurations/${existingConfig.id}`, payload);
        return response.json();
      } else {
        const response = await apiRequest("POST", "/api/template-configurations", payload);
        return response.json();
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/template-configurations"] });
      toast({
        title: "Success",
        description: existingConfig ? "Configuration updated successfully" : "Template configured successfully",
      });
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save configuration",
        variant: "destructive",
      });
    },
  });
  
  const onSubmit = (data: any) => {
    saveMutation.mutate(data);
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Configure Template: {template?.name}</DialogTitle>
          <p className="text-sm text-gray-600">
            Set up scheduling and priority for this template
          </p>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Priority */}
            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Priority</FormLabel>
                  <select
                    {...field}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            {/* Frequency Type */}
            <FormField
              control={form.control}
              name="frequency_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Frequency</FormLabel>
                  <select
                    {...field}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="once">Once</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="custom_interval">Custom Interval</option>
                  </select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            {/* Custom Interval Days */}
            {frequencyType === 'custom_interval' && (
              <FormField
                control={form.control}
                name="frequency_value"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Interval (Days)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="1"
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            
            {/* Daily Reminder Time */}
            {frequencyType === 'daily' && (
              <FormField
                control={form.control}
                name="daily_reminder_time"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reminder Time (UK Time)</FormLabel>
                    <FormControl>
                      <Input
                        type="time"
                        {...field}
                        className="w-48"
                        data-testid="input-daily-reminder-time"
                      />
                    </FormControl>
                    <p className="text-xs text-gray-500 mt-1">
                      🇬🇧 Time shown in UK timezone (Europe/London). Notifications will be sent at this time daily.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            
            {/* Advanced Recurrence Options - Weekly */}
            {(frequencyType === 'weekly' || frequencyType === 'monthly') && (
              <div className="border-t border-gray-200 pt-4 mt-4">
                <h3 className="text-sm font-medium text-gray-900 mb-4">Recurrence Pattern</h3>
                
                {/* Repeat Interval */}
                <FormField
                  control={form.control}
                  name="repeat_interval"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Repeat every {field.value || 1} {frequencyType === 'weekly' ? (field.value === 1 ? 'week' : 'weeks') : (field.value === 1 ? 'month' : 'months')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="1"
                          max="52"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                          className="w-24"
                          data-testid="input-repeat-interval"
                        />
                      </FormControl>
                      <p className="text-xs text-gray-500">How often this audit repeats</p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                {/* Weekly: Repeat on days */}
                {frequencyType === 'weekly' && (
                  <FormField
                    control={form.control}
                    name="repeat_on_days"
                    render={({ field }) => (
                      <FormItem className="mt-4">
                        <FormLabel>Repeat on</FormLabel>
                        <div className="flex gap-2 flex-wrap">
                          {[
                            { label: 'Sun', value: 0 },
                            { label: 'Mon', value: 1 },
                            { label: 'Tue', value: 2 },
                            { label: 'Wed', value: 3 },
                            { label: 'Thu', value: 4 },
                            { label: 'Fri', value: 5 },
                            { label: 'Sat', value: 6 },
                          ].map(day => (
                            <button
                              key={day.value}
                              type="button"
                              onClick={() => {
                                const currentDays = field.value || [];
                                const newDays = currentDays.includes(day.value)
                                  ? currentDays.filter((d: number) => d !== day.value)
                                  : [...currentDays, day.value].sort();
                                field.onChange(newDays);
                              }}
                              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                (field.value || []).includes(day.value)
                                  ? 'bg-[hsl(var(--brand-primary))] text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                              data-testid={`button-day-${day.label.toLowerCase()}`}
                            >
                              {day.label}
                            </button>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          {(field.value || []).length === 0 
                            ? 'Select at least one day' 
                            : `Audit will repeat on ${(field.value || []).map((d: number) => ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][d]).join(', ')}`}
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                
                {/* Monthly: Pattern Type */}
                {frequencyType === 'monthly' && (
                  <>
                    <FormField
                      control={form.control}
                      name="monthly_pattern_type"
                      render={({ field }) => (
                        <FormItem className="mt-4">
                          <FormLabel>Repeat by</FormLabel>
                          <select
                            {...field}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            data-testid="select-monthly-pattern"
                          >
                            <option value="day_of_month">Day of the month</option>
                            <option value="day_of_week">Day of the week</option>
                          </select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    {monthlyPatternType === 'day_of_month' ? (
                      <FormField
                        control={form.control}
                        name="monthly_day_of_month"
                        render={({ field }) => (
                          <FormItem className="mt-4">
                            <FormLabel>Day of month</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                min="1"
                                max="31"
                                {...field}
                                onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                                className="w-24"
                                data-testid="input-day-of-month"
                              />
                            </FormControl>
                            <p className="text-xs text-gray-500">
                              Audit will repeat on day {field.value || 1} of each month
                            </p>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    ) : (
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <FormField
                          control={form.control}
                          name="monthly_week_of_month"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Week of month</FormLabel>
                              <select
                                {...field}
                                onChange={(e) => field.onChange(parseInt(e.target.value))}
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                data-testid="select-week-of-month"
                              >
                                <option value="1">First</option>
                                <option value="2">Second</option>
                                <option value="3">Third</option>
                                <option value="4">Fourth</option>
                                <option value="-1">Last</option>
                              </select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="monthly_day_of_week"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Day of week</FormLabel>
                              <select
                                {...field}
                                onChange={(e) => field.onChange(parseInt(e.target.value))}
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                data-testid="select-day-of-week"
                              >
                                <option value="0">Sunday</option>
                                <option value="1">Monday</option>
                                <option value="2">Tuesday</option>
                                <option value="3">Wednesday</option>
                                <option value="4">Thursday</option>
                                <option value="5">Friday</option>
                                <option value="6">Saturday</option>
                              </select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    )}
                  </>
                )}
                
                {/* End Condition */}
                <FormField
                  control={form.control}
                  name="end_condition_type"
                  render={({ field }) => (
                    <FormItem className="mt-4">
                      <FormLabel>Ends</FormLabel>
                      <select
                        {...field}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        data-testid="select-end-condition"
                      >
                        <option value="never">Never</option>
                        <option value="on_date">On date</option>
                        <option value="after_occurrences">After number of occurrences</option>
                      </select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                {endConditionType === 'after_occurrences' && (
                  <FormField
                    control={form.control}
                    name="end_after_occurrences"
                    render={({ field }) => (
                      <FormItem className="mt-4">
                        <FormLabel>Number of occurrences</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="1"
                            max="999"
                            {...field}
                            onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                            className="w-32"
                            data-testid="input-occurrences"
                          />
                        </FormControl>
                        <p className="text-xs text-gray-500">
                          This audit will occur {field.value || 1} time{(field.value || 1) > 1 ? 's' : ''} before ending
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            )}
            
            {/* Scope Type - Auto-set based on role */}
            <FormField
              control={form.control}
              name="scope_type"
              render={({ field }) => (
                <FormItem>
                  <input type="hidden" {...field} value={defaultScopeType} />
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5">
                        {defaultScopeType === 'Organization' ? (
                          <svg className="h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {defaultScopeType === 'Organization' ? 'Organization-wide Configuration' : 'Home-specific Configuration'}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          {defaultScopeType === 'Organization' 
                            ? 'This configuration will apply to all homes in your organization' 
                            : 'This configuration will only apply to your home'}
                        </p>
                      </div>
                    </div>
                  </div>
                </FormItem>
              )}
            />
            
            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="next_due_at"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Next Due</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            {/* Notify Before Days */}
            <FormField
              control={form.control}
              name="notify_before_days"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notify Before (Days)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      max="30"
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value))}
                    />
                  </FormControl>
                  <p className="text-xs text-gray-500">Send reminder notification this many days before due date</p>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            {/* Active Toggle */}
            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Active</FormLabel>
                    <p className="text-sm text-gray-500">Enable this template configuration</p>
                  </div>
                  <FormControl>
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={field.onChange}
                      className="h-4 w-4"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saveMutation.isPending}
                className="bg-[hsl(var(--brand-primary))] hover:bg-[hsl(var(--secondary))]"
              >
                {saveMutation.isPending ? 'Saving...' : existingConfig ? 'Update Configuration' : 'Save Configuration'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// Templates List Page
function TemplatesPage() {
  const { session } = useSession();
  const user = session?.user;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const { data: templatesResponse, isLoading } = useQuery({
    queryKey: ["/api/templates/available"],
    queryFn: () => apiRequest("GET", "/api/templates/available?limit=100").then(res => res.json())
  });
  
  // Extract templates from the response (handle both array and paginated response)
  const templates = templatesResponse?.items || [];
  
  const { canManageTemplates } = usePermissions();
  const { data: hasCreatePermission = false, isLoading: isLoadingPermission } = useCanCreateTemplates();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [deletingTemplate, setDeletingTemplate] = useState<Template | null>(null);
  const [configuringTemplate, setConfiguringTemplate] = useState<Template | null>(null);
  const breadcrumbs = useBreadcrumbs();
  
  // Combobox states
  const [categorySearchOpen, setCategorySearchOpen] = useState(false);
  const [typeSearchOpen, setTypeSearchOpen] = useState(false);
  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const [typeSearchTerm, setTypeSearchTerm] = useState("");
  
  // Check if user is a manager who can configure templates
  const isManager = user?.role && ['OrgManager', 'OrgAdmin', 'HomeManager'].includes(user.role);
  const isHomeAdmin = user?.role === 'HomeAdmin';
  
  // Fetch template configurations for this user's scope
  const { data: configurationsResponse } = useQuery({
    queryKey: ["/api/template-configurations"],
    queryFn: () => apiRequest("GET", "/api/template-configurations").then(res => res.json()),
    enabled: Boolean(isManager)
  });
  
  const configurations = configurationsResponse?.items || [];
  
  // Fetch user's audits to check for existing drafts
  const { data: auditsResponse } = useQuery({
    queryKey: ["/api/audits"],
    queryFn: () => apiRequest("GET", "/api/audits?limit=1000").then(res => res.json()),
    enabled: Boolean(user)
  });
  
  const audits = Array.isArray(auditsResponse) ? auditsResponse : (auditsResponse?.items || []);
  
  // Get configuration for a specific template
  const getTemplateConfig = (templateId: number) => {
    return configurations.find((config: any) => config.template_id === templateId);
  };
  
  // Get draft audit for a specific template (if exists) that the current user is allowed to continue
  const getDraftAudit = (templateId: number) => {
    return audits.find((audit: any) => {
      if (!(audit && user)) return false;
      if (audit.template_id !== templateId) return false;
      if (audit.status !== 'Draft') return false;
      if (audit.home_id !== user.home_id) return false;
      // Only allow if assigned to current user, otherwise if unassigned allow creator
      const isAssignee = typeof audit.assigned_to === 'number' ? audit.assigned_to === user.id : false;
      const isCreator = audit.created_by === user.id;
      return isAssignee || (!audit.assigned_to && isCreator);
    });
  };
  
  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/templates/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/templates/available"] });
      queryClient.invalidateQueries({ queryKey: ["/api/templates"] });
      toast({
        title: "Success",
        description: "Template deleted successfully",
      });
      setDeletingTemplate(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete template",
        variant: "destructive",
      });
    },
  });
  
  const handleDelete = async () => {
    if (!deletingTemplate) return;
    deleteMutation.mutate(deletingTemplate.id);
  };

  const filteredTemplates = templates.filter((template: Template) => {
    const name = (template.name || '').toString();
    const desc = (template.description || '').toString();
    const matchesSearch = name.toLowerCase().includes((searchTerm || '').toLowerCase()) || 
                         desc.toLowerCase().includes((searchTerm || '').toLowerCase());
    const matchesCategory = categoryFilter === "all" || template.category_name === categoryFilter;
    const matchesType = typeFilter === "all" || template.type_name === typeFilter;
    return matchesSearch && matchesCategory && matchesType;
  });

  // Get unique categories and types for filters
  const categories = Array.from(new Set(templates.map((t: Template) => t.category_name).filter(Boolean)));
  const auditTypes = Array.from(new Set(templates.map((t: Template) => t.type_name).filter(Boolean)));

  // Determine page title based on user role
  const pageTitle = user?.role === 'Developer' || user?.role === 'PlatformManager' 
    ? 'Template Management' 
    : 'Audit Management';

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="page-title text-[26px] font-bold text-foreground">{pageTitle}</h1>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <div className="h-6 bg-gray-200 rounded animate-pulse mb-2" />
                <div className="h-4 bg-gray-200 rounded animate-pulse" />
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  <div className="h-8 bg-gray-200 rounded animate-pulse" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ResponsiveBreadcrumb items={breadcrumbs.templates()} className="mb-4" />
      <div className="flex items-center justify-between">
  <h1 className="page-title text-[26px] font-bold text-foreground" data-testid="page-title">{pageTitle}</h1>
        {hasCreatePermission && !isLoadingPermission && (
          <Link href="/templates/builder">
            <Button data-testid="button-create-template">
              <Plus className="w-4 h-4 mr-2" />
              Create Template
            </Button>
          </Link>
        )}
      </div>
      
      {/* Search and Filter Toolbar */}
      <div className="flex flex-col lg:flex-row gap-4 p-4 bg-card rounded-lg shadow-sm border border-border">
        <div className="flex-1">
          <Input
            placeholder="Search templates by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
            data-testid="search-templates"
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="sm:w-48">
            <Popover open={categorySearchOpen} onOpenChange={setCategorySearchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={categorySearchOpen}
                  className="w-full justify-between"
                  data-testid="filter-category"
                >
                  {categoryFilter === 'all' ? 'All Categories' : categoryFilter}
                  <ArrowUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-0" align="start">
                <Command>
                  <CommandInput 
                    placeholder="Search categories..." 
                    value={categorySearchTerm}
                    onValueChange={setCategorySearchTerm}
                  />
                  <CommandList>
                    <CommandEmpty>No categories found.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="all"
                        onSelect={() => {
                          setCategoryFilter('all');
                          setCategorySearchOpen(false);
                          setCategorySearchTerm("");
                        }}
                      >
                        <CheckCircle className={`mr-2 h-4 w-4 ${categoryFilter === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                        All Categories
                      </CommandItem>
                      {categories
                        .filter((category) => 
                          (category as string).toLowerCase().includes(categorySearchTerm.toLowerCase())
                        )
                        .map((category) => (
                          <CommandItem
                            key={category as string}
                            value={category as string}
                            onSelect={() => {
                              setCategoryFilter(category as string);
                              setCategorySearchOpen(false);
                              setCategorySearchTerm("");
                            }}
                          >
                            <CheckCircle className={`mr-2 h-4 w-4 ${categoryFilter === category ? 'opacity-100' : 'opacity-0'}`} />
                            {category as string}
                          </CommandItem>
                        ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <div className="sm:w-48">
            <Popover open={typeSearchOpen} onOpenChange={setTypeSearchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={typeSearchOpen}
                  className="w-full justify-between"
                  data-testid="filter-type"
                >
                  {typeFilter === 'all' ? 'All Types' : typeFilter}
                  <ArrowUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-0" align="start">
                <Command>
                  <CommandInput 
                    placeholder="Search types..." 
                    value={typeSearchTerm}
                    onValueChange={setTypeSearchTerm}
                  />
                  <CommandList>
                    <CommandEmpty>No types found.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="all"
                        onSelect={() => {
                          setTypeFilter('all');
                          setTypeSearchOpen(false);
                          setTypeSearchTerm("");
                        }}
                      >
                        <CheckCircle className={`mr-2 h-4 w-4 ${typeFilter === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                        All Types
                      </CommandItem>
                      {auditTypes
                        .filter((type) => 
                          (type as string).toLowerCase().includes(typeSearchTerm.toLowerCase())
                        )
                        .map((type) => (
                          <CommandItem
                            key={type as string}
                            value={type as string}
                            onSelect={() => {
                              setTypeFilter(type as string);
                              setTypeSearchOpen(false);
                              setTypeSearchTerm("");
                            }}
                          >
                            <CheckCircle className={`mr-2 h-4 w-4 ${typeFilter === type ? 'opacity-100' : 'opacity-0'}`} />
                            {type as string}
                          </CommandItem>
                        ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>
      
      {/* Results Summary */}
      {searchTerm || categoryFilter !== "all" || typeFilter !== "all" ? (
        <div className="text-sm text-gray-600" data-testid="results-summary">
          Showing {filteredTemplates.length} of {templates.length} templates
          {searchTerm && ` matching "${searchTerm}"`}
          {categoryFilter !== "all" && ` in ${categoryFilter}`}
          {typeFilter !== "all" && ` (${typeFilter})`}
        </div>
      ) : null}
      
      {/* Templates Grid */}
      {filteredTemplates.length === 0 ? (
        <Card className="p-8">
          <div className="text-center space-y-4" data-testid="empty-state">
            <FileText className="w-16 h-16 text-gray-400 mx-auto" />
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm || categoryFilter !== "all" || typeFilter !== "all" ? "No templates found" : "No templates available"}
              </h3>
              <p className="text-gray-600">
                {searchTerm || categoryFilter !== "all" || typeFilter !== "all" 
                  ? "Try adjusting your search or filter criteria."
                  : "Templates will appear here once they are created by system administrators."}
              </p>
            </div>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredTemplates.map((template: Template) => {
            const canEditTemplate = user?.role === 'Developer' || template.created_by === user?.id;
            
            return (
              <Card key={template.id} className="hover:shadow-lg transition-all duration-200 border border-gray-200 hover:border-purple-200">
                <CardContent className="p-0">
                  <div className="flex flex-col sm:flex-row items-stretch">
                    {/* Left accent bar */}
                    <div className="w-full sm:w-1.5 bg-gradient-to-b from-purple-600 to-purple-400 rounded-l-lg" />
                    
                    {/* Main content */}
                    <div className="flex-1 p-5 sm:p-6">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        {/* Template Info */}
                        <div className="flex-1 min-w-0 space-y-3">
                          <div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2 leading-tight">{template.name}</h3>
                            <p className="text-base text-gray-600 leading-relaxed max-w-2xl">{template.description}</p>
                          </div>
                          
                          <div className="flex flex-wrap items-center gap-3">
                            <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 border-0 px-3 py-1 text-sm font-medium">
                              {template.category_name}
                            </Badge>
                            <Badge variant="outline" className="text-gray-700 border-gray-300 px-3 py-1 text-sm">
                              {template.type_name}
                            </Badge>
                            {isManager && getTemplateConfig(template.id) && (
                              <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-0 px-3 py-1 text-sm font-medium">
                                ✓ Configured
                              </Badge>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex sm:flex-col items-center sm:items-end gap-2 sm:gap-3">
                          <div className="flex items-center gap-1">
                            {canEditTemplate && (
                              <>
                                <Link href={`/templates/builder/edit/${template.id}`}>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-9 w-9 p-0 hover:bg-gray-100 transition-colors"
                                    data-testid={`button-edit-template-${template.id}`}
                                  >
                                    <Pencil className="h-4 w-4 text-gray-600" />
                                  </Button>
                                </Link>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-9 w-9 p-0 hover:bg-red-50 transition-colors"
                                  onClick={() => setDeletingTemplate(template)}
                                  data-testid={`button-delete-template-${template.id}`}
                                >
                                  <Trash2 className="h-4 w-4 text-red-600" />
                                </Button>
                              </>
                            )}
                          </div>
                          
                          <div className="flex flex-col gap-2 w-full sm:w-auto">
                            {/* Configure button: visible for managers but hidden for Home Admin */}
                            {isManager && !isHomeAdmin && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setConfiguringTemplate(template)}
                                className="w-full sm:w-auto border-[hsl(var(--brand-primary))] text-[hsl(var(--brand-primary))] hover:bg-purple-50"
                                data-testid={`button-configure-template-${template.id}`}
                              >
                                <Settings className="h-4 w-4 mr-1" />
                                {getTemplateConfig(template.id) ? 'Edit Config' : 'Configure'}
                              </Button>
                            )}
                            
                            {/* Continue Audit or Use Template button */}
                            {(() => {
                              const draftAudit = getDraftAudit(template.id);
                              if (draftAudit) {
                                return (
                                  <Link href={`/audits/take?auditId=${draftAudit.id}`} className="w-full sm:w-auto">
                                    <Button 
                                      className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white shadow-sm px-6" 
                                      data-testid={`button-continue-audit-${template.id}`}
                                    >
                                      Continue Audit
                                    </Button>
                                  </Link>
                                );
                              }
                              return (
                                <Link href={`/audits/new?templateId=${template.id}`} className="w-full sm:w-auto">
                                  <Button 
                                    className="w-full sm:w-auto bg-[hsl(var(--brand-primary))] hover:bg-[hsl(var(--secondary))] text-white shadow-sm px-6" 
                                    data-testid={`button-use-template-${template.id}`}
                                  >
                                    Use Template
                                  </Button>
                                </Link>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deletingTemplate !== null}
        onOpenChange={(open) => !open && setDeletingTemplate(null)}
        title="Delete Template"
        subtitle={`Are you sure you want to delete "${deletingTemplate?.name}"? This action cannot be undone.`}
        confirmText="Delete Template"
        onConfirm={handleDelete}
        requireInput={deletingTemplate?.name}
        variant="destructive"
        isPending={deleteMutation.isPending}
      />
      
      {/* Template Configuration Dialog */}
      {configuringTemplate && (
        <TemplateConfigurationDialog
          template={configuringTemplate}
          open={configuringTemplate !== null}
          onOpenChange={(open) => !open && setConfiguringTemplate(null)}
          existingConfig={getTemplateConfig(configuringTemplate.id)}
        />
      )}
    </div>
  );
}


// User form schema
const userSchema = z.object({
  username: z.string().min(1, "Username is required").regex(/^[a-zA-Z0-9_]+$/, "Username can only contain letters, numbers, and underscores"),
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email address").optional().or(z.literal("")),
  job_title: z.string().optional(),
  role: z.enum(["Developer", "PlatformManager", "OrgManager", "OrgAdmin", "HomeManager", "HomeAdmin", "HomeStaff"]),
  password: z.string().optional().or(z.literal("")).refine(val => !val || val.length >= 6, "Password must be at least 6 characters"),
  organization_id: z.number().nullable(),
  home_id: z.number().nullable(),
  is_superadmin: z.boolean().default(false),
});

type UserFormData = z.infer<typeof userSchema>;

// Enhanced Admin Users Page
function AdminUsersPage() {
  const { session, startImpersonation, isLoading: sessionLoading } = useSession();
  const user = session?.user;
  const { canWrite, showWriteBlockedMessage } = useWriteGuard();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const breadcrumbs = useBreadcrumbs();
  
  // ALL HOOKS MUST BE CALLED FIRST BEFORE ANY CONDITIONAL RETURNS
  const [impersonatingUserId, setImpersonatingUserId] = useState<number | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  // Initialize filters from URL query params (comma-separated values)
  const urlParamsInit = new URLSearchParams(window.location.search);
  const initialRole = parseArrayParam(urlParamsInit.get('roles'));
  const initialOrgs = parseArrayParam(urlParamsInit.get('orgs'));
  const initialHomes = parseArrayParam(urlParamsInit.get('homes'));
  const initialJobTitles = parseArrayParam(urlParamsInit.get('jobTitles'));
  const initialStatus = parseArrayParam(urlParamsInit.get('status'));

  const [roleFilter, setRoleFilter] = useState<string[]>(initialRole);
  const [orgFilter, setOrgFilter] = useState<string[]>(initialOrgs);
  const [homeFilter, setHomeFilter] = useState<string[]>(initialHomes);
  const [jobTitleFilter, setJobTitleFilter] = useState<string[]>(initialJobTitles);
  const [statusFilter, setStatusFilter] = useState<string[]>(initialStatus);

  // Persist filters to URL when they change (debounced)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const setOrDelete = (key: string, value: string | null) => {
      if (value === null) params.delete(key);
      else params.set(key, value);
    };
    setOrDelete('roles', serializeArrayParam(roleFilter));
    setOrDelete('orgs', serializeArrayParam(orgFilter));
    setOrDelete('homes', serializeArrayParam(homeFilter));
    setOrDelete('jobTitles', serializeArrayParam(jobTitleFilter));
    setOrDelete('status', serializeArrayParam(statusFilter));
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
  }, [roleFilter, orgFilter, homeFilter, jobTitleFilter, statusFilter]);
  
  // Permission: OrgAdmin, HomeAdmin, OrgManager, HomeManager, and platform roles can archive users
  const canArchiveUsers = user?.role && ['Developer', 'PlatformManager', 'OrgManager', 'OrgAdmin', 'HomeManager', 'HomeAdmin'].includes(user.role);

  // Queries - use default queryFn which handles normalization
  const { data: usersResp, isLoading } = useQuery<User[]>({ 
    queryKey: ["/api/users"]
  });
  
  const { data: organizationsResp } = useQuery<Organization[]>({ 
    queryKey: ["/api/organizations"]
  });
  
  // Fetch homes; scope to user's organization for Org-level roles to avoid cross-org leakage
  const { data: homesResp } = useQuery<Home[]>({ 
    queryKey: ["/api/homes", user?.role, user?.organization_id],
    queryFn: () => {
      // Org-level users should only see homes in their org
      if (user?.role && ['OrgManager', 'OrgAdmin'].includes(user.role) && user.organization_id) {
        return apiRequest("GET", `/api/homes?organizationId=${user.organization_id}`).then(res => res.json());
      }
      // Developers and PlatformManager see all homes
      return apiRequest("GET", "/api/homes").then(res => res.json());
    }
  });
  
  // Normalize responses (default queryFn already handles {total, items} format)
  const users = usersResp || [];
  // Hide Developer users from non-developer sessions (defensive UI layer)
  const normalizedCurrentRole = (user?.role || '').toLowerCase().replace(/_/g, '');
  const visibleUsers = normalizedCurrentRole === 'developer' ? users : users.filter(u => (u.role || '').toLowerCase().replace(/_/g, '') !== 'developer');
  const organizations = organizationsResp || [];
  const homes = homesResp || [];

  // Clear tampered URL home filter for org-level users: if homeFilter is set to a home
  // that isn't in the returned homes for this org, reset and notify the user.
  useEffect(() => {
    if (!user) return;
    if (!['OrgManager', 'OrgAdmin'].includes(user.role)) return;
    if (!homes || homes.length === 0) return;
  if (!homeFilter || homeFilter.length === 0 || homeFilter.includes('all')) return;
  const parsed = parseInt(String(homeFilter[0]), 10);
    if (Number.isNaN(parsed)) return;
    const found = homes.find((h: Home) => h.id === parsed);
    if (!found) {
  setHomeFilter(['all']);
      toast({
        title: "Home not in your organisation",
        description: "The selected home is not part of your organisation. It has been cleared.",
        variant: 'destructive'
      });
    }
  }, [homes, user, homeFilter]);

  // Form handling
  const form = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      username: "",
      first_name: "",
      last_name: "",
      email: "",
      job_title: "",
      role: "HomeStaff",
      password: "",
      organization_id: null,
      home_id: null,
      is_superadmin: false,
    },
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: UserFormData) => 
      apiRequest("POST", "/api/users", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/users"] });
      setIsCreateModalOpen(false);
      form.reset();
      toast({
        title: "Success",
        description: "User created successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create user",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UserFormData }) => {
      // Remove password if not provided for updates
      const { password, ...updateData } = data;
      const payload = password ? data : updateData;
      return apiRequest("PUT", `/api/users/${id}`, payload);
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ["/api/users"] });
      await queryClient.refetchQueries({ queryKey: ["/api/organizations"] });
      await queryClient.refetchQueries({ queryKey: ["/api/homes"] });
      setEditingUser(null);
      form.reset();
      toast({
        title: "Success",
        description: "User updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update user",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/users/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/users"] });
      setDeletingUser(null);
      toast({
        title: "Success",
        description: "User deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete user",
        variant: "destructive",
      });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: ({ id, archive }: { id: number; archive: boolean }) => 
      apiRequest("PUT", `/api/users/${id}`, { status: archive ? 'Archived' : 'Active' }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/users"] });
      toast({
        title: "Success",
        description: variables.archive ? "User archived successfully" : "User unarchived successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update user status",
        variant: "destructive",
      });
    },
  });

  // Handler functions
  const handleSubmit = (data: UserFormData) => {
    if (editingUser) {
      updateMutation.mutate({ id: editingUser.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (userToEdit: User) => {
    setEditingUser(userToEdit);
    form.reset({
      username: userToEdit.username,
      first_name: userToEdit.first_name,
      last_name: userToEdit.last_name,
      email: userToEdit.email || "",
      job_title: userToEdit.job_title || "",
      role: userToEdit.role as "Developer" | "PlatformManager" | "OrgManager" | "OrgAdmin" | "HomeManager" | "HomeAdmin" | "HomeStaff",
      password: "", // Don't populate password for edits
      organization_id: userToEdit.organization_id || null,
      home_id: userToEdit.home_id || null,
      is_superadmin: userToEdit.is_superadmin,
    });
  };

  const handleDelete = () => {
    if (deletingUser) {
      deleteMutation.mutate(deletingUser.id);
    }
  };

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
    setEditingUser(null);
    form.reset();
  };

  // Get unique job titles for filter
  const jobTitles = Array.from(new Set(users.map((u: User) => u.job_title).filter(Boolean))) as string[];

  // Filtered data (use visibleUsers so Developer entries are hidden for non-developers)
  const filteredUsers = visibleUsers.filter((u: User) => {
    const matchesSearch = u.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         u.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         u.username.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter.length === 0 || roleFilter.includes(u.role);
    const matchesOrg = orgFilter.length === 0 || orgFilter.includes(u.organization_id?.toString() || '');
    const matchesHome = homeFilter.length === 0 || homeFilter.includes(u.home_id?.toString() || '');
    const matchesJobTitle = jobTitleFilter.length === 0 || jobTitleFilter.includes(u.job_title || '');
    const matchesStatus = statusFilter.length === 0 || statusFilter.includes(u.status || '');
    
    return matchesSearch && matchesRole && matchesOrg && matchesHome && matchesJobTitle && matchesStatus;
  });

  // Helper functions
  const getOrganizationName = (orgId: number | null | undefined) => {
    if (!orgId) return "None";
    const org = organizations.find((o: Organization) => o.id === orgId);
    return org?.name || "Unknown";
  };

  const getHomeName = (homeId: number | null | undefined) => {
    if (!homeId) return "None";
    const home = homes.find((h: Home) => h.id === homeId);
    return home?.name || "Unknown";
  };

  // Role options: only Developers see the Developer option
  const roles = normalizedCurrentRole === 'developer' ? ["Developer", "PlatformManager", "OrgManager", "OrgAdmin", "HomeManager", "HomeAdmin", "HomeStaff"] : ["PlatformManager", "OrgManager", "OrgAdmin", "HomeManager", "HomeAdmin", "HomeStaff"];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="page-title text-[26px] font-bold">User Management</h1>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-gray-200 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ResponsiveBreadcrumb items={breadcrumbs.users()} className="mb-4" />
      <div className="flex items-center justify-between">
  <h1 className="page-title text-[26px] font-bold text-foreground" data-testid="page-title">
          User Management
        </h1>
        <Button 
          onClick={() => !canWrite ? toast(showWriteBlockedMessage()) : setIsCreateModalOpen(true)}
          disabled={!canWrite}
          data-testid="button-create-user"
          title={!canWrite ? "Create disabled during impersonation" : "Create new user"}
        >
          <Plus className="w-4 h-4 mr-2" />
          Create User
        </Button>
      </div>
      {/* Search and Filter */}
      <div className="flex gap-4 p-4 bg-card rounded-lg border flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <Input
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="search-users"
          />
        </div>

        {/* Status filter - available to all roles */}
        <div className="w-48">
          <MultiSelectFilter
            options={['Active', 'Pending', 'Suspended', 'Archived'].map(s => ({ value: s, label: s }))}
            selectedValues={statusFilter}
            onSelectionChange={setStatusFilter}
            placeholder="All Statuses"
            searchPlaceholder="Search status..."
            testId="filter-status"
          />
        </div>
        
        {/* Developer/PlatformManager: org, home, and role filters */}
        {['Developer', 'PlatformManager'].includes(user?.role || '') && (
          <>
            <div className="w-48">
              <MultiSelectFilter
                options={organizations.map((org: Organization) => ({ value: org.id.toString(), label: org.name }))}
                selectedValues={orgFilter}
                onSelectionChange={setOrgFilter}
                placeholder="All Organizations"
                searchPlaceholder="Search organizations..."
                testId="filter-org"
              />
            </div>
            <div className="w-48">
              <MultiSelectFilter
                options={homes.map((home: Home) => ({ value: home.id.toString(), label: home.name }))}
                selectedValues={homeFilter}
                onSelectionChange={setHomeFilter}
                placeholder="All Homes"
                searchPlaceholder="Search homes..."
                testId="filter-home"
              />
            </div>
            <div className="w-48">
              <MultiSelectFilter
                options={roles.map(role => ({ value: role, label: role }))}
                selectedValues={roleFilter}
                onSelectionChange={setRoleFilter}
                placeholder="All Roles"
                searchPlaceholder="Search roles..."
                testId="filter-role"
              />
            </div>
          </>
        )}
        
        {/* OrgManager: home filter */}
        {user?.role === 'OrgManager' && (
          <div className="w-48">
            <MultiSelectFilter
              options={homes.map((home: Home) => ({ value: home.id.toString(), label: home.name }))}
              selectedValues={homeFilter}
              onSelectionChange={setHomeFilter}
              placeholder="All Homes"
              searchPlaceholder="Search homes..."
              testId="filter-home"
            />
          </div>
        )}
        
        {/* HomeManager: job title filter */}
        {user?.role === 'HomeManager' && jobTitles.length > 0 && (
          <div className="w-48">
            <MultiSelectFilter
              options={jobTitles.map(title => ({ value: title, label: title }))}
              selectedValues={jobTitleFilter}
              onSelectionChange={setJobTitleFilter}
              placeholder="All Job Titles"
              searchPlaceholder="Search job titles..."
              testId="filter-job-title"
            />
          </div>
        )}
      </div>
      {/* Users Table */}
      {filteredUsers.length === 0 ? (
        <Card className="p-8">
          <div className="text-center space-y-4" data-testid="empty-state">
            <Users className="w-16 h-16 text-gray-400 mx-auto" />
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm || roleFilter.length > 0 ? "No users found" : "No users yet"}
              </h3>
              <p className="text-gray-600">
                {searchTerm || roleFilter.length > 0
                  ? "Try adjusting your search or filter criteria."
                  : "Create your first user to get started."}
              </p>
            </div>
            {!(searchTerm || roleFilter.length > 0) && (
              <Button 
                onClick={() => !canWrite ? toast(showWriteBlockedMessage()) : setIsCreateModalOpen(true)}
                disabled={!canWrite}
                data-testid="create-first-user"
                title={!canWrite ? "Create disabled during impersonation" : "Create first user"}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create First User
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Username</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Organization</TableHead>
                <TableHead>Home</TableHead>
                <TableHead className="w-[180px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((u: User) => (
                <TableRow key={u.id} data-testid={`row-user-${u.id}`}>
                  <TableCell>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{u.first_name} {u.last_name}</span>
                        {u.status === 'Archived' && (
                          <Badge variant="secondary" className="text-xs">Archived</Badge>
                        )}
                        {u.status === 'Suspended' && (
                          <Badge variant="destructive" className="text-xs">Suspended</Badge>
                        )}
                        {u.status === 'Pending' && (
                          <Badge variant="outline" className="text-xs">Pending</Badge>
                        )}
                      </div>
                      {u.job_title && (
                        <div className="text-sm text-gray-500">{u.job_title}</div>
                      )}
                      {u.is_superadmin && (
                        <Badge className="mt-1 text-xs">Superadmin</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-sm">@{u.username}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{u.role}</Badge>
                      {/* Show "View Only" badge if user cannot manage this user due to role hierarchy */}
                      {user?.role && u.role && !canManageUserRole(user.role as Role, u.role as Role) && (
                        <Badge variant="secondary" className="text-xs">View Only</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell data-testid={`org-${u.id}`}>
                    {getOrganizationName(u.organization_id)}
                  </TableCell>
                  <TableCell data-testid={`home-${u.id}`}>
                    {getHomeName(u.home_id)}
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-1">
                      {/* View As Button - Only for Developer role, not when impersonating, not for other developers */}
                      {session?.user?.role?.toLowerCase().replace(/_/g, '') === 'developer' && !session?.isImpersonating && u.role?.toLowerCase().replace(/_/g, '') !== 'developer' && u.id !== session?.user?.id && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={async () => {
                            try {
                              setImpersonatingUserId(u.id);
                              await startImpersonation(u.id);
                            } catch (error) {
                              // Error already handled in startImpersonation
                            } finally {
                              setImpersonatingUserId(null);
                            }
                          }}
                          disabled={impersonatingUserId === u.id || sessionLoading}
                          data-testid={`button-view-as-${u.id}`}
                          title={`View system as ${u.first_name} ${u.last_name}`}
                        >
                          {impersonatingUserId === u.id ? (
                            <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                      
                      {/* Edit button: only show if user can manage target based on scope AND role hierarchy.
                          Developer users are allowed to edit their own row (restore previous behavior)
                          so they can update fields like email from the management UI. Other roles may not edit self here. */}
                      {(
                        // Allow Developer to edit own row
                        (user?.role === 'Developer' && u.id === user?.id)
                        // Otherwise follow existing rule: not self and within scope and lower role
                        || (
                          user?.role && u.id !== user?.id &&
                          canEditInScope({ role: user?.role as any, organizationId: user?.organization_id, homeId: user?.home_id }, u.organization_id ?? null, u.home_id ?? null) &&
                          u.role && canManageUserRole(user.role as Role, u.role as Role)
                        )
                      ) ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => !canWrite ? toast(showWriteBlockedMessage()) : handleEdit(u)}
                          disabled={!canWrite}
                          data-testid={`button-edit-${u.id}`}
                          title={!canWrite ? "Edit disabled during impersonation" : "Edit user"}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      ) : null}
                      {u.id !== user?.id && (
                        <>
                          {/* Archive button: only show if user can manage target based on role hierarchy */}
                          {canArchiveUsers && user?.role && u.role && canManageUserRole(user.role as Role, u.role as Role) && (
                            <Button
                              variant={u.status === 'Archived' ? 'default' : 'secondary'}
                              size="sm"
                              onClick={() => !canWrite ? toast(showWriteBlockedMessage()) : archiveMutation.mutate({ id: u.id, archive: u.status !== 'Archived' })}
                              disabled={!canWrite || archiveMutation.isPending}
                              data-testid={`button-archive-${u.id}`}
                              title={!canWrite ? "Archive disabled during impersonation" : (u.status === 'Archived' ? "Unarchive user" : "Archive user")}
                            >
                              {u.status === 'Archived' ? 'Unarchive' : 'Archive'}
                            </Button>
                          )}

                          {/* Delete button: only for Developer/PlatformManager, or OrgManager within same org */}
                          {ROLE_PERMISSIONS.canDeleteUsers(user?.role as any) && (user?.role === 'Developer' || user?.role === 'PlatformManager' || (user?.role === 'OrgManager' && user?.organization_id && u.organization_id === user.organization_id)) && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => !canWrite ? toast(showWriteBlockedMessage()) : setDeletingUser(u)}
                              disabled={!canWrite}
                              data-testid={`button-delete-${u.id}`}
                              title={!canWrite ? "Delete disabled during impersonation" : "Delete user"}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      {/* Create/Edit Modal */}
      <Dialog open={isCreateModalOpen || editingUser !== null} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="user-modal">
          <DialogHeader>
            <DialogTitle>
              {editingUser ? "Edit User" : "Create User"}
            </DialogTitle>
            <DialogDescription>
              {editingUser 
                ? "Update the user details below."
                : "Enter the details for the new user."}
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto space-y-4 pr-2">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="first_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="John" 
                          {...field} 
                          data-testid="input-first-name"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="last_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Doe" 
                          {...field} 
                          data-testid="input-last-name"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                </div>

                <FormField
                  control={form.control}
                  name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Username</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="johndoe" 
                        {...field} 
                        data-testid="input-username"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                />

                <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email (Optional - Required for password reset)</FormLabel>
                    <FormControl>
                      <Input 
                        type="email"
                        placeholder="john.doe@example.com" 
                        {...field} 
                        data-testid="input-email"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                />

                <FormField
                control={form.control}
                name="job_title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Job Title (Optional)</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="Care Assistant" 
                        {...field} 
                        data-testid="input-job-title"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                />

                <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Role</FormLabel>
                    <FormControl>
                      {/* Role options: only allow assigning roles strictly lower than the requester's role.
                          Keep the current/selected role visible so the form shows the user's existing role,
                          but disable any roles the requester may not assign. */}
                      <select
                        {...field}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                        data-testid="select-role"
                      >
                        {
                          // Ordered from lowest to highest privilege. Only include roles the session user
                          // may assign, or the currently selected role so the form reflects existing value.
                          (['HomeStaff','HomeAdmin','HomeManager','OrgAdmin','OrgManager','PlatformManager','Developer'] as Role[])
                            .filter((r) => {
                              const isCurrent = field.value === r;
                              if (!session || !session.user) return isCurrent;
                              const canAssign = canManageUserRole(session.user.role as Role, r);
                              return canAssign || isCurrent;
                            })
                            .map((r) => (
                              <option key={r} value={r}>{r}</option>
                            ))
                        }
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                />

                <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Password {editingUser && "(Leave blank to keep current)"}
                    </FormLabel>
                    <FormControl>
                      <Input 
                        type="password"
                        placeholder={editingUser ? "Leave blank to keep current" : "Enter password"}
                        {...field} 
                        data-testid="input-password"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {(user?.role === 'Developer' || user?.is_superadmin) && (
                <div className="space-y-4 border-t pt-4">
                  <h4 className="font-medium">Organization & Home Assignment</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="organization_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Organization</FormLabel>
                          <FormControl>
                            <select
                              value={field.value?.toString() ?? "null"}
                              onChange={(e) => field.onChange(e.target.value === "null" ? null : parseInt(e.target.value))}
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                              data-testid="select-organization"
                            >
                              <option value="null">None (Standalone)</option>
                              {organizations.length > 0 ? (
                                organizations
                                  .filter((org: Organization) => org.status === 'Active')
                                  .map((org: Organization) => (
                                    <option key={org.id} value={org.id.toString()}>
                                      {org.name}
                                    </option>
                                  ))
                              ) : (
                                <option disabled>Loading organizations...</option>
                              )}
                            </select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="home_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Home</FormLabel>
                          <FormControl>
                            <select
                              value={field.value?.toString() ?? "null"}
                              onChange={(e) => field.onChange(e.target.value === "null" ? null : parseInt(e.target.value))}
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                              data-testid="select-home"
                            >
                              <option value="null">None</option>
                              {homes.length > 0 ? (
                                homes
                                  .filter((home: Home) => home.is_active)
                                  .map((home: Home) => (
                                    <option key={home.id} value={home.id.toString()}>
                                      {home.name}
                                    </option>
                                  ))
                              ) : (
                                <option disabled>Loading homes...</option>
                              )}
                            </select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="is_superadmin"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                        <FormControl>
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={field.onChange}
                            className="w-4 h-4"
                            data-testid="checkbox-superadmin"
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>Superadmin</FormLabel>
                          <p className="text-sm text-muted-foreground">
                            Grants full system access across all organizations
                          </p>
                        </div>
                      </FormItem>
                    )}
                  />
                </div>
              )}
              </div>

              <DialogFooter className="flex-shrink-0">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseModal}
                  data-testid="button-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  data-testid="button-save"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? "Saving..."
                    : editingUser 
                    ? "Update User"
                    : "Create User"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deletingUser !== null}
        onOpenChange={(open) => !open && setDeletingUser(null)}
        title="Delete User"
        subtitle={`Are you sure you want to delete "${deletingUser?.first_name} ${deletingUser?.last_name}"? This action cannot be undone.`}
        confirmText="Delete User"
        onConfirm={handleDelete}
        requireInput={deletingUser?.username}
        variant="destructive"
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}

// Feature flag for one-click audit
const FEATURE_ONE_CLICK_AUDIT = true;

// Audit Taking Page - Vertical Scrollable View
function TakeAuditPage() {
  const { session } = useSession();
  const user = session?.user;
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  // Get auditId from URL
  const urlParams = new URLSearchParams(window.location.search);
  const auditIdParam = urlParams.get('auditId');
  
  // State
  const [responses, setResponses] = useState<Record<string, any>>({});
  const [auditName, setAuditName] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("medium");
  const [saveStatus, setSaveStatus] = useState<"saved" | "saving" | "unsaved">("saved");
  const [showSettings, setShowSettings] = useState(false);
  const [showOutline, setShowOutline] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [submittedScore, setSubmittedScore] = useState<number | null>(null);
  const [showScoreDialog, setShowScoreDialog] = useState(false);
  const [lockInfo, setLockInfo] = useState<{ locked_by?: number; locked_by_name?: string; locked_until?: string | Date } | null>(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const isManager = user?.role && ['OrgManager','HomeManager','OrgAdmin','HomeAdmin','Developer','PlatformManager'].includes(user.role);
  const [showAssigneeModal, setShowAssigneeModal] = useState(false);
  const lockHeartbeatRef = useRef<number | null>(null);
  
  // Refs for smooth scrolling
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  
  // Fetch audit
  const { data: audit, isLoading: auditLoading } = useQuery({
    queryKey: ["/api/audits", auditIdParam],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/audits/${auditIdParam}`);
      return response.json();
    },
    enabled: !!auditIdParam
  });
  
  // Fetch template from audit
  const { data: template, isLoading: templateLoading } = useQuery({
    queryKey: ["/api/templates", audit?.template_id],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/templates/${audit.template_id}`);
      return response.json();
    },
    enabled: !!audit?.template_id
  });
  
  // Load audit data when available
  useEffect(() => {
    if (audit && !auditName) {
      setAuditName(audit.name);
      setDescription(audit.description || "");
      setPriority(audit.priority || "medium");
      setResponses(audit.responses_json || {});
    }
  }, [audit]);

  // Acquire lock on mount and set heartbeat; release on unmount
  useEffect(() => {
    let cancelled = false;
    const acquire = async () => {
      if (!auditIdParam) return;
      try {
        const res = await apiRequest('POST', `/api/audits/${auditIdParam}/lock`, { ttlSeconds: 120 });
        const data = await res.json();
        // If lock held by me, clear read-only
        if (data.locked_by === user?.id) {
          setLockInfo(null);
          setIsReadOnly(false);
        } else if (data.code === 'ASSIGNEE_ONLY' || res.status === 403) {
          setIsReadOnly(true);
          setLockInfo(null);
          setShowAssigneeModal(true);
        } else if (data.code === 'AUDIT_LOCKED' || res.status === 423) {
          setLockInfo({ locked_by: data.locked_by, locked_by_name: data.locked_by_name, locked_until: data.locked_until });
          setIsReadOnly(true);
        }
      } catch (e: any) {
        // If server returns 423 via thrown error string, try to parse
        const msg = String(e?.message || '');
        const m = msg.match(/\{.*\}$/);
        if (m) {
          try {
            const json = JSON.parse(m[0]);
            if (json.code === 'ASSIGNEE_ONLY') {
              setIsReadOnly(true);
              setLockInfo(null);
              setShowAssigneeModal(true);
              return;
            }
            if (json.code === 'AUDIT_LOCKED') {
              setLockInfo({ locked_by: json.locked_by, locked_by_name: json.locked_by_name, locked_until: json.locked_until });
              setIsReadOnly(true);
            }
          } catch {}
        }
      }
    };
    const refresh = async () => {
      if (!auditIdParam) return;
      try {
        const res = await apiRequest('POST', `/api/audits/${auditIdParam}/lock/refresh`, { ttlSeconds: 120 });
        const data = await res.json();
        if (data.locked_by !== user?.id) {
          setLockInfo({ locked_by: data.locked_by, locked_by_name: data.locked_by_name, locked_until: data.locked_until });
          setIsReadOnly(true);
        } else {
          setLockInfo(null);
          setIsReadOnly(false);
        }
      } catch (e) {
        // silently ignore heartbeat errors
      }
    };
    acquire();
    // Heartbeat every 60s
    const id = window.setInterval(refresh, 60000);
    lockHeartbeatRef.current = id;
    return () => {
      if (lockHeartbeatRef.current) window.clearInterval(lockHeartbeatRef.current);
      lockHeartbeatRef.current = null;
      if (auditIdParam) {
        apiRequest('DELETE', `/api/audits/${auditIdParam}/lock`).catch(() => {});
      }
    };
  }, [auditIdParam, user?.id]);
  
  // Parse template content
  const items = template?.content?.items || [];
  const answerableItems = items.filter((item: any) => 
    item.type !== 'section'
  );
  
  // Calculate progress based on filled responses
  const filledCount = answerableItems.filter((item: any) => {
    const response = responses[item.id];
    if (response === null || response === undefined || response === '') return false;
    if (Array.isArray(response) && response.length === 0) return false;
    
    // For any question with actions enabled, check if answer is provided (response is an object)
    if (item.actions_enabled && typeof response === 'object' && !Array.isArray(response)) {
      return !!response.answer && response.answer !== '';
    }
    
    // For ynna_actions_review (legacy check, but keeping for backwards compatibility)
    if (item.type === 'ynna_actions_review' && response.answer) return true;
    if (item.type === 'ynna_actions_review' && !response.answer) return false;
    
    return true;
  }).length;
  
  const progressPercent = answerableItems.length > 0
    ? (filledCount / answerableItems.length) * 100
    : 0;
  const progress = answerableItems.length > 0 
    ? `${filledCount} / ${answerableItems.length}` 
    : "0 / 0";
  
  // Save audit mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      setSaveStatus("saving");
      
      if (!auditIdParam) {
        throw new Error("No audit ID provided");
      }
      
      const response = await apiRequest("PATCH", `/api/audits/${auditIdParam}` , {
        responses_json: responses,
        name: auditName,
        description: description || undefined,
        priority
      });
      return response.json();
    },
    onSuccess: () => {
      setSaveStatus("saved");
      setHasUnsavedChanges(false);
      queryClient.invalidateQueries({ queryKey: ["/api/audits"] });
    },
    onError: (error: any) => {
      setSaveStatus("unsaved");
      const msg = String(error?.message || '');
      const m = msg.match(/\{.*\}$/);
      if (m) {
        try {
          const json = JSON.parse(m[0]);
          if (json.code === 'AUDIT_LOCKED') {
            setIsReadOnly(true);
            setLockInfo({ locked_by: json.locked_by, locked_by_name: json.locked_by_name, locked_until: json.locked_until });
            toast({ title: 'Read-only mode', description: `This audit is being edited by ${json.locked_by_name || 'another user'}. You can view but not edit.`, variant: 'destructive' });
            return;
          }
        } catch {}
      }
      toast({ title: 'Save Failed', description: error.message || 'Failed to save audit', variant: 'destructive' });
    }
  });
  
  // Submit audit for review mutation
  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!auditIdParam) {
        throw new Error("No audit ID provided");
      }
      // If user added Areas of Good Practice, post them before submit
      try {
        const gp = (responses as any)._good_practice;
        if (gp && (gp.is_none || (gp.description && gp.description.trim()))) {
          await apiRequest("POST", `/api/audits/${auditIdParam}/good-practices`, {
            description: gp.description || '',
            is_none: !!gp.is_none,
          });
        }
      } catch (e) {
        // non-blocking
      }

      const response = await apiRequest("PATCH", `/api/audits/${auditIdParam}`, {
        responses_json: responses,
        name: auditName,
        description: description || undefined,
        priority,
        status: 'Submitted'
      });
      return response.json();
    },
    onSuccess: (submittedAudit: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/audits"] });
      
      // If audit has a score, show score dialog
      if (submittedAudit.score !== null && submittedAudit.score !== undefined) {
        setSubmittedScore(submittedAudit.score);
        setShowScoreDialog(true);
      } else {
        // No score - just show toast and redirect
        toast({
          title: "Audit Submitted",
          description: "Your audit has been submitted for review",
        });
        setLocation('/audits');
      }
    },
    onError: (error: any) => {
      const msg = String(error?.message || '');
      const m = msg.match(/\{.*\}$/);
      if (m) {
        try {
          const json = JSON.parse(m[0]);
          if (json.code === 'AUDIT_LOCKED') {
            setIsReadOnly(true);
            setLockInfo({ locked_by: json.locked_by, locked_by_name: json.locked_by_name, locked_until: json.locked_until });
            toast({ title: 'Submit Blocked', description: `Another user is editing this audit (${json.locked_by_name || 'User'}). Try again later.`, variant: 'destructive' });
            return;
          }
        } catch {}
      }
      toast({ title: 'Submit Failed', description: error.message || 'Failed to submit audit', variant: 'destructive' });
    }
  });
  
  // Handle response change
  const handleResponseChange = (itemId: string, value: any) => {
    if (isReadOnly) return; // Block edits when locked
    setResponses(prev => ({ ...prev, [itemId]: value }));
    setSaveStatus("unsaved");
    setHasUnsavedChanges(true);
  };
  
  // Scroll to section
  const scrollToSection = (itemId: string) => {
    const element = sectionRefs.current[itemId];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };
  
  // Warn on navigation with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);
  
  if (!auditIdParam) {
    return (
      <div className="p-6">
        <Card className="p-8">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Audit Selected</h3>
            <p className="text-gray-600 mb-4">Please create an audit to begin.</p>
            <Button onClick={() => setLocation('/audits/new')}>Create Audit</Button>
          </div>
        </Card>
      </div>
    );
  }

  // Render read-only banner if locked by others
  const ReadOnlyBanner = () => !isReadOnly ? null : (
    <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900">
      <div className="text-sm">
        This audit is currently being edited by {lockInfo?.locked_by_name || 'another user'}. You are in read-only mode.
      </div>
      {isManager && (
        <div className="mt-2">
          <Button
            variant="destructive"
            size="sm"
            onClick={async () => {
              if (!auditIdParam) return;
              try {
                const res = await apiRequest('POST', `/api/audits/${auditIdParam}/lock/force`, { ttlSeconds: 120 });
                if (res.ok) {
                  const data = await res.json();
                  setIsReadOnly(false);
                  setLockInfo(null);
                  toast({ title: 'Taken over', description: 'You have taken over this audit and can continue editing.' });
                } else {
                  const msg = await res.text();
                  toast({ title: 'Takeover failed', description: msg || 'Unable to take over lock', variant: 'destructive' });
                }
              } catch (e: any) {
                toast({ title: 'Takeover failed', description: e?.message || 'Unable to take over lock', variant: 'destructive' });
              }
            }}
          >
            Force Take Over
          </Button>
        </div>
      )}
    </div>
  );
  
  if (auditLoading || templateLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-16 bg-gray-200 rounded"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }
  
  if (!audit || !template) {
    return (
      <div className="p-6">
        <Card className="p-8">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Audit Not Found</h3>
            <p className="text-gray-600 mb-4">The requested audit could not be loaded.</p>
            <Button onClick={() => setLocation('/audits')}>Go Back</Button>
          </div>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-white">
      {showAssigneeModal && (
        <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowAssigneeModal(false)} />
          <div className="relative z-10 w-full max-w-md rounded-lg border bg-card p-6 shadow-lg">
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-amber-500 mt-[2px]" />
              <div>
                <h3 className="text-lg font-semibold">This audit has been assigned to another user.</h3>
                <p className="text-sm text-muted-foreground mt-1">You can view it in read-only, notify the assignee, or take it over.</p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAssigneeModal(false)}>View only</Button>
              {isManager && (
                <Button
                  variant="outline"
                  onClick={async () => {
                    if (!auditIdParam) return;
                    try {
                      const res = await apiRequest('POST', `/api/audits/${auditIdParam}/reminder`);
                      if (res.ok) {
                        toast({ title: 'Reminder sent', description: 'The assignee has been notified.' });
                      } else {
                        const msg = await res.text();
                        toast({ title: 'Reminder failed', description: msg || 'Unable to send reminder', variant: 'destructive' });
                      }
                    } catch (e: any) {
                      toast({ title: 'Reminder failed', description: e?.message || 'Unable to send reminder', variant: 'destructive' });
                    }
                  }}
                  title="Send a reminder to the assigned user"
                >
                  <Bell className="w-4 h-4 mr-2" /> Notify
                </Button>
              )}
              {isManager && (
                <Button
                  variant="destructive"
                  onClick={async () => {
                    if (!auditIdParam) return;
                    try {
                      const res = await apiRequest('POST', `/api/audits/${auditIdParam}/lock/force`, { ttlSeconds: 120 });
                      if (res.ok) {
                        await res.json();
                        setIsReadOnly(false);
                        setLockInfo(null);
                        setShowAssigneeModal(false);
                        toast({ title: 'Taken over', description: 'You have taken over this audit and can continue editing.' });
                      } else {
                        const msg = await res.text();
                        toast({ title: 'Takeover failed', description: msg || 'Unable to take over', variant: 'destructive' });
                      }
                    } catch (e: any) {
                      toast({ title: 'Takeover failed', description: e?.message || 'Unable to take over', variant: 'destructive' });
                    }
                  }}
                >
                  Take Over
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            {/* Breadcrumb */}
            <div className="flex items-center space-x-2 text-sm">
              <Link href="/audits" className="text-gray-500 hover:text-[hsl(var(--brand-primary))] transition">
                Audits
              </Link>
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <span className="text-gray-900 font-semibold">{template.name}</span>
            </div>
            
            {/* Save Status & Settings */}
            <div className="flex items-center space-x-3">
              <span className={`px-3 py-1.5 rounded-md text-xs font-medium ${
                saveStatus === 'saved' ? 'bg-green-100 text-green-700' :
                saveStatus === 'saving' ? 'bg-blue-100 text-blue-700' :
                'bg-amber-100 text-amber-700'
              }`}>
                {saveStatus === 'saved' ? '✓ Saved' :
                 saveStatus === 'saving' ? 'Saving...' :
                 'Unsaved changes'}
              </span>
              
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="text-sm text-gray-600 hover:text-gray-900 flex items-center"
                data-testid="button-toggle-settings"
              >
                Settings {showSettings ? '▴' : '▾'}
              </button>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span className="font-medium">Progress: {progress} completed</span>
              <span className="font-bold text-[hsl(var(--brand-primary))]">{Math.round(progressPercent)}%</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2.5">
              <div 
                className="bg-[hsl(var(--brand-primary))] h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              ></div>
            </div>
          </div>
          
          {/* Settings Dropdown */}
          {showSettings && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-3">
              <div>
                <Label className="text-xs font-medium">Audit Name</Label>
                <Input
                  value={auditName}
                  onChange={(e) => {
                    setAuditName(e.target.value);
                    setSaveStatus("unsaved");
                    setHasUnsavedChanges(true);
                  }}
                  className="mt-1 bg-gray-100 cursor-not-allowed"
                  data-testid="input-audit-name"
                  disabled={true}
                />
                <p className="text-xs text-gray-500 mt-1">Audit name cannot be changed</p>
              </div>
              <div>
                <Label className="text-xs font-medium">Description (Optional)</Label>
                <Input
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    setSaveStatus("unsaved");
                    setHasUnsavedChanges(true);
                  }}
                  className="mt-1 bg-gray-100 cursor-not-allowed"
                  placeholder="Add description..."
                  data-testid="input-audit-description"
                  disabled={true}
                />
                <p className="text-xs text-gray-500 mt-1">Description cannot be changed</p>
              </div>
              <div>
                <Label className="text-xs font-medium">Priority</Label>
                <select
                  value={priority}
                  onChange={(e) => {
                    setPriority(e.target.value);
                    setSaveStatus("unsaved");
                    setHasUnsavedChanges(true);
                  }}
                  className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="select-audit-priority"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Main Content - Vertical Scrollable Layout */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <ReadOnlyBanner />
        <div className="flex gap-8">
          {/* Sticky Outline Sidebar */}
          {showOutline && (
            <div className="hidden lg:block w-72 flex-shrink-0">
              <div className="sticky top-28 space-y-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-base text-gray-900">Quick Navigation</h3>
                  <button
                    onClick={() => setShowOutline(false)}
                    className="text-xs text-gray-500 hover:text-gray-900"
                    data-testid="button-hide-outline"
                  >
                    Hide
                  </button>
                </div>
                <div className="space-y-1.5 max-h-[calc(100vh-180px)] overflow-y-auto pr-2">
                  {answerableItems.map((item: any, idx: number) => {
                    const response = responses[item.id];
                    let isFilled = response !== undefined && response !== '' && 
                                   !(Array.isArray(response) && response.length === 0);
                    
                    // For any question with actions enabled, check if answer is provided
                    if (item.actions_enabled && typeof response === 'object' && !Array.isArray(response)) {
                      isFilled = !!response?.answer && response.answer !== '';
                    }
                    // For ynna_actions_review (legacy check)
                    else if (item.type === 'ynna_actions_review') {
                      isFilled = !!response?.answer;
                    }
                    return (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        className={`w-full text-left text-sm p-3 rounded-lg transition flex items-start gap-3 ${
                          isFilled 
                            ? 'bg-green-50 text-green-900 hover:bg-green-100 border border-green-200' 
                            : 'hover:bg-gray-50 text-gray-700 border border-transparent'
                        }`}
                        data-testid={`button-nav-section-${idx}`}
                      >
                        <span className="flex-shrink-0 text-sm font-semibold text-gray-500">{idx + 1}.</span>
                        <span className="line-clamp-2">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          
          {/* All Questions - Vertical List */}
          <div className="flex-1 min-w-0">
            {/* Show outline toggle for mobile */}
            {!showOutline && (
              <div className="mb-4">
                <Button
                  variant="outline"
                  onClick={() => setShowOutline(true)}
                  size="sm"
                  data-testid="button-show-outline"
                >
                  Show Navigation
                </Button>
              </div>
            )}
            
            <div className="space-y-4">
              {answerableItems.map((item: any, idx: number) => (
                <div 
                  key={item.id}
                  ref={(el) => { sectionRefs.current[item.id] = el; }}
                  className="scroll-mt-24 bg-white rounded-lg border border-gray-200 p-4"
                  data-testid={`card-question-${idx}`}
                >
                  {/* Question Header */}
                  <div className="mb-3">
                    <div className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--brand-primary))] text-white flex items-center justify-center text-sm font-semibold">
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-gray-900">{item.label}</h3>
                        {item.description && (
                          <p className="text-xs text-gray-500 mt-1">{item.description}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Response Input based on type */}
                  <div className="space-y-3">
                    {item.type === 'text' && (
                      <Input
                        value={typeof responses[item.id] === 'object' ? responses[item.id]?.answer || '' : responses[item.id] || ''}
                        onChange={(e) => {
                          if (item.actions_enabled) {
                            handleResponseChange(item.id, {
                              ...(typeof responses[item.id] === 'object' ? responses[item.id] : {}),
                              answer: e.target.value
                            });
                          } else {
                            handleResponseChange(item.id, e.target.value);
                          }
                        }}
                        placeholder="Enter your response..."
                        data-testid={`input-text-${idx}`}
                        disabled={isReadOnly}
                      />
                    )}
                    {item.type === 'textarea' && (
                      <textarea
                        value={responses[item.id] || ''}
                        onChange={(e) => handleResponseChange(item.id, e.target.value)}
                        className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--brand-primary))] focus-visible:ring-offset-2"
                        placeholder="Enter your response..."
                        data-testid={`input-textarea-${idx}`}
                        disabled={isReadOnly}
                      />
                    )}
                    {item.type === 'radio' && item.options && (
                      <div className="space-y-2">
                        {item.options.map((option: string) => (
                          <label key={option} className="flex items-center space-x-3 cursor-pointer p-2 rounded hover:bg-gray-50">
                            <input
                              type="radio"
                              name={item.id}
                              value={option}
                              checked={responses[item.id] === option}
                              onChange={(e) => handleResponseChange(item.id, e.target.value)}
                              className="w-4 h-4 text-[hsl(var(--brand-primary))] focus:ring-[hsl(var(--brand-primary))]"
                              data-testid={`input-radio-${idx}-${option}`}
                              disabled={isReadOnly}
                            />
                            <span className="text-sm">{option}</span>
                          </label>
                        ))}
                      </div>
                    )}
                    {item.type === 'checkbox' && item.options && (
                      <div className="space-y-2">
                        {item.options.map((option: string) => (
                          <label key={option} className="flex items-center space-x-3 cursor-pointer p-2 rounded hover:bg-gray-50">
                            <input
                              type="checkbox"
                              value={option}
                              checked={(responses[item.id] || []).includes(option)}
                              onChange={(e) => {
                                const currentValues = responses[item.id] || [];
                                const newValues = e.target.checked
                                  ? [...currentValues, option]
                                  : currentValues.filter((v: string) => v !== option);
                                handleResponseChange(item.id, newValues);
                              }}
                              className="w-4 h-4 text-[hsl(var(--brand-primary))] focus:ring-[hsl(var(--brand-primary))] rounded"
                              data-testid={`input-checkbox-${idx}-${option}`}
                              disabled={isReadOnly}
                            />
                            <span className="text-sm">{option}</span>
                          </label>
                        ))}
                      </div>
                    )}
                    {item.type === 'number' && (
                      <Input
                        type="number"
                        value={responses[item.id] || ''}
                        onChange={(e) => handleResponseChange(item.id, parseFloat(e.target.value) || '')}
                        placeholder="Enter number..."
                        data-testid={`input-number-${idx}`}
                        disabled={isReadOnly}
                      />
                    )}
                    {item.type === 'date' && (
                      <Input
                        type="date"
                        value={responses[item.id] || ''}
                        onChange={(e) => handleResponseChange(item.id, e.target.value)}
                        data-testid={`input-date-${idx}`}
                        disabled={isReadOnly}
                      />
                    )}
                    {item.type === 'ynna_actions_review' && (
                      <div className="space-y-3">
                        {/* Yes/No/N/A Buttons - Compact */}
                        <div className="flex flex-wrap gap-2">
                          {['Yes', 'No', 'N/A'].map((option) => {
                            const currentResponse = responses[item.id] || {};
                            const isSelected = currentResponse.answer === option;
                            return (
                              <button
                                key={option}
                                onClick={() => {
                                  handleResponseChange(item.id, {
                                    ...currentResponse,
                                    answer: option
                                  });
                                }}
                                className={`flex items-center gap-2 px-4 py-2 rounded-md border transition-all text-sm font-medium ${
                                  isSelected 
                                    ? 'bg-[hsl(var(--brand-primary))] border-[hsl(var(--brand-primary))] text-white' 
                                    : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                                }`}
                                data-testid={`button-response-${idx}-${option.toLowerCase()}`}
                                disabled={isReadOnly}
                              >
                                {option === 'Yes' && <CheckCircle className="w-4 h-4" />}
                                {option === 'No' && <XCircle className="w-4 h-4" />}
                                {option === 'N/A' && <Minus className="w-4 h-4" />}
                                <span>{option}</span>
                              </button>
                            );
                          })}
                        </div>
                        
                        {/* Actions Section - Only show if actions are enabled and an answer is selected */}
                        {item.actions_enabled && responses[item.id]?.answer && (
                          <div className="pt-3 border-t border-gray-200 mt-3">
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <div className="text-sm font-medium text-gray-900">Actions Required</div>
                                <div className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`no-action-${item.id}`}
                                    checked={responses[item.id]?.actions?.noActionNeeded || false}
                                    onCheckedChange={(checked) => {
                                      const currentResponse = responses[item.id] || {};
                                      handleResponseChange(item.id, {
                                        ...currentResponse,
                                        actions: {
                                          ...currentResponse.actions,
                                          noActionNeeded: !!checked
                                        }
                                      });
                                    }}
                                    data-testid={`checkbox-no-action-${idx}`}
                                    disabled={isReadOnly}
                                  />
                                  <label
                                    htmlFor={`no-action-${item.id}`}
                                    className="text-sm text-muted-foreground cursor-pointer"
                                  >
                                    No actions needed
                                  </label>
                                </div>
                              </div>
                              {!responses[item.id]?.actions?.noActionNeeded && (
                                <Textarea
                                  placeholder="Describe any actions required..."
                                  className="min-h-16 text-sm resize-none"
                                  value={responses[item.id]?.actions?.actionText || ''}
                                  onChange={(e) => {
                                    const currentResponse = responses[item.id] || {};
                                    handleResponseChange(item.id, {
                                      ...currentResponse,
                                      actions: {
                                        ...currentResponse.actions,
                                        actionText: e.target.value
                                      }
                                    });
                                  }}
                                  data-testid={`textarea-actions-${idx}`}
                                  disabled={isReadOnly}
                                />
                              )}
                              {responses[item.id]?.actions?.noActionNeeded && (
                                <div className="text-xs text-green-600 bg-green-50 p-2 rounded">
                                  ✓ No actions required for this item
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Universal Actions Section - Show for ANY type with actions enabled and a response */}
                    {item.actions_enabled && item.type !== 'ynna_actions_review' && (
                      (() => {
                        const hasResponse = typeof responses[item.id] === 'object' 
                          ? !!responses[item.id]?.answer 
                          : !!responses[item.id];
                        
                        if (!hasResponse) return null;
                        
                        return (
                          <div className="pt-3 border-t border-gray-200">
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <div className="text-sm font-medium text-gray-900">Actions Required</div>
                                <div className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`no-action-${item.id}`}
                                    checked={responses[item.id]?.actions?.noActionNeeded || false}
                                    onCheckedChange={(checked) => {
                                      const currentResponse = responses[item.id] || {};
                                      handleResponseChange(item.id, {
                                        ...currentResponse,
                                        actions: {
                                          ...currentResponse.actions,
                                          noActionNeeded: !!checked
                                        }
                                      });
                                    }}
                                    data-testid={`checkbox-no-action-${idx}`}
                                    disabled={isReadOnly}
                                  />
                                  <label
                                    htmlFor={`no-action-${item.id}`}
                                    className="text-sm text-muted-foreground cursor-pointer"
                                  >
                                    No actions needed
                                  </label>
                                </div>
                              </div>
                              {!responses[item.id]?.actions?.noActionNeeded && (
                                <Textarea
                                  placeholder="Describe any actions required..."
                                  className="min-h-16 text-sm resize-none"
                                  value={responses[item.id]?.actions?.actionText || ''}
                                  onChange={(e) => {
                                    const currentResponse = responses[item.id] || {};
                                    handleResponseChange(item.id, {
                                      ...currentResponse,
                                      actions: {
                                        ...currentResponse.actions,
                                        actionText: e.target.value
                                      }
                                    });
                                  }}
                                  data-testid={`textarea-actions-${idx}`}
                                  disabled={isReadOnly}
                                />
                              )}
                              {responses[item.id]?.actions?.noActionNeeded && (
                                <div className="text-xs text-green-600 bg-green-50 p-2 rounded">
                                  ✓ No actions required for this item
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()
                    )}
                  </div>
                </div>
              ))}
              
              {/* Attachment Upload Section */}
              <Card className="p-4 border border-gray-200">
                <h3 className="text-base font-semibold mb-3">📎 Attachments</h3>
                <p className="text-sm text-gray-600 mb-3">Add photos, PDFs, or Word documents to support this audit (max 10MB each)</p>
                
                <ObjectUploader
                  maxNumberOfFiles={10}
                  maxFileSize={10485760}
                  onComplete={async (result: UploadResult<Record<string, unknown>, Record<string, unknown>>) => {
                    if (!result.successful || result.successful.length === 0) {
                      toast({
                        title: "Upload failed",
                        description: "No files were uploaded successfully",
                        variant: "destructive"
                      });
                      return;
                    }
                    
                    for (const file of result.successful) {
                      // With proxy uploads, server returns { objectPath } in response body.
                      // Fallback to file.uploadURL if present (e.g., presign flow).
                      const objectPath = (file as any)?.response?.body?.objectPath;
                      const attachmentURL = objectPath || (file as any)?.uploadURL;
                      const fileName = file.name;
                      const fileSize = file.size;
                      const fileType = file.type || 'application/octet-stream';
                      
                      await apiRequest('POST', `/api/audits/${audit.id}/attachments`, {
                        attachmentURL,
                        fileName,
                        fileSize,
                        fileType,
                      });
                    }
                    
                    queryClient.invalidateQueries({ queryKey: ['/api/audits', auditIdParam] });
                    toast({
                      title: "Attachments uploaded",
                      description: `${result.successful.length} file(s) uploaded successfully`,
                    });
                  }}
                  buttonClassName="w-full"
                >
                  <div className="flex items-center justify-center gap-2">
                    <span>📁</span>
                    <span>Upload Files</span>
                  </div>
                </ObjectUploader>
                
                {/* Display existing attachments */}
                {audit.attachments && Array.isArray(audit.attachments) && audit.attachments.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-sm font-medium text-gray-700">Uploaded files:</p>
                    {audit.attachments.map((attachment: any, idx: number) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                        <div className="flex items-center gap-2">
                          <span>📄</span>
                          <span className="text-gray-900">{attachment.fileName}</span>
                          <span className="text-gray-500">({Math.round(attachment.fileSize / 1024)}KB)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <a 
                            href={attachment.path} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-[hsl(var(--brand-primary))] hover:underline"
                            data-testid={`link-view-attachment-${idx}`}
                          >
                            View
                          </a>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              try {
                                await apiRequest('DELETE', `/api/audits/${audit.id}/attachments`, {
                                  attachmentPath: attachment.path
                                });
                                queryClient.invalidateQueries({ queryKey: ['/api/audits', auditIdParam] });
                                toast({
                                  title: "Attachment removed",
                                  description: "File has been deleted from this audit"
                                });
                              } catch (error: any) {
                                toast({
                                  title: "Delete failed",
                                  description: error.message || "Failed to delete attachment",
                                  variant: "destructive"
                                });
                              }
                            }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-auto p-1"
                            data-testid={`button-delete-attachment-${idx}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
              
              {/* Completion / Score Card */}
              <Card className="p-4 bg-gradient-to-br from-purple-50 to-white border border-[hsl(var(--brand-primary))]">
                <div className="text-center">
                  <CheckCircle className="w-12 h-12 text-[hsl(var(--brand-primary))] mx-auto mb-3" />
                  <h3 className="text-lg font-bold mb-1 text-gray-900">Ready to Submit?</h3>
                  <p className="text-sm text-gray-600 mb-1">You've completed {filledCount} of {answerableItems.length} questions</p>
                  <div className="mb-4">
                    {/* Progress and Score bars */}
                    <AuditProgressAndScore
                      status={audit?.status}
                      completionPercentage={progressPercent}
                      score={audit?.score}
                    />
                  </div>
                  <div className="flex gap-2 justify-center flex-wrap">
                    <Button 
                      onClick={() => submitMutation.mutate()} 
                      variant="default"
                      size="lg"
                      disabled={isReadOnly || submitMutation.isPending || progressPercent < 100}
                      className="bg-green-600 hover:bg-green-700 font-semibold"
                      data-testid="button-submit-audit"
                    >
                      {isReadOnly ? 'View Only' : (submitMutation.isPending ? 'Submitting...' : '✓ Submit for Review')}
                    </Button>
                    <Button 
                      onClick={() => saveMutation.mutate()} 
                      variant="outline"
                      size="lg"
                      disabled={isReadOnly || saveMutation.isPending || saveStatus === 'saved'}
                      data-testid="button-save-audit"
                    >
                      {isReadOnly ? 'View Only' : (saveMutation.isPending ? 'Saving...' : saveStatus === 'saved' ? '✓ Saved' : 'Save Progress')}
                    </Button>
                    <Button 
                      onClick={() => setLocation('/audits')} 
                      variant="outline"
                      size="lg"
                      data-testid="button-back-to-audits"
                    >
                      Back to Audits
                    </Button>
                  </div>
                  {progressPercent < 100 && (
                    <p className="text-xs text-amber-600 mt-3">
                      ⚠️ Complete all questions before submitting
                    </p>
                  )}
                </div>
              </Card>

              {/* Areas of Good Practice */}
              <Card className="p-4 border border-green-200 bg-green-50/40">
                <h3 className="text-base font-semibold mb-2">🌟 Areas of Good Practice</h3>
                <p className="text-sm text-gray-600 mb-3">Share any positive practices observed during this audit. This helps motivate the team.</p>
                <div className="flex items-start gap-2 mb-2">
                  <Checkbox
                    id="gp-none"
                    checked={(responses as any)._good_practice?.is_none || false}
                    onCheckedChange={(checked) => {
                      setResponses(prev => ({
                        ...prev,
                        _good_practice: { ...(prev as any)._good_practice, is_none: !!checked }
                      }));
                    }}
                  />
                  <label htmlFor="gp-none" className="text-sm text-gray-800">No areas of good practice to add</label>
                </div>
                {!((responses as any)._good_practice?.is_none) && (
                  <Textarea
                    placeholder="Write a short note about good practice observed..."
                    value={(responses as any)._good_practice?.description || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setResponses(prev => ({
                        ...prev,
                        _good_practice: { ...(prev as any)._good_practice, description: val }
                      }));
                      setSaveStatus("unsaved");
                      setHasUnsavedChanges(true);
                    }}
                  />
                )}
              </Card>
            </div>
          </div>
        </div>
      </div>
      
      {/* Floating Save Button for Mobile */}
      <div className="lg:hidden fixed bottom-6 right-6 z-10">
        <Button 
          onClick={() => saveMutation.mutate()} 
          disabled={isReadOnly || saveMutation.isPending || saveStatus === 'saved'}
          className="bg-[hsl(var(--brand-primary))] hover:bg-[hsl(var(--secondary))] shadow-lg rounded-full h-14 w-14 p-0"
          data-testid="button-floating-save"
        >
          {isReadOnly ? '🔒' : (saveMutation.isPending ? '...' : saveStatus === 'saved' ? '✓' : '💾')}
        </Button>
      </div>

      {/* Score Alert Dialog */}
      <Dialog open={showScoreDialog} onOpenChange={setShowScoreDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-6 w-6 text-green-600" />
              Audit Submitted Successfully
            </DialogTitle>
            <DialogDescription>
              Here's your audit score and next steps
            </DialogDescription>
          </DialogHeader>
          {submittedScore !== null && (() => {
            const scoreStatus = getScoreStatus(submittedScore);
            return (
              <div className="space-y-4">
                <Alert className={scoreStatus.bgClass}>
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{scoreStatus.emoji}</span>
                    <div className="flex-1">
                      <AlertTitle className={scoreStatus.colorClass}>
                        Your Score: {submittedScore}%
                      </AlertTitle>
                      <AlertDescription className="mt-2">
                        <strong>{scoreStatus.label}</strong>
                        {scoreStatus.severity === 'critical' && (
                          <p className="mt-2">Your manager has been notified and will review this audit immediately. Please discuss any concerns with your supervisor.</p>
                        )}
                        {scoreStatus.severity === 'warning' && (
                          <p className="mt-2">Your manager has been notified. Please review the areas needing improvement and discuss with your team.</p>
                        )}
                        {scoreStatus.severity === 'info' && (
                          <p className="mt-2">Good work! Share any learnings from this audit with your team to maintain quality standards.</p>
                        )}
                        {scoreStatus.severity === 'none' && (
                          <p className="mt-2">Excellent work! Your audit shows strong compliance and quality standards.</p>
                        )}
                      </AlertDescription>
                    </div>
                  </div>
                </Alert>
              </div>
            );
          })()}
          <DialogFooter>
            <Button 
              onClick={() => {
                setShowScoreDialog(false);
                setLocation('/audits');
              }}
              className="w-full"
              data-testid="button-close-score-dialog"
            >
              Return to Audits
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Actions Page - For managers to track and resolve audit action items
function ActionsPage() {
  const { session, isLoading } = useSession();
  const user = session?.user;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const breadcrumbs = useBreadcrumbs();
  const [, setLocation] = useLocation();
  
  const [statusFilter, setStatusFilter] = useState<'all' | 'Pending' | 'InProgress' | 'Completed'>('all');
  const [selectedAction, setSelectedAction] = useState<any | null>(null);

  // CONFIDENTIALITY: Platform roles should NOT access action items
  const isPlatformRole = user?.role === 'Developer' || user?.role === 'PlatformManager';
  const canReopen = user?.role && ['OrgManager', 'OrgAdmin', 'HomeManager', 'HomeAdmin', 'Developer', 'PlatformManager'].includes(user.role);
  if (!isLoading && isPlatformRole) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center text-red-600">
              <AlertCircle className="mr-2 h-5 w-5" />
              Access Denied
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-600">
              Platform roles do not have access to confidential action items. Actions contain sensitive care home data that is restricted to organizational and home-level users only.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Fetch actions
  const { data: actions = [], isLoading: actionsLoading } = useQuery({
    queryKey: ["/api/actions", { status: statusFilter !== 'all' ? statusFilter : undefined }],
    queryFn: async () => {
      try {
        const params = new URLSearchParams();
        if (statusFilter !== 'all') params.append('status', statusFilter);
        const url = params.toString() ? `/api/actions?${params.toString()}` : '/api/actions';
        const res = await apiRequest("GET", url);
        const data = await res.json();
        // Debug log to help diagnose frontend empty list issues (remove in prod)
        // eslint-disable-next-line no-console
        console.debug('[DEBUG] /api/actions response:', data);
        // Normalize response: support both array and { items } formats
        if (Array.isArray(data)) return data;
        return data?.items ?? [];
      } catch (e) {
        // Log error and return empty array so UI shows friendly message
        // eslint-disable-next-line no-console
        console.error('Failed to fetch /api/actions:', e);
        return [];
      }
    }
  });
  
  // Update action mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => 
      apiRequest("PATCH", `/api/actions/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/actions"] });
      toast({
        title: "Success",
        description: "Action updated successfully",
      });
      setSelectedAction(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update action",
        variant: "destructive",
      });
    },
  });
  
  // Human-friendly status label
  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'InProgress':
        return 'In Progress';
      default:
        return status;
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      case 'InProgress': return 'bg-blue-100 text-blue-800';
      case 'Completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getPriorityBadgeClass = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-blue-100 text-blue-800';
      case 'low': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  const pendingCount = actions.filter((a: any) => a.status === 'Pending').length;
  const inProgressCount = actions.filter((a: any) => a.status === 'InProgress').length;
  
  if (actionsLoading) {
    return (
      <div className="space-y-6">
  <h1 className="page-title text-[26px] font-bold">Actions</h1>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-gray-200 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <ResponsiveBreadcrumb items={breadcrumbs.actions()} className="mb-4" />
      
      <div className="flex items-center justify-between">
  <h1 className="page-title text-[26px] font-bold text-foreground" data-testid="page-title">Actions</h1>
      </div>
      
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{pendingCount}</div>
            <p className="text-sm text-muted-foreground">Pending Actions</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{inProgressCount}</div>
            <p className="text-sm text-muted-foreground">In Progress</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{actions.length}</div>
            <p className="text-sm text-muted-foreground">Total Actions</p>
          </CardContent>
        </Card>
      </div>
      
      {/* Status Filter */}
      <div className="flex gap-4 p-4 bg-card rounded-lg border">
        <div className="flex gap-2">
          <Button
            variant={statusFilter === 'all' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('all')}
            data-testid="filter-all"
          >
            All
          </Button>
          <Button
            variant={statusFilter === 'Pending' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('Pending')}
            data-testid="filter-pending"
          >
            Pending
          </Button>
          <Button
            variant={statusFilter === 'InProgress' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('InProgress')}
            data-testid="filter-inprogress"
          >
            In Progress
          </Button>
          <Button
            variant={statusFilter === 'Completed' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('Completed')}
            data-testid="filter-completed"
          >
            Completed
          </Button>
        </div>
      </div>
      
      {/* Actions List */}
      {actions.length === 0 ? (
        <Card className="p-8">
          <div className="text-center space-y-4">
            <ClipboardList className="w-16 h-16 text-gray-400 mx-auto" />
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No actions found</h3>
              <p className="text-gray-600">
                {statusFilter !== 'all' 
                  ? `No ${statusFilter.toLowerCase()} actions at this time.`
                  : 'No action items require your attention.'}
              </p>
            </div>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {actions.map((action: any) => (
            <Card 
              key={action.id} 
              className="p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => setSelectedAction(action)}
              data-testid={`card-action-${action.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  {/* Question and Action */}
                  <div className="mb-3">
                    <h3 className="font-semibold text-foreground mb-1 line-clamp-2">
                      {action.item_label}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {action.action_description}
                    </p>
                  </div>
                  
                  {/* Metadata */}
                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <FileText className="w-3.5 h-3.5" />
                      {action.audit?.name || '-'}
                    </div>
                    <div className="flex items-center gap-1">
                      <Building2 className="w-3.5 h-3.5" />
                      {action.home?.name || '-'}
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="w-3.5 h-3.5" />
                      {action.assignee?.name || 'Unassigned'}
                    </div>
                  </div>
                </div>
                
                {/* Status and Priority */}
                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  <Badge className={getStatusBadgeClass(action.status)}>
                    {getStatusDisplay(action.status)}
                  </Badge>
                  <Badge variant="outline" className={getPriorityBadgeClass(action.audit?.priority || 'medium')}>
                    {action.audit?.priority || 'medium'}
                  </Badge>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      
      {/* Action Review Dialog */}
      <Dialog open={!!selectedAction} onOpenChange={() => setSelectedAction(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review Action Item</DialogTitle>
          </DialogHeader>
          
          {selectedAction && (
            <div className="space-y-6">
              {/* Action Details */}
              <div className="space-y-4 pb-4 border-b">
                <div>
                  <label className="text-sm font-medium text-foreground">Question</label>
                  <p className="text-sm text-muted-foreground mt-1">{selectedAction.item_label}</p>
                </div>
                
                {selectedAction.item_answer && (
                  <div>
                    <label className="text-sm font-medium text-foreground">Answer Given</label>
                    <p className="text-sm text-muted-foreground mt-1 italic">{selectedAction.item_answer}</p>
                  </div>
                )}
                
                <div>
                  <label className="text-sm font-medium text-foreground">Action Required</label>
                  <p className="text-sm text-muted-foreground mt-1">{selectedAction.action_description}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground">Audit</label>
                    <p className="text-sm text-muted-foreground mt-1">{selectedAction.audit?.name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">Home</label>
                    <p className="text-sm text-muted-foreground mt-1">{selectedAction.home?.name}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground">Status</label>
                    <div className="mt-1">
                      <Badge className={getStatusBadgeClass(selectedAction.status)}>
                        {selectedAction.status}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">Assigned To</label>
                    <p className="text-sm text-muted-foreground mt-1">{selectedAction.assignee?.name || 'Unassigned'}</p>
                  </div>
                </div>
              </div>

              {/* Review Section */}
              <div className="space-y-4">
                <h3 className="font-semibold text-foreground">Manager Review</h3>
                
                <div>
                  <Label htmlFor="review-notes" className="text-sm font-medium">Reviewer Notes</Label>
                  <Textarea
                    id="review-notes"
                    placeholder="Add your review notes about this action..."
                    className="mt-2 min-h-24"
                    defaultValue={selectedAction.review_notes || ''}
                    onChange={(e) => {
                      setSelectedAction({ ...selectedAction, _tempReviewNotes: e.target.value });
                    }}
                    data-testid="textarea-review-notes"
                  />
                </div>

                {/* Action Status Indicators */}
                {selectedAction.actioned_at && (
                  <div className="bg-blue-50 dark:bg-blue-950/20 p-3 rounded-md">
                    <div className="flex items-center gap-2 text-sm text-blue-700 dark:text-blue-400">
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Marked as actioned on {new Date(selectedAction.actioned_at).toLocaleDateString('en-GB')}</span>
                    </div>
                  </div>
                )}

                {selectedAction.acknowledged && (
                  <div className="bg-green-50 dark:bg-green-950/20 p-3 rounded-md">
                    <div className="flex items-center gap-2 text-sm text-meta dark:text-green-400">
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Acknowledged as completed on {new Date(selectedAction.acknowledged_at).toLocaleDateString('en-GB')}</span>
                    </div>
                  </div>
                )}

                {/* Acknowledgment Checkbox */}
                {selectedAction.actioned_at && !selectedAction.acknowledged && (
                  <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-950/20 rounded-md">
                    <Checkbox
                      id="acknowledge-action"
                      checked={selectedAction._tempAcknowledged || false}
                      onCheckedChange={(checked) => {
                        setSelectedAction({ ...selectedAction, _tempAcknowledged: !!checked });
                      }}
                      data-testid="checkbox-acknowledge"
                    />
                    <div className="flex-1">
                      <label htmlFor="acknowledge-action" className="text-sm font-medium text-foreground cursor-pointer">
                        I acknowledge that I have reviewed this action and confirm it has been actioned accordingly
                      </label>
                      <p className="text-xs text-muted-foreground mt-1">
                        This will mark the action as acknowledged and close this review
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                {!selectedAction.actioned_at && (
                  <>
                    <Button
                      onClick={() => {
                        const notes = selectedAction._tempReviewNotes || selectedAction.review_notes;
                        updateMutation.mutate({ 
                          id: selectedAction.id, 
                          data: { 
                            actioned: true,
                            review_notes: notes
                          } 
                        });
                      }}
                      disabled={updateMutation.isPending}
                      data-testid="button-mark-actioned"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Mark as Actioned
                    </Button>
                  </>
                )}

                {selectedAction.actioned_at && !selectedAction.acknowledged && selectedAction._tempAcknowledged && (
                  <Button
                    onClick={() => {
                      const notes = selectedAction._tempReviewNotes || selectedAction.review_notes;
                      updateMutation.mutate({ 
                        id: selectedAction.id, 
                        data: { 
                          acknowledged: true,
                          review_notes: notes
                        } 
                      });
                    }}
                    disabled={updateMutation.isPending}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="button-acknowledge"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Acknowledge & Complete Review
                  </Button>
                )}

                <Button
                  variant="outline"
                  onClick={() => setLocation(`/audits/view?auditId=${selectedAction.audit_id}`)}
                  data-testid="button-view-audit"
                >
                  View Audit
                </Button>
              </div>

              {/* Reopen Section - managers can revert an action back to Pending with a reason */}
              {( (selectedAction.status === 'InProgress' || selectedAction.status === 'Completed' || selectedAction.acknowledged) && canReopen ) && (
                <div className="pt-4 border-t space-y-2">
                  <Label htmlFor="reopen-reason" className="text-sm font-medium">Reopen Reason (required)</Label>
                  <Textarea
                    id="reopen-reason"
                    placeholder="Explain why you're reopening this action (this will be appended to review notes and sent as a notification)"
                    className="mt-2 min-h-20"
                    value={selectedAction._tempReopenReason || ''}
                    onChange={(e) => setSelectedAction({ ...selectedAction, _tempReopenReason: e.target.value })}
                    data-testid="textarea-reopen-reason"
                  />
                  <div className="flex gap-2">
                    <Button
                      variant="destructive"
                      onClick={() => {
                        const reason = (selectedAction._tempReopenReason || '').trim();
                        if (!reason) {
                          // Local validation before sending
                          // eslint-disable-next-line no-console
                          console.warn('Reopen reason required');
                          return;
                        }
                        updateMutation.mutate({ id: selectedAction.id, data: { reopen: true, reopen_reason: reason } });
                      }}
                      disabled={updateMutation.isPending || !(selectedAction._tempReopenReason && selectedAction._tempReopenReason.trim())}
                      data-testid="button-reopen-action"
                    >
                      Reopen to Pending
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setSelectedAction({ ...selectedAction, _tempReopenReason: '' })}
                    >
                      Clear
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// New Audit Wizard Page
function NewAuditPage() {
  const { session } = useSession();
  const user = session?.user;
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const breadcrumbs = useBreadcrumbs();
  
  // Get templateId from URL
  const urlParams = new URLSearchParams(window.location.search);
  const templateIdParam = urlParams.get('templateId');
  
  // Wizard state
  const [step, setStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [auditName, setAuditName] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("medium");
  const [completionMode, setCompletionMode] = useState<'self' | 'assign' | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedHomeId, setSelectedHomeId] = useState<number | null>(null);
  
  // Check if user is manager (can assign audits)
  const isManager = user?.role && ['OrgManager', 'HomeManager', 'Developer', 'PlatformManager'].includes(user.role);
  const isOrgManager = user?.role && ['OrgManager', 'OrgAdmin'].includes(user.role);
  
  // Fetch templates
  const { data: templatesResponse, isLoading: templatesLoading } = useQuery({
    queryKey: ["/api/templates/available"],
    queryFn: () => apiRequest("GET", "/api/templates/available?limit=50").then(res => res.json()),
    enabled: !templateIdParam
  });
  const templates = templatesResponse?.items || [];
  
  // Fetch specific template if ID provided
  const { data: selectedTemplateData, isLoading: templateLoading } = useQuery({
    queryKey: ["/api/templates", templateIdParam],
    queryFn: () => apiRequest("GET", `/api/templates/${templateIdParam}`).then(res => res.json()),
    enabled: !!templateIdParam
  });
  
  // Fetch homes for OrgManagers
  const { data: homesData } = useQuery({
    queryKey: ["/api/homes"],
    queryFn: () => apiRequest("GET", "/api/homes").then(res => res.json()),
    enabled: Boolean(isOrgManager && completionMode)
  });
  const homes = homesData || [];
  
  // Fetch users for assignment (managers only)
  const { data: usersData } = useQuery({
    queryKey: ["/api/users"],
    queryFn: () => apiRequest("GET", "/api/users").then(res => res.json()),
    enabled: Boolean(isManager && completionMode === 'assign')
  });
  
  // Filter users by selected home if OrgManager
  const users = isOrgManager && selectedHomeId 
    ? (usersData || []).filter((u: any) => u.home_id === selectedHomeId)
    : (usersData || []);
  
  // Auto-set template if templateId provided
  useEffect(() => {
    if (selectedTemplateData && !selectedTemplate) {
      setSelectedTemplate(selectedTemplateData);
      setAuditName(selectedTemplateData.name);
      setStep(2);
    }
  }, [selectedTemplateData]);
  
  // Create audit mutation
  const createAuditMutation = useMutation({
    mutationFn: async (auditData: any) => {
      const response = await apiRequest("POST", "/api/audits", auditData);
      if (!response.ok) {
        const error = await response.json();
        const err = new Error(error.message || "Failed to create audit");
        (err as any).existingAuditId = error.existingAuditId;
        (err as any).existingAuditName = error.existingAuditName;
        throw err;
      }
      return response.json();
    },
    onSuccess: (audit) => {
      queryClient.invalidateQueries({ queryKey: ["/api/audits"] });
      
      if (completionMode === 'self') {
        toast({
          title: "Success",
          description: "Audit created successfully. Starting audit now...",
        });
        setLocation(`/audits/take?auditId=${audit.id}`);
      } else {
        toast({
          title: "Success",
          description: "Audit assigned successfully.",
        });
        setLocation('/audits');
      }
    },
    onError: (error: any) => {
      // If there's an existing audit, show action buttons
      if (error.existingAuditId) {
        toast({
          title: "Audit Already Started",
          description: error.message || "An audit for this template already exists",
          variant: "destructive",
          action: (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setLocation(`/audits/take?auditId=${error.existingAuditId}`)}
                data-testid="button-continue-existing-audit"
              >
                Continue Audit
              </Button>
            </div>
          ),
        });
      } else {
        toast({
          title: "Error",
          description: error.message || "Failed to create audit",
          variant: "destructive",
        });
      }
    },
  });
  
  const handleCreateAudit = () => {
    if (!selectedTemplate || !auditName.trim()) {
      toast({
        title: "Validation Error",
        description: "Please provide audit name",
        variant: "destructive"
      });
      return;
    }
    
    // Determine which home_id to use
    const homeId = isOrgManager ? selectedHomeId : user?.home_id;
    
    if (!homeId) {
      toast({
        title: "Error",
        description: isOrgManager ? "Please select a home" : "User not assigned to a home",
        variant: "destructive"
      });
      return;
    }
    
    if (completionMode === 'assign' && !selectedUserId) {
      toast({
        title: "Validation Error",
        description: "Please select a user to assign",
        variant: "destructive"
      });
      return;
    }
    
    createAuditMutation.mutate({
      name: auditName,
      description: description || undefined,
      priority,
      template_id: selectedTemplate.id,
      home_id: homeId,
      type_id: selectedTemplate.type_id,
      status: 'Draft',
      assigned_to: completionMode === 'assign' ? selectedUserId : user?.id
    });
  };
  
  if (templatesLoading || templateLoading) {
    return (
      <div className="space-y-6">
        <ResponsiveBreadcrumb items={breadcrumbs.newAudit()} className="mb-4" />
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="clean-ui space-y-6 pb-20 sm:pb-6">
      <ResponsiveBreadcrumb items={breadcrumbs.newAudit()} className="mb-4" />
      <PageHeader 
        title="Create New Audit"
        subtitle={
          step === 1 ? "Select a template to get started" :
          step === 2 && isManager ? "Choose how to complete this audit" :
          step === 2 ? "Configure audit details" :
          "Assign to a user"
        }
        customBackText="Back"
      />
      
      {/* Step 1: Template Selection (skip if templateId provided) */}
      {step === 1 && (
        <div className="space-y-4">
          {templates.map((template: Template) => (
            <Card 
              key={template.id} 
              className="hover:shadow-lg transition-all duration-200 border border-gray-200 hover:border-purple-200"
            >
              <CardContent className="p-0">
                <div className="flex flex-col sm:flex-row items-stretch">
                  {/* Left accent bar */}
                  <div className="w-full sm:w-1.5 bg-gradient-to-b from-purple-600 to-purple-400 rounded-l-lg" />
                  
                  {/* Main content */}
                  <div className="flex-1 p-5 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      {/* Template Info */}
                      <div className="flex-1 min-w-0 space-y-3">
                        <div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2 leading-tight">{template.name}</h3>
                          <p className="text-base text-gray-600 leading-relaxed max-w-2xl">{template.description}</p>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-3">
                          <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 border-0 px-3 py-1 text-sm font-medium">
                            {template.category_name}
                          </Badge>
                          <Badge variant="outline" className="text-gray-700 border-gray-300 px-3 py-1 text-sm">
                            {template.type_name}
                          </Badge>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex sm:flex-col items-center sm:items-end gap-2 sm:gap-3">
                        <Button 
                          className="w-full sm:w-auto bg-[hsl(var(--brand-primary))] hover:bg-[hsl(var(--secondary))] text-white shadow-sm px-6"
                          onClick={() => {
                            setSelectedTemplate(template);
                            setAuditName(template.name);
                            setStep(2);
                          }}
                          data-testid={`template-option-${template.id}`}
                        >
                          Use Template
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {/* Step 2: Manager Decision OR Audit Details */}
      {step === 2 && isManager && (
        <Card>
          <CardHeader>
            <CardTitle>Who will complete this audit?</CardTitle>
            <p className="text-gray-600">Choose how to proceed with this audit</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div
                onClick={() => setCompletionMode('self')}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  completionMode === 'self' 
                    ? 'border-purple-500 bg-purple-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid="option-complete-myself"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 ${
                    completionMode === 'self' 
                      ? 'border-purple-500 bg-purple-500' 
                      : 'border-gray-300'
                  }`}>
                    {completionMode === 'self' && (
                      <div className="w-full h-full flex items-center justify-center text-white text-xs">✓</div>
                    )}
                  </div>
                  <div>
                    <p className="font-semibold">I'll complete it myself</p>
                    <p className="text-sm text-gray-600">Start the audit now and fill it out</p>
                  </div>
                </div>
              </div>
              
              <div
                onClick={() => setCompletionMode('assign')}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  completionMode === 'assign' 
                    ? 'border-purple-500 bg-purple-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid="option-assign-to-user"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 ${
                    completionMode === 'assign' 
                      ? 'border-purple-500 bg-purple-500' 
                      : 'border-gray-300'
                  }`}>
                    {completionMode === 'assign' && (
                      <div className="w-full h-full flex items-center justify-center text-white text-xs">✓</div>
                    )}
                  </div>
                  <div>
                    <p className="font-semibold">Assign to a user</p>
                    <p className="text-sm text-gray-600">Create the audit and assign it to someone else</p>
                  </div>
                </div>
              </div>
            </div>
            
            {completionMode && (
              <div className="pt-4 border-t space-y-4">
                <div>
                  <Label>Audit Name *</Label>
                  <Input
                    value={auditName}
                    onChange={(e) => setAuditName(e.target.value)}
                    placeholder="Audit name"
                    disabled={true}
                    className="bg-gray-50 cursor-not-allowed"
                    data-testid="input-audit-name"
                  />
                  <p className="text-xs text-gray-500 mt-1">Audit name is set from the template and cannot be changed</p>
                </div>
                <div>
                  <Label>Description (Optional)</Label>
                  <textarea
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-gray-50 px-3 py-2 text-sm cursor-not-allowed"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Optional description..."
                    disabled={true}
                    data-testid="textarea-audit-description"
                  />
                  <p className="text-xs text-gray-500 mt-1">Description is set from the template and cannot be changed</p>
                </div>
                <div>
                  <Label>Priority</Label>
                  <select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                
                {isOrgManager && (
                  <div>
                    <Label>Select Home *</Label>
                    <select
                      value={selectedHomeId || ''}
                      onChange={(e) => {
                        setSelectedHomeId(parseInt(e.target.value));
                        setSelectedUserId(null); // Reset user selection when home changes
                      }}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="select-home"
                    >
                      <option value="">Select a home...</option>
                      {homes.map((home: any) => (
                        <option key={home.id} value={home.id}>
                          {home.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                {completionMode === 'assign' && (
                  <div>
                    <Label>Assign To *</Label>
                    <select
                      value={selectedUserId || ''}
                      onChange={(e) => setSelectedUserId(parseInt(e.target.value))}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="select-assign-user"
                      disabled={!!(isOrgManager && !selectedHomeId)}
                    >
                      <option value="">
                        {isOrgManager && !selectedHomeId ? "Please select a home first..." : "Select a user..."}
                      </option>
                      {users
                        .filter((u: any) => {
                          const currentRoleNorm = (user?.role || '').toLowerCase().replace(/_/g, '');
                          const targetRoleNorm = (u?.role || '').toLowerCase().replace(/_/g, '');
                          return currentRoleNorm === 'developer' ? true : targetRoleNorm !== 'developer';
                        })
                        .map((u: any) => (
                        <option key={u.id} value={u.id}>
                          {u.first_name} {u.last_name} ({u.role})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                <div className="flex gap-3 pt-4">
                  <Button onClick={() => setStep(1)} variant="outline">
                    Back
                  </Button>
                  <Button 
                    onClick={handleCreateAudit}
                    disabled={
                      createAuditMutation.isPending || 
                      !auditName.trim() || 
                      (isOrgManager && !selectedHomeId) ||
                      (completionMode === 'assign' && !selectedUserId)
                    }
                  >
                    {createAuditMutation.isPending ? 'Creating...' : 
                     completionMode === 'self' ? 'Create & Start Audit' : 'Create & Assign'}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Step 2: Staff (non-manager) - Just audit details */}
      {step === 2 && !isManager && (
        <Card>
          <CardHeader>
            <CardTitle>Audit Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Audit Name *</Label>
              <Input
                value={auditName}
                onChange={(e) => setAuditName(e.target.value)}
                placeholder="Audit name"
                disabled={true}
                className="bg-gray-50 cursor-not-allowed"
                data-testid="input-audit-name"
              />
              <p className="text-xs text-gray-500 mt-1">Audit name is set from the template and cannot be changed</p>
            </div>
            <div>
              <Label>Description (Optional)</Label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-gray-50 px-3 py-2 text-sm cursor-not-allowed"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description..."
                disabled={true}
                data-testid="textarea-audit-description"
              />
              <p className="text-xs text-gray-500 mt-1">Description is set from the template and cannot be changed</p>
            </div>
            <div>
              <Label>Priority</Label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button onClick={() => setStep(1)} variant="outline">
                Back
              </Button>
              <Button 
                onClick={() => {
                  setCompletionMode('self');
                  handleCreateAudit();
                }}
                disabled={createAuditMutation.isPending || !auditName.trim()}
              >
                {createAuditMutation.isPending ? 'Creating...' : 'Create & Start Audit'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Good Practices Page - motivational feed of positive notes
function GoodPracticesPage() {
  const { session } = useSession();
  const user = session?.user;
  const { toast } = useToast();
  const breadcrumbs = useBreadcrumbs();
  const [, setLocation] = useLocation();
  const [q, setQ] = useState('');
  const debouncedQ = q; // keep simple
  // Filter by Template instead of individual audits (dedupe by template)
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  const [selectedHomeIds, setSelectedHomeIds] = useState<string[]>([]);
  // Fetch audits and homes for filter options (respect role-based scoping on server)
  const { data: auditsResp } = useQuery({
    queryKey: ['/api/audits', 'good-practices'],
    queryFn: () => apiRequest('GET', '/api/audits').then(r => r.json()),
    enabled: true,
  });
  const audits = Array.isArray(auditsResp) ? auditsResp : (auditsResp?.items || []);

  // Fetch templates to label template options
  const { data: templatesResp } = useQuery({
    queryKey: ['/api/templates', 'good-practices'],
    queryFn: () => apiRequest('GET', '/api/templates').then(r => r.json()),
    enabled: true,
  });
  const templatesList = toArray(templatesResp);

  // Dedupe template options from audits present
  const templateOptions = ((): Array<{ value: string; label: string }> => {
    const templateIdSet = new Set<string>();
    audits.forEach((a: any) => {
      if (a && a.template_id != null) templateIdSet.add(String(a.template_id));
    });
    const opts = Array.from(templateIdSet).map((tid) => {
      const t = templatesList.find((tt: any) => String(tt.id) === tid);
      return { value: tid, label: t?.name || `Template ${tid}` };
    });
    // sort by label for UX
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  })();

  // Expand selected template IDs to audit IDs for the API param (server expects 'audits')
  const expandedAuditIds = useMemo(() => {
    if (!selectedTemplateIds || selectedTemplateIds.length === 0) return [] as string[];
    return audits
      .filter((a: any) => a && a.template_id != null && selectedTemplateIds.includes(String(a.template_id)))
      .map((a: any) => String(a.id));
  }, [audits, selectedTemplateIds]);

  const { data: homesResp } = useQuery({
    queryKey: ['/api/homes', 'good-practices'],
    queryFn: () => apiRequest('GET', '/api/homes').then(r => r.json()),
    enabled: true,
  });
  const homes = Array.isArray(homesResp) ? homesResp : (homesResp?.items || []);

  // Check if user is HomeStaff (read-only mode)
  const isHomeStaff = user?.role === 'HomeStaff';

  const { data, isLoading } = useQuery({
    queryKey: ['/api/good-practices', { q: debouncedQ, audits: expandedAuditIds, homes: selectedHomeIds }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (debouncedQ) params.append('q', debouncedQ);
      if (expandedAuditIds && expandedAuditIds.length > 0) params.append('audits', expandedAuditIds.join(','));
      if (selectedHomeIds && selectedHomeIds.length > 0) params.append('homes', selectedHomeIds.join(','));
      const res = await apiRequest('GET', `/api/good-practices?${params.toString()}`);
      return res.json();
    }
  });
  const items = data?.items || data || [];

  return (
    <div className="space-y-6">
      <ResponsiveBreadcrumb items={breadcrumbs.custom([{ label: 'Good Practices', href: '/good-practices' }])} className="mb-4" />
      
      {/* Read-only banner for HomeStaff */}
      {isHomeStaff && (
        <Alert className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
          <Eye className="h-4 w-4 text-blue-600" />
          <AlertTitle className="text-blue-900 dark:text-blue-100">View Only</AlertTitle>
          <AlertDescription className="text-blue-700 dark:text-blue-300">
            You can view good practices from your home but cannot create, edit, or delete them.
          </AlertDescription>
        </Alert>
      )}
      
      <div className="flex items-center justify-between">
  <h1 className="page-title text-[26px] font-bold">Good Practices</h1>
      </div>
      <div className="flex gap-2">
        <Input placeholder="Search..." value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="w-48">
          <MultiSelectFilter
            options={templateOptions}
            selectedValues={selectedTemplateIds}
            onSelectionChange={setSelectedTemplateIds}
            placeholder="All Templates"
            searchPlaceholder="Search templates..."
            testId="gp-filter-audits"
          />
        </div>
        <div className="w-48">
          <MultiSelectFilter
            options={homes.map((h: any) => ({ value: String(h.id), label: h.name || `Home ${h.id}` }))}
            selectedValues={selectedHomeIds}
            onSelectionChange={setSelectedHomeIds}
            placeholder="All Homes"
            searchPlaceholder="Search homes..."
            testId="gp-filter-homes"
          />
        </div>
      </div>
      {isLoading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => <div key={i} className="h-20 bg-gray-100 rounded animate-pulse" />)}
        </div>
      ) : // filter out empty / N/A / is_none items for display
      items.filter((gp: any) => {
        if (!gp) return false;
        if (gp.is_none) return false;
        const t = (gp.title || '').toString().trim();
        const d = (gp.description || '').toString().trim();
        if (!t && !d) return false;
        if (d.toLowerCase() === 'n/a' || t.toLowerCase() === 'n/a') return false;
        return true;
      }).length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">
          {isHomeStaff ? 'No good practices recorded yet for your home' : 'No items yet'}
        </Card>
      ) : (
        <div className="space-y-3">
          {items.filter((gp: any) => {
            if (!gp) return false;
            if (gp.is_none) return false;
            const t = (gp.title || '').toString().trim();
            const d = (gp.description || '').toString().trim();
            if (!t && !d) return false;
            if (d.toLowerCase() === 'n/a' || t.toLowerCase() === 'n/a') return false;
            return true;
          }).map((gp: any) => (
            <Card key={gp.id} className="p-4 border border-slate-200" data-testid={`card-gp-${gp.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1 pr-4">
                  {gp.title && <div className="text-lg font-semibold" data-testid={`gp-item-title-${gp.id}`}>{gp.title}</div>}
                  <div className="text-sm text-slate-600 mt-1" data-testid={`gp-item-desc-${gp.id}`}>{gp.description}</div>

                  <div className="flex items-center gap-4 text-xs text-slate-500 mt-3">
                    <span className="inline-flex items-center gap-1">
                      <FileText className="w-3 h-3 opacity-80" />
                      <span className="truncate max-w-[160px]">{gp.audit_name || 'General Audit'}</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Home className="w-3 h-3 opacity-80" />
                      <span className="truncate max-w-[120px]">{gp.home_name || 'Unknown home'}</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Users className="w-3 h-3 opacity-80" />
                      <span className="truncate max-w-[100px]">{gp.author_name || 'Home Manager'}</span>
                    </span>
                  </div>
                </div>

                {/* Removed status & priority badges per design: Good Practices cards should be text-only */}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// Main Audits List Page
function AuditsPage() {
  const { session } = useSession();
  const user = session?.user;
  const [, setLocation] = useLocation();
  const { canWrite, showWriteBlockedMessage } = useWriteGuard();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const breadcrumbs = useBreadcrumbs();
  
  // Permission checks using new 7-role system
  const canApproveAudits = user?.role && ['OrgManager', 'HomeManager', 'OrgAdmin', 'HomeAdmin', 'Developer', 'PlatformManager'].includes(user.role);
  const canCreateAudits = user?.role && ['HomeStaff', 'HomeAdmin', 'HomeManager', 'OrgAdmin', 'OrgManager', 'Developer', 'PlatformManager'].includes(user.role);
  const canDeleteAudits = user?.role && ['OrgManager', 'HomeManager', 'Developer', 'PlatformManager'].includes(user.role);
  const canSendReminders = user?.role && ['OrgManager', 'OrgAdmin', 'HomeManager', 'HomeAdmin', 'Developer', 'PlatformManager'].includes(user.role);
  
  const [searchTerm, setSearchTerm] = useState("");
  // Initialize from URL params
  const auditUrlParams = new URLSearchParams(window.location.search);
  const initialAuditStatus = parseArrayParam(auditUrlParams.get('status'));
  const initialAuditCreatedBy = (auditUrlParams.get('createdBy') as 'all' | 'me') || 'all';
  const initialAuditTypes = parseArrayParam(auditUrlParams.get('types'));
  const initialAuditCategories = parseArrayParam(auditUrlParams.get('categories'));
  const initialAuditOrgs = parseArrayParam(auditUrlParams.get('orgs'));
  const initialAuditHomes = parseArrayParam(auditUrlParams.get('homes'));

  // Use arrays consistently across the app for filters
  const [statusFilter, setStatusFilter] = useState<string[]>(initialAuditStatus);
  const [createdByFilter, setCreatedByFilter] = useState<'all' | 'me'>(initialAuditCreatedBy);
  const [typeFilter, setTypeFilter] = useState<string[]>(initialAuditTypes);
  const [categoryFilter, setCategoryFilter] = useState<string[]>(initialAuditCategories);
  const [orgFilter, setOrgFilter] = useState<string[]>(initialAuditOrgs);
  const [homeFilter, setHomeFilter] = useState<string[]>(initialAuditHomes);

  // Popover/search open state and search terms used by filters (local to AuditsPage)
  const [statusSearchOpen, setStatusSearchOpen] = useState(false);
  const [statusSearchTerm, setStatusSearchTerm] = useState("");
  const [typeSearchOpen, setTypeSearchOpen] = useState(false);
  const [typeSearchTerm, setTypeSearchTerm] = useState("");
  const [categorySearchOpen, setCategorySearchOpen] = useState(false);
  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const [orgSearchOpen, setOrgSearchOpen] = useState(false);
  const [orgSearchTerm, setOrgSearchTerm] = useState("");
  const [homeSearchOpen, setHomeSearchOpen] = useState(false);
  const [homeSearchTerm, setHomeSearchTerm] = useState("");
  const [createdBySearchOpen, setCreatedBySearchOpen] = useState(false);
  const [createdBySearchTerm, setCreatedBySearchTerm] = useState("");
  // Template filter state for AuditsPage
  const [templateFilter, setTemplateFilter] = useState<string[]>(parseArrayParam(auditUrlParams.get('templates')));
  const [templateSearchOpen, setTemplateSearchOpen] = useState(false);
  const [templateSearchTerm, setTemplateSearchTerm] = useState("");

  // Persist audits filters to URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const setOrDelete = (key: string, value: string | null) => {
      if (value === null) params.delete(key);
      else params.set(key, value);
    };
    setOrDelete('status', serializeArrayParam(statusFilter));
    setOrDelete('createdBy', createdByFilter === 'all' ? null : createdByFilter);
    setOrDelete('types', serializeArrayParam(typeFilter));
  setOrDelete('templates', serializeArrayParam(templateFilter));
    setOrDelete('categories', serializeArrayParam(categoryFilter));
    setOrDelete('orgs', serializeArrayParam(orgFilter));
    setOrDelete('homes', serializeArrayParam(homeFilter));
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
  }, [statusFilter, createdByFilter, typeFilter, categoryFilter, orgFilter, homeFilter, templateFilter]);
  
  const isOrgManager = user?.role && ['OrgManager', 'OrgAdmin'].includes(user.role);
  const isPlatformAdmin = user?.role && ['Developer', 'PlatformManager'].includes(user.role);

  // Approve/Reject modal state
  const [approvingAuditId, setApprovingAuditId] = useState<number | null>(null);
  const [rejectingAuditId, setRejectingAuditId] = useState<number | null>(null);
  const [rejectionNotes, setRejectionNotes] = useState<string>("");
  
  // Fetch template types for filter
  const { data: typesData } = useQuery({
    queryKey: ["/api/template-types"],
    queryFn: () => apiRequest("GET", "/api/template-types").then(res => res.json()),
  });
  const types = typesData || [];

  // Fetch templates for the template filter options
  const { data: templatesForFilterResp } = useQuery({
    queryKey: ["/api/templates", "audits-filter"],
    queryFn: () => apiRequest("GET", "/api/templates").then(res => res.json()),
  });
  const templatesForFilter = toArray(templatesForFilterResp);
  
  // Fetch template categories for filter
  const { data: categoriesData } = useQuery({
    queryKey: ["/api/template-categories"],
    queryFn: () => apiRequest("GET", "/api/template-categories").then(res => res.json()),
    enabled: Boolean(isPlatformAdmin),
  });
  const categories = categoriesData || [];
  
  // Fetch organizations for platform admin filter
  const { data: orgsData } = useQuery({
    queryKey: ["/api/organizations"],
    queryFn: () => apiRequest("GET", "/api/organizations").then(res => res.json()),
    enabled: Boolean(isPlatformAdmin),
  });
  const orgs = Array.isArray(orgsData) ? orgsData : (orgsData?.items || []);
  
  // Fetch homes for OrgManager or Platform Admin filter
  const { data: homesData } = useQuery({
    queryKey: ["/api/homes", orgFilter],
    queryFn: () => {
      const params = new URLSearchParams();
      if (isPlatformAdmin && orgFilter && orgFilter.length > 0 && !orgFilter.includes('all')) {
        if (orgFilter.includes('standalone')) {
          params.set('organizationId', 'null');
        } else {
          params.set('organizationId', orgFilter[0].toString());
        }
      }
      const url = params.toString() ? `/api/homes?${params}` : '/api/homes';
      return apiRequest("GET", url).then(res => res.json());
    },
    enabled: Boolean(isOrgManager || isPlatformAdmin),
  });
  // Handle both legacy array response and new paginated response
  const homes = Array.isArray(homesData) ? homesData : (homesData?.items || []);

  // Query parameters based on user role
  const getQueryParams = () => {
    const params = new URLSearchParams();
    
    if (statusFilter && statusFilter.length > 0 && !statusFilter.includes('all')) {
      params.set('status', serializeArrayParam(statusFilter));
    }
    
    if (createdByFilter === 'me') {
      params.set('createdBy', 'me');
    }
    if (templateFilter && templateFilter.length > 0 && !templateFilter.includes('all')) {
      params.set('templateIds', serializeArrayParam(templateFilter));
    }
    
    // Organization filter for Platform Admin
    if (isPlatformAdmin && orgFilter && orgFilter.length > 0 && !orgFilter.includes('all')) {
      if (orgFilter.includes('standalone')) {
        params.set('organizationId', 'null');
      } else {
        // use first selected organization for server-side scope param
        params.set('organizationId', String(orgFilter[0]));
      }
    }
    
    // Home filter for OrgManagers and Platform Admin
    if (homeFilter && homeFilter.length > 0 && !homeFilter.includes('all')) {
      params.set('homeId', String(homeFilter[0]));
    }
    
    // For org-level roles, use organization scope (unless specific home selected)
    if (user?.role && ['OrgAdmin', 'OrgManager'].includes(user.role) && (!homeFilter || homeFilter.length === 0 || homeFilter.includes('all'))) {
      params.set('orgScope', 'true');
    }
    
    return params.toString();
  };

  // Fetch audits with role-based filtering
  const { data: auditResponse, isLoading } = useQuery({
    queryKey: ["/api/audits", statusFilter, createdByFilter, orgFilter, homeFilter, templateFilter, user?.role],
    queryFn: () => {
      const queryParams = getQueryParams();
      const url = queryParams ? `/api/audits?${queryParams}` : '/api/audits';
      return apiRequest("GET", url).then(res => res.json());
    },
    enabled: !!user,
  });

  // Handle both legacy array response and new paginated response
  const audits = Array.isArray(auditResponse) ? auditResponse : (auditResponse?.items || []);
  const totalAudits = Array.isArray(auditResponse) ? auditResponse.length : (auditResponse?.total || 0);

  // Modal state for approval blocked due to open actions
  const [actionsBlockForAuditId, setActionsBlockForAuditId] = useState<number | null>(null);
  const parseActionsOpen = (err: any): boolean => {
    try {
      // If apiRequest attached parsed error body, check it first
      const apiErr = err?.apiError || err?.body || err?.error || null;
      if (apiErr && apiErr.code) return apiErr.code === 'ACTIONS_OPEN';

      const msg: string = err?.message || '';
      const colon = msg.indexOf(':');
      const jsonText = colon >= 0 ? msg.slice(colon + 1).trim() : '';
      const parsed = jsonText.startsWith('{') ? JSON.parse(jsonText) : null;
      return !!(parsed && parsed.code === 'ACTIONS_OPEN');
    } catch {
      return false;
    }
  };

  // Approve audit mutation
  const approveMutation = useMutation<Response, any, number>({
    mutationFn: (auditId) => 
      apiRequest("POST", `/api/audits/${auditId}/approve`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/audits"] });
      toast({
        title: "Success",
        description: "Audit approved successfully",
      });
    },
    onError: (error: any) => {
      // If server returned ACTIONS_OPEN we handle a friendly modal elsewhere
      // and should not show the generic destructive toast to avoid duplicate UI.
      try {
        if (parseActionsOpen(error)) return;
      } catch {}
      // Fallback toast for other errors
      toast({ title: "Error", description: error.message || "Failed to approve audit", variant: "destructive" });
    },
  });

  // Reject audit mutation
  const rejectMutation = useMutation({
    mutationFn: ({ auditId, rejection_notes }: { auditId: number; rejection_notes: string }) => 
      apiRequest("POST", `/api/audits/${auditId}/reject`, { rejection_notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/audits"] });
      toast({
        title: "Success",
        description: "Audit rejected successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to reject audit", 
        variant: "destructive",
      });
    },
  });

  // Delete audit mutation
  const deleteMutation = useMutation({
    mutationFn: (auditId: number) => 
      apiRequest("DELETE", `/api/audits/${auditId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/audits"] });
      toast({
        title: "Success",
        description: "Audit deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete audit", 
        variant: "destructive",
      });
    },
  });
  
  // Send reminder mutation
  const reminderMutation = useMutation({
    mutationFn: (auditId: number) => 
      apiRequest("POST", `/api/audits/${auditId}/reminder`),
    onSuccess: () => {
      toast({
        title: "Reminder Sent",
        description: "A reminder has been sent to the assigned user",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to send reminder",
        variant: "destructive",
      });
    },
  });

  // Approve modal submit
  const submitApprove = () => {
    if (approvingAuditId == null) return;
    approveMutation.mutate(approvingAuditId, {
      onError: (err) => {
        if (parseActionsOpen(err)) setActionsBlockForAuditId(approvingAuditId);
      },
      onSettled: () => setApprovingAuditId(null),
    });
  };

  // Reject modal submit
  const submitReject = () => {
    if (rejectingAuditId == null) return;
    if (!rejectionNotes.trim()) {
      toast({ title: 'Validation', description: 'Please enter rejection notes', variant: 'destructive' });
      return;
    }
    rejectMutation.mutate({ auditId: rejectingAuditId, rejection_notes: rejectionNotes }, {
      onSettled: () => {
        setRejectingAuditId(null);
        setRejectionNotes("");
      }
    });
  };

  // Render modals: Approve confirm and Reject dialog
  // Approve confirmation uses ConfirmModal
  
  // ...later in JSX return, add modals near other confirmation modals


  // Filter audits by search term, type, and category (client-side filtering)
  const filteredAudits = audits.filter((audit: Audit) => {
    const name = (audit.name || '').toString();
    const desc = (audit.description || '').toString();
    const matchesSearch = name.toLowerCase().includes((searchTerm || '').toLowerCase()) ||
      desc.toLowerCase().includes((searchTerm || '').toLowerCase());
    const matchesType = (!typeFilter || typeFilter.length === 0 || typeFilter.includes('all')) || (audit.type_id !== undefined && typeFilter.includes(String(audit.type_id)));
    const matchesCategory = (!categoryFilter || categoryFilter.length === 0 || categoryFilter.includes('all')) || (audit.category_id !== undefined && categoryFilter.includes(String(audit.category_id)));
    const matchesTemplate = (!templateFilter || templateFilter.length === 0 || templateFilter.includes('all')) || (audit.template_id !== undefined && templateFilter.includes(String(audit.template_id)));
    return matchesSearch && matchesType && matchesCategory && matchesTemplate;
  });

  const handleApprove = (auditId: number) => {
    if (!canWrite) {
      toast(showWriteBlockedMessage());
      return;
    }
    // Open confirmation modal instead of directly mutating
    setApprovingAuditId(auditId);
  };

  const handleReject = (auditId: number) => {
    if (!canWrite) {
      toast(showWriteBlockedMessage());
      return;
    }
    // Open reject dialog to collect rejection notes
    setRejectingAuditId(auditId);
  };

  const handleDelete = (auditId: number, auditName: string) => {
    if (!canWrite) {
      toast(showWriteBlockedMessage());
      return;
    }
    
    if (confirm(`Are you sure you want to delete "${auditName}"? This action cannot be undone.`)) {
      deleteMutation.mutate(auditId);
    }
  };
  
  const handleSendReminder = (auditId: number) => {
    if (!canWrite) {
      toast(showWriteBlockedMessage());
      return;
    }
    
    reminderMutation.mutate(auditId);
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'Draft': return 'secondary';
      case 'Submitted': return 'default';
      case 'Approved': return 'default';
      default: return 'outline';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'urgent': return 'text-red-600';
      case 'high': return 'text-orange-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };
  
  const getHomeName = (homeId?: number) => {
    if (!homeId) return 'N/A';
    const home = homes.find((h: any) => h.id === homeId);
    return home?.name || 'Unknown';
  };

  const getRoleBasedEmptyMessage = () => {
    switch (user?.role) {
      case 'HomeStaff':
        return {
          title: "No audits found",
          description: "You haven't created any audits yet. Start your first audit to get going!"
        };
      case 'HomeAdmin':
      case 'HomeManager':
        return {
          title: "No audits in this home",
          description: "No audits have been created for this care home yet."
        };
      case 'OrgAdmin':
      case 'OrgManager':
        return {
          title: "No audits in your organization",
          description: "No audits have been created in your organization yet."
        };
      default:
        return {
          title: "No audits found",
          description: "No audits match your current filters."
        };
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="page-title text-[26px] font-bold">Audits</h1>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-gray-200 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ResponsiveBreadcrumb items={breadcrumbs.audits()} className="mb-4" />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title text-[26px] font-bold text-foreground" data-testid="page-title">
            Audits
          </h1>
          <p className="text-muted-foreground mt-1 text-[14px]">
            {user?.role === 'HomeStaff' && "View and manage your audits"}
            {['HomeAdmin', 'HomeManager'].includes(user?.role || '') && "Manage audits for your care home"}
            {['OrgAdmin', 'OrgManager'].includes(user?.role || '') && "Manage audits across your organization"}
            {['Developer', 'PlatformManager'].includes(user?.role || '') && "System-wide audit management"}
          </p>
        </div>
        {canCreateAudits && (
          <Button 
            onClick={() => !canWrite ? toast(showWriteBlockedMessage()) : window.location.href = '/audits/new'}
            disabled={!canWrite}
            data-testid="button-create-audit"
            title={!canWrite ? "Create disabled during impersonation" : "Create new audit"}
          >
            <Plus className="w-4 h-4 mr-2" />
            New Audit
          </Button>
        )}
      </div>
      {/* Search and Filters */}
      <div className="flex flex-wrap gap-4 p-4 bg-card rounded-lg border">
        <div className="flex-1 min-w-[200px]">
          <Input
            placeholder="Search audits..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="search-audits"
          />
        </div>
        <div className="w-48">
          <Popover open={templateSearchOpen} onOpenChange={setTemplateSearchOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={templateSearchOpen}
                className="w-full justify-between"
                data-testid="filter-template"
              >
                {(!templateFilter || templateFilter.length === 0 || templateFilter.includes('all'))
                  ? 'All Templates'
                  : (Array.isArray(templateFilter) ? templateFilter.map((id: string) => templatesForFilter.find((t:any)=>String(t.id)===id)?.name || id).join(', ') : String(templateFilter))}
                <ArrowUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0" align="start">
              <Command>
                <CommandInput 
                  placeholder="Search templates..." 
                  value={templateSearchTerm}
                  onValueChange={setTemplateSearchTerm}
                />
                <CommandList>
                  <CommandEmpty>No templates found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value="all"
                      onSelect={() => {
                        setTemplateFilter(['all']);
                        setTemplateSearchOpen(false);
                        setTemplateSearchTerm("");
                      }}
                    >
                      <CheckCircle className={`mr-2 h-4 w-4 ${templateFilter.includes('all') ? 'opacity-100' : 'opacity-0'}`} />
                      All Templates
                    </CommandItem>
                    {templatesForFilter
                      .filter((t: any) => (t.name || '').toLowerCase().includes(templateSearchTerm.toLowerCase()))
                      .map((t: any) => (
                        <CommandItem
                          key={t.id}
                          value={t.name}
                          onSelect={() => {
                            setTemplateFilter([String(t.id)]);
                            setTemplateSearchOpen(false);
                            setTemplateSearchTerm("");
                          }}
                        >
                          <CheckCircle className={`mr-2 h-4 w-4 ${templateFilter.includes(String(t.id)) ? 'opacity-100' : 'opacity-0'}`} />
                          {t.name}
                        </CommandItem>
                      ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <div className="w-48">
          <Popover open={statusSearchOpen} onOpenChange={setStatusSearchOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={statusSearchOpen}
                className="w-full justify-between"
                data-testid="filter-status"
              >
                {(!statusFilter || statusFilter.length === 0 || statusFilter.includes('all')) ? 'All Status' : statusFilter.join(', ')}
                <ArrowUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-48 p-0" align="start">
              <Command>
                <CommandInput 
                  placeholder="Search status..." 
                  value={statusSearchTerm}
                  onValueChange={setStatusSearchTerm}
                />
                <CommandList>
                  <CommandEmpty>No status found.</CommandEmpty>
                  <CommandGroup>
                    {['all', 'Draft', 'Submitted', 'Approved'].filter((status) =>
                      status.toLowerCase().includes(statusSearchTerm.toLowerCase())
                    ).map((status) => (
                      <CommandItem
                        key={status}
                        value={status}
                        onSelect={() => {
                          setStatusFilter([status]);
                          setStatusSearchOpen(false);
                          setStatusSearchTerm("");
                        }}
                      >
                        <CheckCircle className={`mr-2 h-4 w-4 ${statusFilter.includes(status) ? 'opacity-100' : 'opacity-0'}`} />
                        {status === 'all' ? 'All Status' : status}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <div className="w-48">
          <Popover open={typeSearchOpen} onOpenChange={setTypeSearchOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={typeSearchOpen}
                className="w-full justify-between"
                data-testid="filter-type"
              >
                {(!typeFilter || typeFilter.length === 0 || typeFilter.includes('all'))
                  ? 'All Types'
                  : (Array.isArray(typeFilter) ? typeFilter.join(', ') : String(typeFilter))}
                <ArrowUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-48 p-0" align="start">
              <Command>
                <CommandInput 
                  placeholder="Search types..." 
                  value={typeSearchTerm}
                  onValueChange={setTypeSearchTerm}
                />
                <CommandList>
                  <CommandEmpty>No types found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value="all"
                      onSelect={() => {
                        setTypeFilter(['all']);
                        setTypeSearchOpen(false);
                        setTypeSearchTerm("");
                      }}
                    >
                      <CheckCircle className={`mr-2 h-4 w-4 ${typeFilter.includes('all') ? 'opacity-100' : 'opacity-0'}`} />
                      All Types
                    </CommandItem>
                    {types
                      .filter((type: any) => 
                        type.name.toLowerCase().includes(typeSearchTerm.toLowerCase())
                      )
                      .map((type: any) => (
                        <CommandItem
                          key={type.id}
                          value={type.name}
                          onSelect={() => {
                            setTypeFilter([String(type.id)]);
                            setTypeSearchOpen(false);
                            setTypeSearchTerm("");
                          }}
                        >
                          <CheckCircle className={`mr-2 h-4 w-4 ${typeFilter.includes(String(type.id)) ? 'opacity-100' : 'opacity-0'}`} />
                          {type.name}
                        </CommandItem>
                      ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        {isPlatformAdmin && (
          <>
            <div className="w-48">
              <Popover open={categorySearchOpen} onOpenChange={setCategorySearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={categorySearchOpen}
                    className="w-full justify-between"
                    data-testid="filter-category"
                  >
                    {(!categoryFilter || categoryFilter.length === 0 || categoryFilter.includes('all'))
                      ? 'All Categories'
                      : categoryFilter.join(', ')}
                    <ArrowUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-0" align="start">
                  <Command>
                    <CommandInput 
                      placeholder="Search categories..." 
                      value={categorySearchTerm}
                      onValueChange={setCategorySearchTerm}
                    />
                    <CommandList>
                      <CommandEmpty>No categories found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="all"
                          onSelect={() => {
                            setCategoryFilter(['all']);
                            setCategorySearchOpen(false);
                            setCategorySearchTerm("");
                          }}
                        >
                          <CheckCircle className={`mr-2 h-4 w-4 ${categoryFilter.includes('all') ? 'opacity-100' : 'opacity-0'}`} />
                          All Categories
                        </CommandItem>
                        {categories
                          .filter((category: any) => 
                            category.name.toLowerCase().includes(categorySearchTerm.toLowerCase())
                          )
                          .map((category: any) => (
                            <CommandItem
                              key={category.id}
                              value={category.name}
                              onSelect={() => {
                                setCategoryFilter([String(category.id)]);
                                setCategorySearchOpen(false);
                                setCategorySearchTerm("");
                              }}
                            >
                              <CheckCircle className={`mr-2 h-4 w-4 ${categoryFilter.includes(String(category.id)) ? 'opacity-100' : 'opacity-0'}`} />
                              {category.name}
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="w-64">
              <Popover open={orgSearchOpen} onOpenChange={setOrgSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={orgSearchOpen}
                    className="w-full justify-between"
                    data-testid="filter-organization"
                  >
                    {(!orgFilter || orgFilter.length === 0 || orgFilter.includes('all'))
                      ? 'All Organizations'
                      : (orgFilter.includes('standalone') ? 'Standalone Homes' : orgs.find((org: any) => org.id === orgFilter[0])?.name || 'Select...')}
                    <ArrowUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-0" align="start">
                  <Command>
                    <CommandInput 
                      placeholder="Search organizations..." 
                      value={orgSearchTerm}
                      onValueChange={setOrgSearchTerm}
                    />
                    <CommandList>
                      <CommandEmpty>No organizations found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="all"
                          onSelect={() => {
                              setOrgFilter(['all']);
                              setHomeFilter(['all']);
                              setOrgSearchOpen(false);
                              setOrgSearchTerm("");
                            }}
                        >
                            <CheckCircle className={`mr-2 h-4 w-4 ${orgFilter.includes('all') ? 'opacity-100' : 'opacity-0'}`} />
                          All Organizations
                        </CommandItem>
                        <CommandItem
                          value="standalone"
                          onSelect={() => {
                            setOrgFilter(['standalone']);
                            setHomeFilter(['all']);
                            setOrgSearchOpen(false);
                            setOrgSearchTerm("");
                          }}
                        >
                          <CheckCircle className={`mr-2 h-4 w-4 ${orgFilter.includes('standalone') ? 'opacity-100' : 'opacity-0'}`} />
                          Standalone Homes
                        </CommandItem>
                        {orgs
                          .filter((org: any) => 
                            org.name.toLowerCase().includes(orgSearchTerm.toLowerCase())
                          )
                          .map((org: any) => (
                            <CommandItem
                              key={org.id}
                              value={org.name}
                              onSelect={() => {
                                setOrgFilter([String(org.id)]);
                                setHomeFilter(['all']);
                                setOrgSearchOpen(false);
                                setOrgSearchTerm("");
                              }}
                            >
                              <CheckCircle className={`mr-2 h-4 w-4 ${orgFilter.includes(String(org.id)) ? 'opacity-100' : 'opacity-0'}`} />
                              {org.name}
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="w-48">
              <Popover open={homeSearchOpen} onOpenChange={setHomeSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={homeSearchOpen}
                    className="w-full justify-between"
                    data-testid="filter-home"
                    disabled={!orgFilter || orgFilter.includes('all')}
                  >
                    {(!homeFilter || homeFilter.length === 0 || homeFilter.includes('all'))
                      ? 'All Homes'
                      : homeFilter.join(', ')}
                    <ArrowUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-0" align="start">
                  <Command>
                    <CommandInput 
                      placeholder="Search homes..." 
                      value={homeSearchTerm}
                      onValueChange={setHomeSearchTerm}
                    />
                    <CommandList>
                      <CommandEmpty>No homes found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="all"
                          onSelect={() => {
                            setHomeFilter(['all']);
                            setHomeSearchOpen(false);
                            setHomeSearchTerm("");
                          }}
                        >
                          <CheckCircle className={`mr-2 h-4 w-4 ${homeFilter.includes('all') ? 'opacity-100' : 'opacity-0'}`} />
                          All Homes
                        </CommandItem>
                        {homes
                          .filter((home: any) => 
                            home.name.toLowerCase().includes(homeSearchTerm.toLowerCase())
                          )
                          .map((home: any) => (
                            <CommandItem
                              key={home.id}
                              value={home.name}
                              onSelect={() => {
                                setHomeFilter([String(home.id)]);
                                setHomeSearchOpen(false);
                                setHomeSearchTerm("");
                              }}
                            >
                              <CheckCircle className={`mr-2 h-4 w-4 ${homeFilter.includes(String(home.id)) ? 'opacity-100' : 'opacity-0'}`} />
                              {home.name}
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </>
        )}
        {isOrgManager && (
          <div className="w-48">
            <Popover open={homeSearchOpen} onOpenChange={setHomeSearchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={homeSearchOpen}
                  className="w-full justify-between"
                  data-testid="filter-home"
                >
                  {(!homeFilter || homeFilter.length === 0 || homeFilter.includes('all')) 
                    ? 'All Homes' 
                    : homes.find((h: any) => h.id === parseInt(String(homeFilter[0]), 10))?.name || 'Select...'}
                  <ArrowUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-0" align="start">
                <Command>
                  <CommandInput 
                    placeholder="Search homes..." 
                    value={homeSearchTerm}
                    onValueChange={setHomeSearchTerm}
                  />
                  <CommandList>
                    <CommandEmpty>No homes found.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="all"
                        onSelect={() => {
                          setHomeFilter(['all']);
                          setHomeSearchOpen(false);
                          setHomeSearchTerm("");
                        }}
                      >
                        <CheckCircle className={`mr-2 h-4 w-4 ${homeFilter && homeFilter.includes('all') ? 'opacity-100' : 'opacity-0'}`} />
                        All Homes
                      </CommandItem>
                      {homes
                        .filter((home: any) => 
                          home.name.toLowerCase().includes(homeSearchTerm.toLowerCase())
                        )
                        .map((home: any) => (
                          <CommandItem
                            key={home.id}
                            value={home.name}
                            onSelect={() => {
                              setHomeFilter([String(home.id)]);
                              setHomeSearchOpen(false);
                              setHomeSearchTerm("");
                            }}
                          >
                            <CheckCircle className={`mr-2 h-4 w-4 ${homeFilter && homeFilter.includes(String(home.id)) ? 'opacity-100' : 'opacity-0'}`} />
                            {home.name}
                          </CommandItem>
                        ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        )}
        {user?.role !== 'HomeStaff' && (
          <div className="w-48">
            <Popover open={createdBySearchOpen} onOpenChange={setCreatedBySearchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={createdBySearchOpen}
                  className="w-full justify-between"
                  data-testid="filter-created-by"
                >
                  {createdByFilter === 'all' ? 'All Audits' : 'My Audits'}
                  <ArrowUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-0" align="start">
                <Command>
                  <CommandInput 
                    placeholder="Search..." 
                    value={createdBySearchTerm}
                    onValueChange={setCreatedBySearchTerm}
                  />
                  <CommandList>
                    <CommandEmpty>No options found.</CommandEmpty>
                    <CommandGroup>
                      {['all', 'me'].filter((option) =>
                        (option === 'all' ? 'All Audits' : 'My Audits')
                          .toLowerCase()
                          .includes(createdBySearchTerm.toLowerCase())
                      ).map((option) => (
                        <CommandItem
                          key={option}
                          value={option}
                          onSelect={() => {
                            setCreatedByFilter(option as typeof createdByFilter);
                            setCreatedBySearchOpen(false);
                            setCreatedBySearchTerm("");
                          }}
                        >
                          <CheckCircle className={`mr-2 h-4 w-4 ${createdByFilter === option ? 'opacity-100' : 'opacity-0'}`} />
                          {option === 'all' ? 'All Audits' : 'My Audits'}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>
      {/* Audits Table */}
      {filteredAudits.length === 0 ? (
        <Card className="p-8">
          <div className="text-center space-y-4" data-testid="empty-state">
            <ClipboardCheck className="w-16 h-16 text-gray-400 mx-auto" />
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {getRoleBasedEmptyMessage().title}
              </h3>
              <p className="text-gray-600">
                {getRoleBasedEmptyMessage().description}
              </p>
            </div>
            {canCreateAudits && user?.role === 'HomeStaff' && (
              <Button 
                onClick={() => !canWrite ? toast(showWriteBlockedMessage()) : window.location.href = '/audits/new'}
                disabled={!canWrite}
                data-testid="create-first-audit"
                title={!canWrite ? "Create disabled during impersonation" : "Create first audit"}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create First Audit
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Score</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Type</TableHead>
                {isOrgManager && <TableHead>Home</TableHead>}
                <TableHead>Created By</TableHead>
                <TableHead>Assigned To</TableHead>
                <TableHead>Created</TableHead>
                {canApproveAudits && <TableHead className="w-[200px]">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAudits.map((audit: Audit) => (
                <TableRow key={audit.id} data-testid={`row-audit-${audit.id}`}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{audit.name}</div>
                      {audit.description && (
                        <div className="text-sm text-gray-500 line-clamp-1">{audit.description}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={getStatusBadgeVariant(audit.status)} data-testid={`badge-status-${audit.id}`}>
                      {audit.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {audit.score !== null && audit.score !== undefined ? (
                      <span 
                        className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getScoreStatus(audit.score).bgClass} ${getScoreStatus(audit.score).colorClass}`}
                        data-testid={`badge-score-${audit.id}`}
                      >
                        {getScoreStatus(audit.score).emoji} {audit.score}%
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">N/A</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={`text-sm font-medium ${getPriorityColor(audit.priority)}`}>
                      {audit.priority}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {audit.type_name || 'N/A'}
                  </TableCell>
                  {isOrgManager && (
                    <TableCell className="text-sm text-gray-600">
                      {getHomeName(audit.home_id)}
                    </TableCell>
                  )}
                  <TableCell className="text-sm text-gray-600">
                    {(audit as any).creator_name || 'Unknown'}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {(audit as any).assignee_name || 'Unassigned'}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {audit.created_at ? new Date(audit.created_at).toLocaleDateString() : 'Unknown'}
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      {audit.status === 'Draft' ? (
                        <>
                          {(() => {
                            const isAssignee = typeof audit.assigned_to === 'number' ? audit.assigned_to === user?.id : false;
                            const isCreator = audit.created_by === user?.id;
                            const canContinue = isAssignee || (!audit.assigned_to && isCreator);
                            if (!canContinue) {
                              const isManager = user?.role && ['OrgManager','HomeManager','OrgAdmin','HomeAdmin','Developer','PlatformManager'].includes(user.role);
                              if (isManager) {
                                return (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setLocation(`/audits/view?auditId=${audit.id}`)}
                                    title="Opens read-only view with takeover options."
                                    data-testid={`button-view-readonly-${audit.id}`}
                                  >
                                    <Eye className="w-4 h-4 mr-1" />
                                    View
                                  </Button>
                                );
                              }
                              return (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled
                                  title="Only the assigned user (or the creator if unassigned) can continue this draft"
                                  data-testid={`button-continue-${audit.id}`}
                                >
                                  <Edit className="w-4 h-4 mr-1" />
                                  Continue
                                </Button>
                              );
                            }
                            return (
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => setLocation(`/audits/take?auditId=${audit.id}`)}
                                data-testid={`button-continue-${audit.id}`}
                              >
                                <Edit className="w-4 h-4 mr-1" />
                                Continue
                              </Button>
                            );
                          })()}
                          {canSendReminders && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSendReminder(audit.id)}
                              disabled={reminderMutation.isPending || !canWrite}
                              data-testid={`button-reminder-${audit.id}`}
                              title={!canWrite ? "Reminder disabled during impersonation" : "Send reminder"}
                            >
                              <Bell className="w-4 h-4" />
                            </Button>
                          )}
                          {canDeleteAudits && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(audit.id, audit.name)}
                              disabled={deleteMutation.isPending || !canWrite}
                              data-testid={`button-delete-${audit.id}`}
                              title={!canWrite ? "Delete disabled during impersonation" : "Delete audit"}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          )}
                        </>
                      ) : audit.status === 'Approved' ? (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => window.location.href = `/audits/view?auditId=${audit.id}`}
                          data-testid={`button-view-completed-${audit.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View & Export
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.location.href = `/audits/view?auditId=${audit.id}`}
                          data-testid={`button-review-${audit.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Review
                        </Button>
                      )}
                      {audit.status === 'Submitted' && (
                        <>
                          {canSendReminders && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSendReminder(audit.id)}
                              disabled={reminderMutation.isPending || !canWrite}
                              data-testid={`button-reminder-${audit.id}`}
                              title={!canWrite ? "Reminder disabled during impersonation" : "Send reminder"}
                            >
                              <Bell className="w-4 h-4" />
                            </Button>
                          )}
                          {canApproveAudits && (
                            <>
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => {
                                  if (!canWrite) { toast(showWriteBlockedMessage()); return; }
                                  approveMutation.mutate(audit.id, {
                                    onError: (err) => {
                                      if (parseActionsOpen(err)) setActionsBlockForAuditId(audit.id);
                                    }
                                  });
                                }}
                                disabled={approveMutation.isPending || !canWrite}
                                data-testid={`button-approve-${audit.id}`}
                                title={!canWrite ? "Approve disabled during impersonation" : "Approve audit"}
                              >
                                ✓ Approve
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => {
                                  if (!canWrite) { toast(showWriteBlockedMessage()); return; }
                                  const notes = prompt('Enter rejection notes (required):') || '';
                                  if (!notes.trim()) { toast({ title: 'Validation', description: 'Rejection notes are required', variant: 'destructive' }); return; }
                                  rejectMutation.mutate({ auditId: audit.id, rejection_notes: notes });
                                }}
                                disabled={rejectMutation.isPending || !canWrite}
                                data-testid={`button-reject-${audit.id}`}
                                title={!canWrite ? "Reject disabled during impersonation" : "Reject audit"}
                              >
                                ✗ Reject
                              </Button>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Modal: approval blocked due to open actions */}
      {actionsBlockForAuditId !== null && (
        <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setActionsBlockForAuditId(null)} />
          <div className="relative z-10 w-full max-w-md rounded-lg border bg-card p-6 shadow-lg">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-500 mt-[2px]" />
              <div>
                <h3 className="text-lg font-semibold">Actions need attention</h3>
                <p className="text-sm text-muted-foreground mt-1">This audit has open actions (Pending or In Progress). Please complete all actions before approving.</p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setActionsBlockForAuditId(null)}>Close</Button>
              <Button onClick={() => setLocation(`/actions?auditId=${actionsBlockForAuditId}`)} data-testid="modal-go-to-actions">
                View Actions
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Audit Review Page
function AuditReviewPage({ params }: { params: { id?: string } }) {
  const auditId = params.id;
  const breadcrumbs = useBreadcrumbs();
  
  // Fetch real audit data
  const { data: audit, isLoading } = useQuery<Audit>({
    queryKey: ['/api/audits', auditId],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/audits/${auditId}`);
      return response.json();
    },
    enabled: !!auditId,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-gray-200 rounded animate-pulse w-64" />
        <div className="h-64 bg-gray-200 rounded animate-pulse" />
      </div>
    );
  }

  if (!audit) {
    return (
      <div className="text-center py-12">
        <p className="text-lg text-gray-600">Audit not found</p>
      </div>
    );
  }

  // Prefer server-provided score when present, otherwise fall back to any completion percentage
  const _hasBackendScore = audit.score !== null && audit.score !== undefined;
  const _rawDisplayScore = _hasBackendScore ? audit.score : (audit as any).completionPercentage ?? null;
  const displayScore = _rawDisplayScore != null ? Number(_rawDisplayScore) : null;
  const displayLabel = _hasBackendScore ? 'Score' : 'Completion';

  return (
    <div className="space-y-6">
      <ResponsiveBreadcrumb 
        items={breadcrumbs.auditDetail(audit.name)} 
        className="mb-4" 
      />
      
      <PageHeader 
        title={audit.name}
        subtitle={`${audit.status}`}
        customBackText="Back to Audits"
      />

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Audit Details */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Audit Information
                <Badge variant="outline">{audit.status}</Badge>
              </CardTitle>
              {audit.description && (
                <p className="text-gray-600">{audit.description}</p>
              )}
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label>Audit Type</Label>
                  <p className="text-sm font-medium mt-1 capitalize">{audit.type_name || 'N/A'}</p>
                </div>
                
                <div>
                  <Label>Priority</Label>
                  <div className="mt-1">
                    <span className={`text-sm font-medium ${
                      audit.priority === 'urgent' ? 'text-red-600' :
                      audit.priority === 'high' ? 'text-orange-600' :
                      audit.priority === 'medium' ? 'text-yellow-600' :
                      'text-green-600'
                    }`}>
                      {audit.priority}
                    </span>
                  </div>
                </div>

                <div>
                  <Label>{displayLabel}</Label>
                  <div className="mt-2">
                    {displayScore != null ? (
                      <span
                        data-testid="audit-score-value"
                        className={`inline-flex items-center px-3 py-2 rounded text-sm font-medium ${getScoreStatus(displayScore).bgClass} ${getScoreStatus(displayScore).colorClass}`}
                      >
                        {getScoreStatus(displayScore).emoji} {(_hasBackendScore ? displayScore.toFixed(1) : Math.round(displayScore))}%{_hasBackendScore ? ` - ${getScoreStatus(displayScore).label}` : ''}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">N/A</span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Audit Details Sidebar */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle>Timeline</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {audit.created_at && (
                <div>
                  <Label>Created</Label>
                  <p className="text-sm font-medium mt-1">
                    {new Date(audit.created_at).toLocaleDateString()}
                  </p>
                </div>
              )}
              
              {audit.due_date && (
                <div>
                  <Label>Due Date</Label>
                  <p className="text-sm font-medium mt-1">
                    {new Date(audit.due_date).toLocaleDateString()}
                  </p>
                </div>
              )}

              <div className="pt-4 border-t">
                <Button className="w-full" data-testid="button-view-audit" onClick={() => window.location.href = `/audits`}>
                  <ClipboardCheck className="w-4 h-4 mr-2" />
                  Back to Audits
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

// Organization form schema
const organizationSchema = z.object({
  name: z.string().min(1, "Organization name is required"),
  billing_email: z.string(),
  address: z.string().optional(),
  contact_number: z.string().optional(),
  status: z.enum(["Active", "Suspended"]),
});

type OrganizationFormData = z.infer<typeof organizationSchema>;

// Organizations Page - Developer and Platform Manager Only
function OrganizationsPage() {
  const { session } = useSession();
  const user = session?.user;
  const { canWrite, showWriteBlockedMessage } = useWriteGuard();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const breadcrumbs = useBreadcrumbs();
  
  // Get effective user - session user takes precedence (for impersonation)
  const effectiveUser = session?.user ?? user;
  
  // Check for proper role-based permissions
  const canViewOrganizations = effectiveUser?.role === 'Developer' || effectiveUser?.role === 'PlatformManager';
  const canCreateOrganizations = effectiveUser?.role === 'Developer' || effectiveUser?.role === 'PlatformManager';
  
  if (!canViewOrganizations) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [deletingOrg, setDeletingOrg] = useState<Organization | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Homes import state
  const [isHomesImportOpen, setIsHomesImportOpen] = useState(false);
  const [homesSelectedOrg, setHomesSelectedOrg] = useState<number | null>(null);
  const [homesImportFile, setHomesImportFile] = useState<File | null>(null);
  const [homesImportPreview, setHomesImportPreview] = useState<any[]>([]);
  
  // Users import state
  const [isUsersImportOpen, setIsUsersImportOpen] = useState(false);
  const [usersSelectedOrg, setUsersSelectedOrg] = useState<number | null>(null);
  const [usersImportFile, setUsersImportFile] = useState<File | null>(null);
  const [usersImportPreview, setUsersImportPreview] = useState<any[]>([]);

  // CSV parsing functions with Zod validation
  const parseHomesCSV = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        toast({
          title: "Invalid CSV",
          description: "CSV file must have headers and at least one data row",
          variant: "destructive"
        });
        return;
      }
      
      const headers = lines[0].split(',').map(h => h.trim());
      const data = lines.slice(1).map((line, lineIndex) => {
        const values = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
        const row: any = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        
        // Validate with Zod
        const validation = csvHomeImportRowSchema.safeParse(row);
        return {
          ...row,
          _valid: validation.success,
          _errors: validation.success ? null : validation.error.errors.map(e => `${e.path.join('.')}: ${e.message}`).join(', '),
          _rowNumber: lineIndex + 2 // +2 because we skip header and 0-index
        };
      });
      
      setHomesImportPreview(data);
    };
    reader.readAsText(file);
  };

  const parseUsersCSV = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        toast({
          title: "Invalid CSV",
          description: "CSV file must have headers and at least one data row",
          variant: "destructive"
        });
        return;
      }
      
      const headers = lines[0].split(',').map(h => h.trim());
      const data = lines.slice(1).map((line, lineIndex) => {
        const values = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
        const row: any = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        
        // Validate with Zod
        const validation = csvUserImportRowSchema.safeParse(row);
        return {
          ...row,
          _valid: validation.success,
          _errors: validation.success ? null : validation.error.errors.map(e => `${e.path.join('.')}: ${e.message}`).join(', '),
          _rowNumber: lineIndex + 2 // +2 because we skip header and 0-index
        };
      });
      
      setUsersImportPreview(data);
    };
    reader.readAsText(file);
  };

  // Queries - use default queryFn which handles normalization
  const { data: organizationsResp, isLoading } = useQuery<Organization[]>({ 
    queryKey: ["/api/organizations"]
  });
  
  const { data: homesResp } = useQuery<Home[]>({ 
    queryKey: ["/api/homes"]
  });
  
  const { data: usersResp } = useQuery<User[]>({ 
    queryKey: ["/api/users"]
  });
  
  // Normalize responses (handle both array and {total, items} formats)
  const organizations = Array.isArray(organizationsResp) 
    ? organizationsResp 
    : (organizationsResp as any)?.items || [];
  const homes = Array.isArray(homesResp) 
    ? homesResp 
    : (homesResp as any)?.items || [];
  const users = Array.isArray(usersResp) 
    ? usersResp 
    : (usersResp as any)?.items || [];

  // Form handling
  const form = useForm<OrganizationFormData>({
    resolver: zodResolver(organizationSchema),
    defaultValues: {
      name: "",
      billing_email: "",
      status: "Active",
    },
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: OrganizationFormData) => 
      apiRequest("POST", "/api/organizations", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organizations"] });
      setIsCreateModalOpen(false);
      form.reset();
      toast({
        title: "Success",
        description: "Organization created successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create organization",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: OrganizationFormData }) =>
      apiRequest("PUT", `/api/organizations/${id}`, data),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ["/api/organizations"] });
      setEditingOrg(null);
      form.reset();
      toast({
        title: "Success",
        description: "Organization updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update organization",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/organizations/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organizations"] });
      setDeletingOrg(null);
      toast({
        title: "Success",
        description: "Organization deleted successfully",
      });
    },
    onError: (error: any) => {
      const message = error.code === 'ORG_IN_USE' 
        ? "Cannot delete organization with existing homes or users"
        : error.message || "Failed to delete organization";
      
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    },
  });

  const importHomesMutation = useMutation({
    mutationFn: ({ organization_id, homes }: { organization_id: number; homes: any[] }) =>
      apiRequest("POST", "/api/import/homes", { organization_id, homes }),
    onSuccess: async (response: Response) => {
      const result = await response.json();
      await queryClient.invalidateQueries({ queryKey: ["/api/homes"] });
      
      setIsHomesImportOpen(false);
      setHomesSelectedOrg(null);
      setHomesImportFile(null);
      setHomesImportPreview([]);
      
      const hasErrors = result.errorRows && result.errorRows.length > 0;
      toast({
        title: hasErrors ? "Import Completed with Errors" : "Import Successful",
        description: `Created ${result.created} homes${result.skipped > 0 ? `, skipped ${result.skipped}` : ''}${hasErrors ? ` with ${result.errorRows.length} errors` : ''}`,
        variant: hasErrors ? "destructive" : "default",
      });
      
      if (hasErrors) {
        console.error("Import errors:", result.errorRows);
      }
    },
    onError: (error: any) => {
      toast({
        title: "Import Failed",
        description: error.message || "Failed to import homes",
        variant: "destructive",
      });
    },
  });

  const importUsersMutation = useMutation({
    mutationFn: ({ organization_id, users }: { organization_id: number; users: any[] }) =>
      apiRequest("POST", "/api/import/users", { organization_id, users }),
    onSuccess: async (response: Response) => {
      const result = await response.json();
      await queryClient.invalidateQueries({ queryKey: ["/api/users"] });
      
      setIsUsersImportOpen(false);
      setUsersImportFile(null);
      setUsersImportPreview([]);
      
      const hasErrors = result.errorRows && result.errorRows.length > 0;
      toast({
        title: hasErrors ? "Import Completed with Errors" : "Import Successful",
        description: `Created ${result.created} users${hasErrors ? ` with ${result.errorRows.length} errors` : ''}`,
        variant: hasErrors ? "destructive" : "default",
      });
      
      if (hasErrors) {
        console.error("Import errors:", result.errorRows);
      }
    },
    onError: (error: any) => {
      toast({
        title: "Import Failed",
        description: error.message || "Failed to import users",
        variant: "destructive",
      });
    },
  });


  // Handler functions
  const handleSubmit = (data: OrganizationFormData) => {
    if (editingOrg) {
      updateMutation.mutate({ id: editingOrg.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (org: Organization) => {
    setEditingOrg(org);
    form.reset({
      name: org.name,
      billing_email: org.billing_email,
      address: org.address || "",
      contact_number: org.contact_number || "",
      status: org.status as "Active" | "Suspended",
    });
  };

  const handleDelete = () => {
    if (deletingOrg) {
      deleteMutation.mutate(deletingOrg.id);
    }
  };

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
    setEditingOrg(null);
    form.reset();
  };

  // Filtered data
  const filteredOrgs = organizations.filter((org: Organization) =>
    org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.billing_email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Helper functions
  const getHomesCount = (orgId: number) => 
    homes.filter((home: Home) => home.organization_id === orgId).length;
  
  const getUsersCount = (orgId: number) => 
    users.filter((user: User) => user.organization_id === orgId).length;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="page-title text-[26px] font-bold">Organizations</h1>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-gray-200 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ResponsiveBreadcrumb items={breadcrumbs.adminOrganizations()} className="mb-4" />
      
      <div className="flex items-center justify-between">
  <h1 className="page-title text-[26px] font-bold text-foreground" data-testid="page-title">
          Organizations
        </h1>
        {canCreateOrganizations && (
          <Button 
            onClick={() => !canWrite ? toast(showWriteBlockedMessage()) : setIsCreateModalOpen(true)}
            disabled={!canWrite}
            data-testid="button-create-organization"
            title={!canWrite ? "Create disabled during impersonation" : "Create new organization"}
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Organization
          </Button>
        )}
      </div>

      {/* Search and Filter */}
      <div className="flex gap-4 p-4 bg-card rounded-lg border">
        <div className="flex-1">
          <Input
            placeholder="Search organizations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="search-organizations"
          />
        </div>
      </div>

      {/* Organizations Table */}
      {filteredOrgs.length === 0 ? (
        <Card className="p-8">
          <div className="text-center space-y-4" data-testid="empty-state">
            <Building className="w-16 h-16 text-gray-400 mx-auto" />
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm ? "No organizations found" : "No organizations yet"}
              </h3>
              <p className="text-gray-600">
                {searchTerm 
                  ? "Try adjusting your search criteria."
                  : "Create your first organization to get started."}
              </p>
            </div>
            {!searchTerm && canCreateOrganizations && (
              <Button 
                onClick={() => !canWrite ? toast(showWriteBlockedMessage()) : setIsCreateModalOpen(true)}
                disabled={!canWrite}
                data-testid="create-first-organization"
                title={!canWrite ? "Create disabled during impersonation" : "Create first organization"}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create First Organization
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Billing Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Homes</TableHead>
                <TableHead>Users</TableHead>
                <TableHead className="w-[120px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrgs.map((org: Organization) => (
                <TableRow key={org.id} data-testid={`row-organization-${org.id}`}>
                  <TableCell className="font-medium">{org.name}</TableCell>
                  <TableCell>{org.billing_email}</TableCell>
                  <TableCell>
                    <Badge 
                      variant={org.status === "Active" ? "default" : "secondary"}
                      data-testid={`status-${org.id}`}
                    >
                      {org.status}
                    </Badge>
                  </TableCell>
                  <TableCell data-testid={`homes-count-${org.id}`}>
                    {getHomesCount(org.id)}
                  </TableCell>
                  <TableCell data-testid={`users-count-${org.id}`}>
                    {getUsersCount(org.id)}
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => !canWrite ? toast(showWriteBlockedMessage()) : handleEdit(org)}
                        disabled={!canWrite}
                        data-testid={`button-edit-${org.id}`}
                        title={!canWrite ? "Edit disabled during impersonation" : "Edit organization"}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => !canWrite ? toast(showWriteBlockedMessage()) : setDeletingOrg(org)}
                        disabled={!canWrite}
                        data-testid={`button-delete-${org.id}`}
                        title={!canWrite ? "Delete disabled during impersonation" : "Delete organization"}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={isCreateModalOpen || editingOrg !== null} onOpenChange={handleCloseModal}>
        <DialogContent data-testid="organization-modal">
          <DialogHeader>
            <DialogTitle>
              {editingOrg ? "Edit Organization" : "Create Organization"}
            </DialogTitle>
            <DialogDescription>
              {editingOrg 
                ? "Update the organization details below."
                : "Enter the details for the new organization."}
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="Enter organization name" 
                        {...field} 
                        data-testid="input-org-name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                />

                <FormField
                control={form.control}
                name="billing_email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Billing Email</FormLabel>
                    <FormControl>
                      <Input 
                        type="email"
                        placeholder="billing@organization.com" 
                        {...field} 
                        data-testid="input-org-email"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                />

                <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="Head office address (optional)" 
                        {...field} 
                        data-testid="input-org-address"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                />

                <FormField
                control={form.control}
                name="contact_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contact Number</FormLabel>
                    <FormControl>
                      <Input 
                        type="tel"
                        placeholder="01234 567890 (optional)" 
                        {...field} 
                        data-testid="input-org-contact"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                />

                <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <FormControl>
                      <select 
                        {...field} 
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                        data-testid="select-org-status"
                      >
                        <option value="Active">Active</option>
                        <option value="Suspended">Suspended</option>
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseModal}
                  data-testid="button-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  data-testid="button-save"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? "Saving..."
                    : editingOrg 
                    ? "Update Organization"
                    : "Create Organization"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deletingOrg !== null}
        onOpenChange={(open) => !open && setDeletingOrg(null)}
        title="Delete Organization"
        subtitle={`Are you sure you want to delete "${deletingOrg?.name}"? This action cannot be undone.`}
        confirmText="Delete Organization"
        onConfirm={handleDelete}
        requireInput={deletingOrg?.name}
        variant="destructive"
        isPending={deleteMutation.isPending}
      />

      {/* Import Cards Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        {/* Import Homes Card */}
        <Card data-testid="import-homes-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="w-5 h-5" />
              Import Homes
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload a CSV file to import multiple care homes for an organization
            </p>
            
            <div className="space-y-2">
              <Label className="text-xs font-medium">CSV Template Headers:</Label>
              <div className="bg-muted p-3 rounded-md">
                <code className="text-xs">
                  home_name, home_address, home_email, home_contact_number, home_bed_count
                </code>
              </div>
              <a
                href="data:text/csv;charset=utf-8,home_name,home_address,home_email,home_contact_number,home_bed_count%0A"
                download="homes_import_template.csv"
                className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                data-testid="link-download-homes-template"
              >
                <FileUp className="w-3 h-3" />
                Download CSV Template
              </a>
            </div>

            {homesImportPreview.length > 0 && (
              <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  {homesImportPreview.length} home{homesImportPreview.length !== 1 ? 's' : ''} ready to import
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                  {homesImportPreview.filter((r: any) => r._valid).length} valid, {homesImportPreview.filter((r: any) => !r._valid).length} with errors
                </p>
              </div>
            )}

            <Button
              onClick={() => !canWrite ? toast(showWriteBlockedMessage()) : setIsHomesImportOpen(true)}
              disabled={!canWrite}
              className="w-full"
              data-testid="button-open-homes-import"
              title={!canWrite ? "Import disabled during impersonation" : "Import homes from CSV"}
            >
              <Upload className="w-4 h-4 mr-2" />
              Import Homes
            </Button>
          </CardContent>
        </Card>

        {/* Import Users Card */}
        <Card data-testid="import-users-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Import Users
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload a CSV file to import multiple users for existing homes
            </p>
            
            <div className="space-y-2">
              <Label className="text-xs font-medium">CSV Template Headers:</Label>
              <div className="bg-muted p-3 rounded-md">
                <code className="text-xs break-all">
                  user_first_name, user_last_name, user_email, user_job_title, user_home_name, user_access_level
                </code>
              </div>
              <a
                href="data:text/csv;charset=utf-8,user_first_name,user_last_name,user_email,user_job_title,user_home_name,user_access_level%0A"
                download="users_import_template.csv"
                className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                data-testid="link-download-users-template"
              >
                <FileUp className="w-3 h-3" />
                Download CSV Template
              </a>
            </div>

            {usersImportPreview.length > 0 && (
              <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  {usersImportPreview.length} user{usersImportPreview.length !== 1 ? 's' : ''} ready to import
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                  {usersImportPreview.filter((r: any) => r._valid).length} valid, {usersImportPreview.filter((r: any) => !r._valid).length} with errors
                </p>
              </div>
            )}

            <Button
              onClick={() => !canWrite ? toast(showWriteBlockedMessage()) : setIsUsersImportOpen(true)}
              disabled={!canWrite || homes.length === 0}
              className="w-full"
              data-testid="button-open-users-import"
              title={!canWrite ? "Import disabled during impersonation" : homes.length === 0 ? "Create homes before importing users" : "Import users from CSV"}
            >
              <Users className="w-4 h-4 mr-2" />
              {homes.length === 0 ? "No Homes Available" : "Import Users"}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Import Homes Modal */}
      <Dialog open={isHomesImportOpen} onOpenChange={setIsHomesImportOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="homes-import-modal">
          <DialogHeader>
            <DialogTitle>Import Homes</DialogTitle>
            <DialogDescription>
              Upload a CSV file to import multiple care homes for an organization
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Organization Selection */}
            <div className="space-y-2">
              <Label>Select Organization *</Label>
              <select
                value={homesSelectedOrg?.toString() || ""}
                onChange={(e) => setHomesSelectedOrg(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full p-2 border border-input rounded-md bg-background text-foreground"
                data-testid="select-homes-import-organization"
              >
                <option value="">Select an organization</option>
                {organizations.map((org: Organization) => (
                  <option key={org.id} value={org.id.toString()}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>

            {/* File Upload */}
            {homesSelectedOrg && (
              <div className="space-y-2">
                <Label>Upload CSV File *</Label>
                <div className="border-2 border-dashed rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setHomesImportFile(file);
                        parseHomesCSV(file);
                      }
                    }}
                    className="hidden"
                    id="homes-import-file"
                    data-testid="input-homes-import-file"
                  />
                  <label htmlFor="homes-import-file" className="cursor-pointer">
                    <FileUp className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">
                      {homesImportFile ? homesImportFile.name : "Click to upload or drag and drop"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      CSV file with homes data
                    </p>
                  </label>
                </div>
              </div>
            )}

            {/* Preview Table */}
            {homesImportPreview.length > 0 && (
              <div className="space-y-2">
                <Label>Import Preview ({homesImportPreview.length} rows)</Label>
                <div className="border rounded-md overflow-auto max-h-60">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Row</TableHead>
                        <TableHead>Home Name</TableHead>
                        <TableHead>Address</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Beds</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {homesImportPreview.map((row: any, idx: number) => (
                        <TableRow key={idx} className={!row._valid ? 'bg-destructive/10' : ''}>
                          <TableCell className="text-xs">{row._rowNumber}</TableCell>
                          <TableCell>{row.home_name}</TableCell>
                          <TableCell className="text-xs">{row.home_address || '-'}</TableCell>
                          <TableCell className="text-xs">{row.home_email || '-'}</TableCell>
                          <TableCell className="text-xs">{row.home_contact_number || '-'}</TableCell>
                          <TableCell className="text-xs">{row.home_bed_count || '-'}</TableCell>
                          <TableCell>
                            {row._valid ? (
                              <Badge variant="outline" className="text-xs">Valid</Badge>
                            ) : (
                              <Badge variant="destructive" className="text-xs" title={row._errors}>Error</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {homesImportPreview.some((r: any) => !r._valid) && (
                  <p className="text-xs text-destructive">
                    Some rows have validation errors. Hover over the error badge to see details.
                  </p>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsHomesImportOpen(false);
                setHomesSelectedOrg(null);
                setHomesImportFile(null);
                setHomesImportPreview([]);
              }}
              data-testid="button-cancel-homes-import"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (homesSelectedOrg && homesImportPreview.length > 0) {
                  const validHomes = homesImportPreview.filter((r: any) => r._valid);
                  importHomesMutation.mutate({
                    organization_id: homesSelectedOrg,
                    homes: validHomes
                  });
                }
              }}
              disabled={!homesSelectedOrg || !homesImportFile || homesImportPreview.length === 0 || homesImportPreview.some((r: any) => !r._valid) || importHomesMutation.isPending}
              data-testid="button-confirm-homes-import"
            >
              <Upload className="w-4 h-4 mr-2" />
              {importHomesMutation.isPending ? 'Importing...' : `Import ${homesImportPreview.filter((r: any) => r._valid).length} Homes`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Users Modal */}
      <Dialog open={isUsersImportOpen} onOpenChange={setIsUsersImportOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="users-import-modal">
          <DialogHeader>
            <DialogTitle>Import Users</DialogTitle>
            <DialogDescription>
              Upload a CSV file to import users. Users will be assigned to homes by matching the home_name column.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Organization Selection */}
            <div className="space-y-2">
              <Label>Select Organization *</Label>
              <select
                value={usersSelectedOrg?.toString() || ""}
                onChange={(e) => setUsersSelectedOrg(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full p-2 border border-input rounded-md bg-background text-foreground"
                data-testid="select-users-import-organization"
              >
                <option value="">Select an organization</option>
                {organizations.map((org: Organization) => (
                  <option key={org.id} value={org.id.toString()}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>

            {/* File Upload */}
            {usersSelectedOrg && (
              <div className="space-y-2">
                <Label>Upload CSV File *</Label>
                <div className="border-2 border-dashed rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setUsersImportFile(file);
                      parseUsersCSV(file);
                    }
                  }}
                  className="hidden"
                  id="users-import-file"
                  data-testid="input-users-import-file"
                />
                <label htmlFor="users-import-file" className="cursor-pointer">
                  <FileUp className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {usersImportFile ? usersImportFile.name : "Click to upload or drag and drop"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    CSV file with users data
                  </p>
                </label>
              </div>
              </div>
            )}

            {/* Preview Table */}
            {usersImportPreview.length > 0 && (
              <div className="space-y-2">
                <Label>Import Preview ({usersImportPreview.length} rows)</Label>
                <div className="border rounded-md overflow-auto max-h-60">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Row</TableHead>
                        <TableHead>First Name</TableHead>
                        <TableHead>Last Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Job Title</TableHead>
                        <TableHead>Home Name</TableHead>
                        <TableHead>Access Level</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {usersImportPreview.map((row: any, idx: number) => (
                        <TableRow key={idx} className={!row._valid ? 'bg-destructive/10' : ''}>
                          <TableCell className="text-xs">{row._rowNumber}</TableCell>
                          <TableCell>{row.user_first_name}</TableCell>
                          <TableCell>{row.user_last_name}</TableCell>
                          <TableCell className="text-xs">{row.user_email}</TableCell>
                          <TableCell className="text-xs">{row.user_job_title || '-'}</TableCell>
                          <TableCell className="text-xs">{row.user_home_name || '-'}</TableCell>
                          <TableCell className="text-xs">{row.user_access_level}</TableCell>
                          <TableCell>
                            {row._valid ? (
                              <Badge variant="outline" className="text-xs">Valid</Badge>
                            ) : (
                              <Badge variant="destructive" className="text-xs" title={row._errors}>Error</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {usersImportPreview.some((r: any) => !r._valid) && (
                  <p className="text-xs text-destructive">
                    Some rows have validation errors. Hover over the error badge to see details.
                  </p>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsUsersImportOpen(false);
                setUsersSelectedOrg(null);
                setUsersImportFile(null);
                setUsersImportPreview([]);
              }}
              data-testid="button-cancel-users-import"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (usersSelectedOrg && usersImportPreview.length > 0) {
                  const validUsers = usersImportPreview.filter((r: any) => r._valid);
                  importUsersMutation.mutate({
                    organization_id: usersSelectedOrg,
                    users: validUsers
                  });
                }
              }}
              disabled={!usersSelectedOrg || !usersImportFile || usersImportPreview.length === 0 || usersImportPreview.some((r: any) => !r._valid) || importUsersMutation.isPending}
              data-testid="button-confirm-users-import"
            >
              <Users className="w-4 h-4 mr-2" />
              {importUsersMutation.isPending ? 'Importing...' : `Import ${usersImportPreview.filter((r: any) => r._valid).length} Users`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// Legacy AuthProvider removed - SessionProvider is now the single source of truth

// Route Components to replace hooks in Route children
function CreateBuilderRoute() {
  return <BuilderPage mode="create" />;
}

function EditBuilderRoute(props: { templateId: string }) {
  return <BuilderPage mode="edit" templateId={props.templateId} />;
}

function CloneBuilderRoute(props: { templateId: string }) {
  return <BuilderPage mode="clone" templateId={props.templateId} />;
}

function AuditReviewRoute(props: { id: string }) {
  return <AuditReviewPage params={{ id: props.id }} />;
}

// Guarded route for Good Practices - only OrgManager/Admin and HomeManager/Admin may view
function GoodPracticesRoute() {
  const { session } = useSession();
  const user = session?.user;
  const allowed = user?.role && ['OrgManager', 'OrgAdmin', 'HomeManager', 'HomeAdmin'].includes(user.role);
  if (!allowed) return <NotFoundPage />;
  return <GoodPracticesPage />;
}

// Not Found Page Component
function NotFoundPage() {
  const [, setLocation] = useLocation();
  
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-center space-y-4">
        <div className="text-6xl font-bold text-muted-foreground">404</div>
  <h1 className="page-title text-2xl font-bold text-foreground">Page Not Found</h1>
        <p className="text-muted-foreground max-w-md">
          The page you're looking for doesn't exist or you don't have permission to access it.
        </p>
        <div className="space-x-4">
          <Button onClick={() => setLocation('/')} variant="default" data-testid="home-button">
            Go Home
          </Button>
          <Button onClick={() => window.history.back()} variant="outline" data-testid="back-button">
            Go Back
          </Button>
        </div>
      </div>
    </div>
  );
}

// Subscription Guard - blocks access when subscription is inactive/suspended
function SubscriptionGuard({ children }: { children: ReactNode }) {
  const { session, logout } = useSession();
  
  // Check if user is authenticated and has inactive/suspended subscription
  const hasInactiveSubscription = session?.user && 
    session.subscription && 
    (session.subscription.status === 'suspended' || session.subscription.status === 'inactive');
  
  // Show blocking modal if subscription is inactive
  if (hasInactiveSubscription) {
    return (
      <SubscriptionBlockedModal
        subscriptionStatus={session.subscription!.status as 'suspended' | 'inactive'}
        entity={session.subscription!.entity}
        onLogout={logout}
      />
    );
  }
  
  // Normal access - subscription is active or user not authenticated
  return <>{children}</>;
}

// ErrorBoundary to catch runtime errors and prevent white screens
interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-screen items-center justify-center bg-slate-50 dark:bg-slate-900">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-6 w-6 text-destructive" />
                <CardTitle>Something went wrong</CardTitle>
              </div>
              <CardDescription>
                An error occurred while loading the page. Please try refreshing or logging out.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {this.state.error && (
                <div className="bg-muted p-3 rounded-md">
                  <p className="text-sm font-mono text-muted-foreground">
                    {this.state.error.message}
                  </p>
                </div>
              )}
              <div className="flex gap-2">
                <Button 
                  onClick={() => window.location.reload()} 
                  className="flex-1"
                  data-testid="button-reload-app"
                >
                  Reload App
                </Button>
                <Button 
                  onClick={() => window.location.href = '/login'} 
                  variant="outline"
                  className="flex-1"
                  data-testid="button-logout"
                >
                  Log Out
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

// Main App - FIXED: Layout now wraps Router to prevent blank pages
function App() {
  return (
    <ThemeProvider defaultTheme="light" storageKey="smartcare-theme">
      <QueryClientProvider client={queryClient}>
        <SessionProvider>
          <RuntimeBanner />
          <SubscriptionGuard>
            <ErrorBoundary>
              <Layout>
                  <Router>
                    <Switch>
                    {/* Root route - handles authentication and role-based redirects */}
                    <Route path="/" component={RootRoute} />
                
                {/* Authentication */}
                <Route path="/login" component={LoginPage} />
                <Route path="/forgot-password" component={ForgotPasswordPage} />
                <Route path="/reset-password" component={ResetPasswordPage} />
                
                {/* Public Routes */}
                <Route path="/demo" component={DemoPage} />
                <Route path="/privacy" component={PrivacyPage} />
                <Route path="/terms" component={TermsPage} />
                
                {/* Admin/Management Routes */}
                <Route path="/enquiries" component={EnquiriesPage} />
                
                {/* Dashboard Routes - All explicit routes registered */}
                <Route path="/dashboard" component={DashboardPage} />
                <Route path="/dashboard/developer" component={DeveloperDashboard} />
                <Route path="/dashboard/platform-manager" component={PlatformManagerDashboard} />
                <Route path="/dashboard/manager" component={DashboardPage} />
                <Route path="/dashboard/staff" component={StaffDashboard} />
                
                {/* Feature Routes */}
                <Route path="/manager/staff" component={ManagerStaffPage} />
                <Route path="/homes" component={HomesPage} />
                <Route path="/templates" component={TemplatesPage} />
                <Route path="/good-practices" component={GoodPracticesRoute} />
                <Route path="/templates/library" component={TemplatesPage} />
                <Route path="/templates/builder" component={CreateBuilderRoute} />
                <Route path="/templates/builder/edit/:templateId">
                  {(params) => <EditBuilderRoute templateId={params.templateId} />}
                </Route>
                <Route path="/templates/builder/clone/:templateId">
                  {(params) => <CloneBuilderRoute templateId={params.templateId} />}
                </Route>
                <Route path="/actions" component={ActionsPage} />
                <Route path="/audits" component={AuditsPage} />
                <Route path="/audits/take" component={TakeAuditPage} />
                <Route path="/audits/new" component={NewAuditPage} />
                <Route path="/audits/view" component={ViewCompletedAuditPage} />
                <Route path="/audits/:id/review">
                  {(params) => <AuditReviewRoute id={params.id} />}
                </Route>
                <Route path="/reports" component={ReportsPage} />
                <Route path="/regions" component={RegionsPage} />
                <Route path="/settings" component={SettingsPage} />
                
                {/* Admin Routes */}
                <Route path="/admin/my-home" component={MyHomeInfoPage} />
                <Route path="/admin/my-organization" component={MyOrganizationPage} />
                <Route path="/admin/organizations" component={OrganizationsPage} />
                <Route path="/admin/users" component={AdminUsersPage} />
                <Route path="/admin/template-permissions" component={TemplatePermissionManager} />
                
                {/* Catch-all route for unmatched paths - prevents blank pages */}
                <Route component={NotFoundPage} />
                </Switch>
              </Router>
            </Layout>
            <Toaster />
            </ErrorBoundary>
          </SubscriptionGuard>
        </SessionProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}


export default App;
